/* eslint-disable react/no-unescaped-entities, no-empty, react/no-unstable-nested-components */
/* NOTE: App.js is a 11.9k-line legacy file. The rules above are pre-existing style findings
   in working production code. They are scoped-disabled here to avoid a high-risk wholesale
   rewrite; addressing them belongs to the planned App.js refactor (see ROADMAP). */
import { useState, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import ReactMarkdown from 'react-markdown';
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import { Activity, Users, LogOut, Plus, BarChart3, FileText, Calendar, Clock, DollarSign, Settings as SettingsIcon, X, Edit, Trash2, CheckCircle, Search, Mail, Network, FileSpreadsheet } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import AssenzeManager from "@/components/AssenzeManager";
import PublicRegister from "@/components/PublicRegister";
import GestioneTurni from "@/components/GestioneTurni";
import PlanningOrario from "@/components/PlanningOrario";
import Preventivi from "@/components/Preventivi";
import Informative from "@/components/Informative";
import PrivacyPolicyView from "@/components/PrivacyPolicyView";
import ComunicazioniOperatore from "@/components/ComunicazioniOperatore";
import AdminHome from "@/components/AdminHome";
import AdminSidebar from "@/components/AdminSidebar";
import OperatorSidebar from "@/components/OperatorSidebar";
import OperatorHome from "@/components/OperatorHome";
import Procedure from "@/components/Procedure";
import Audit from "@/components/Audit";
import Accreditamenti from "@/components/Accreditamenti";
import OrganigrammaPanel from "@/components/OrganigrammaPanel";
import HRImportPanel from "@/components/HRImportPanel";
import { getTotalAccessiFromData, months as monthsList } from "@/utils/reportUtils";
import { getAuthenticatedFileUrl } from "@/utils/fileUtils";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Constants
// These will be loaded dynamically from settings
let FIGURE_PROFESSIONALI = [];
let ASL_RIFERIMENTO = [];

const getAccessTypes = (figuraProfessionale) => {
  if (["Infermiere", "Fisioterapista", "OSS"].includes(figuraProfessionale)) {
    return ["bassa", "media", "alta"];
  }
  return ["standard", "plus"];
};

const getMinutesForAccess = (figuraProfessionale, tipoAccesso, count) => {
  const minutesMap = {
    "Infermiere": { bassa: 30, media: 45, alta: 60 },
    "Fisioterapista": { bassa: 30, media: 45, alta: 60 },
    "OSS": { bassa: 60, media: 90, alta: 480 },
    "Logopedista": { standard: 30, plus: 60 },
    "Psicologo": { standard: 30, plus: 60 },
    "Medico": { standard: 30, plus: 60 },
    "Altro": { standard: 30, plus: 60 }
  };
  const minutes = minutesMap[figuraProfessionale]?.[tipoAccesso] || 0;
  return minutes * count;
};

// Auth Context
const AuthContext = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchCurrentUser();
    }
  }, [token]);

  const fetchCurrentUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
    } catch (error) {
      logout();
    }
  };

  const login = (newToken, userData) => {
    localStorage.setItem('token', newToken);
    setToken(newToken);
    setUser(userData);
    axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
  };

  const logout = () => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
    delete axios.defaults.headers.common['Authorization'];
  };

  return children({ user, login, logout });
};


// Home Page - Landing Page
const HomePage = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 sm:py-6">
          <div className="flex items-center justify-center">
            <img src="/logo.png" alt="Croce Gialla Home" className="h-16 sm:h-20 md:h-24" />
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8 sm:py-12 md:py-16">
        {/* Hero Section */}
        <div className="text-center mb-8 sm:mb-12 md:mb-16">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-3 sm:mb-4 leading-tight px-2">
            Benvenuto nel Portale Croce Gialla Home
          </h2>
          <p className="text-base sm:text-lg md:text-xl text-gray-600 max-w-2xl mx-auto px-4">
            Scegli il servizio a cui vuoi accedere
          </p>
        </div>

        {/* Service Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8 mb-8 sm:mb-12 md:mb-16 items-stretch">
          {/* Portale Operatore */}
          <Card className="shadow-xl border-0 hover:shadow-2xl transition-shadow cursor-pointer group flex flex-col">
            <CardContent className="p-6 sm:p-8 text-center flex flex-col h-full">
              <div className="w-16 h-16 sm:w-20 sm:h-20 mx-auto mb-4 sm:mb-6 bg-cyan-600 rounded-full flex items-center justify-center">
                <FileText className="w-8 h-8 sm:w-10 sm:h-10 text-white" />
              </div>
              <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2 sm:mb-3">Portale Operatore</h3>
              <p className="text-sm sm:text-base text-gray-600 mb-4 sm:mb-6 flex-grow">
                Accedi al tuo portale aziendale
              </p>
              <a href="/login">
                <Button className="w-full bg-cyan-600 hover:bg-cyan-700 text-white font-semibold h-11 sm:h-12 text-base sm:text-lg group-hover:scale-105 transition-transform">
                  Accedi al Portale
                </Button>
              </a>
            </CardContent>
          </Card>

          {/* Rete della Sanità */}
          <Card className="shadow-xl border-0 hover:shadow-2xl transition-shadow cursor-pointer group flex flex-col">
            <CardContent className="p-6 sm:p-8 text-center flex flex-col h-full">
              <div className="w-16 h-16 sm:w-20 sm:h-20 mx-auto mb-4 sm:mb-6 bg-gradient-to-br from-blue-400 to-indigo-500 rounded-full flex items-center justify-center">
                <Activity className="w-8 h-8 sm:w-10 sm:h-10 text-white" />
              </div>
              <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2 sm:mb-3">Rete della Sanità</h3>
              <p className="text-sm sm:text-base text-gray-600 mb-4 sm:mb-6 flex-grow">
                Cartella clinica digitale e gestione pazienti integrata
              </p>
              <a href="https://crocegiallahome.retedellasanita.it" target="_blank" rel="noopener noreferrer">
                <Button className="w-full bg-gradient-to-r from-blue-400 to-indigo-500 hover:from-blue-500 hover:to-indigo-600 text-white font-semibold h-11 sm:h-12 text-base sm:text-lg group-hover:scale-105 transition-transform">
                  Vai alla Cartella Clinica
                </Button>
              </a>
            </CardContent>
          </Card>

          {/* Magazzino e Documenti */}
          <Card className="shadow-xl border-0 hover:shadow-2xl transition-shadow cursor-pointer group flex flex-col">
            <CardContent className="p-6 sm:p-8 text-center flex flex-col h-full">
              <div className="w-16 h-16 sm:w-20 sm:h-20 mx-auto mb-4 sm:mb-6 bg-gradient-to-br from-green-400 to-emerald-500 rounded-full flex items-center justify-center">
                <SettingsIcon className="w-8 h-8 sm:w-10 sm:h-10 text-white" />
              </div>
              <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2 sm:mb-3">Magazzino e Gestione</h3>
              <p className="text-sm sm:text-base text-gray-600 mb-4 sm:mb-6 flex-grow">
                Gestione magazzino, documenti e automezzi aziendali
              </p>
              <a href="https://adicure.it" target="_blank" rel="noopener noreferrer">
                <Button className="w-full bg-gradient-to-r from-green-400 to-emerald-500 hover:from-green-500 hover:to-emerald-600 text-white font-semibold h-11 sm:h-12 text-base sm:text-lg group-hover:scale-105 transition-transform">
                  Accedi al Gestionale
                </Button>
              </a>
            </CardContent>
          </Card>
        </div>

        {/* Contact Section - 3 Sedi */}
        <Card className="shadow-xl border-0 bg-white">
          <CardHeader>
            <CardTitle className="text-xl sm:text-2xl text-center">Contatti</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8">
              {/* Sede ASL 03 - PESCARA */}
              <div className="space-y-3">
                <h3 className="text-base font-bold text-yellow-600 mb-3">ASL 03 - PESCARA</h3>
                <div className="flex items-start gap-2">
                  <svg className="w-5 h-5 text-yellow-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  <div>
                    <p className="text-gray-700 text-sm">Via B. Nunzio Sulprizio 16/2</p>
                    <p className="text-gray-700 text-sm">Pescara</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <svg className="w-5 h-5 text-yellow-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                  </svg>
                  <p className="text-gray-700 text-sm">085 2080797</p>
                </div>
                <div className="flex items-center gap-2">
                  <svg className="w-5 h-5 text-yellow-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  <a href="mailto:info@crocegiallahome.com" className="text-cyan-600 hover:text-cyan-700 text-sm break-all">
                    info@crocegiallahome.com
                  </a>
                </div>
                <div className="flex items-center gap-2">
                  <svg className="w-5 h-5 text-yellow-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
                  </svg>
                  <a href="http://www.crocegiallahome.com" target="_blank" rel="noopener noreferrer" className="text-cyan-600 hover:text-cyan-700 text-sm">
                    www.crocegiallahome.com
                  </a>
                </div>
              </div>

              {/* Sede ASL 02 - CHIETI */}
              <div className="space-y-3">
                <h3 className="text-base font-bold text-cyan-600 mb-3">ASL 02 - CHIETI</h3>
                <div className="flex items-start gap-2">
                  <svg className="w-5 h-5 text-cyan-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  <div>
                    <p className="text-gray-700 text-sm">Contrada Mozzoni 13</p>
                    <p className="text-gray-700 text-sm">Treglio (CH)</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <svg className="w-5 h-5 text-cyan-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                  </svg>
                  <p className="text-gray-700 text-sm">0872 633331</p>
                </div>
                <div className="flex items-center gap-2">
                  <svg className="w-5 h-5 text-cyan-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  <a href="mailto:adi.chieti@crocegiallahome.com" className="text-cyan-600 hover:text-cyan-700 text-sm break-all">
                    adi.chieti@crocegiallahome.com
                  </a>
                </div>
                <div className="flex items-center gap-2">
                  <svg className="w-5 h-5 text-cyan-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
                  </svg>
                  <a href="http://www.crocegiallahome.com" target="_blank" rel="noopener noreferrer" className="text-cyan-600 hover:text-cyan-700 text-sm">
                    www.crocegiallahome.com
                  </a>
                </div>
              </div>

              {/* Sede ASL 04 - TERAMO */}
              <div className="space-y-3">
                <h3 className="text-base font-bold text-green-600 mb-3">ASL 04 - TERAMO</h3>
                <div className="flex items-start gap-2">
                  <svg className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  <div>
                    <p className="text-gray-700 text-sm">S.S 16</p>
                    <p className="text-gray-700 text-sm">Silvi (Te)</p>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <svg className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                  </svg>
                  <div>
                    <p className="text-gray-700 text-sm">0861 030035</p>
                    <p className="text-gray-700 text-sm">Cell: 340 580 7589</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <svg className="w-5 h-5 text-green-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  <a href="mailto:adi.teramo@crocegiallahome.com" className="text-cyan-600 hover:text-cyan-700 text-sm break-all">
                    adi.teramo@crocegiallahome.com
                  </a>
                </div>
                <div className="flex items-center gap-2">
                  <svg className="w-5 h-5 text-green-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
                  </svg>
                  <a href="http://www.crocegiallahome.com" target="_blank" rel="noopener noreferrer" className="text-cyan-600 hover:text-cyan-700 text-sm">
                    www.crocegiallahome.com
                  </a>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Footer */}
      <div className="bg-white border-t border-gray-200 mt-8 sm:mt-12 md:mt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 sm:py-6">
          <p className="text-center text-gray-600 text-xs sm:text-sm">
            © 2025 Croce Gialla Home - Cure Domiciliari d&apos;Abruzzo. Tutti i diritti riservati.
          </p>
        </div>
      </div>
    </div>
  );
};

// Login Page
const LoginPage = ({ onLogin }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState('');
  const [forgotPasswordLoading, setForgotPasswordLoading] = useState(false);

  useEffect(() => {
    // Check if user was redirected after password reset
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('reset') === 'success') {
      toast.success('Password reimpostata con successo! Ora puoi accedere con la nuova password.');
      // Clean URL
      window.history.replaceState({}, document.title, '/login');
    }
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/login`, { username, password });
      onLogin(response.data.token, response.data.user);
      toast.success('Login effettuato con successo!');
    } catch (error) {
      toast.error('Credenziali non valide');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setForgotPasswordLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email: forgotPasswordEmail });
      toast.success('Se l\'email esiste, riceverai un link per reimpostare la password');
      setShowForgotPassword(false);
      setForgotPasswordEmail('');
    } catch (error) {
      toast.error('Errore durante l\'invio della richiesta');
    } finally {
      setForgotPasswordLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-white" data-testid="login-page">
      {/* Brand Panel - Left */}
      <div className="hidden lg:flex lg:w-[55%] login-brand-panel relative overflow-hidden bg-gradient-to-br from-cyan-700 via-cyan-600 to-teal-600">
        {/* Dot pattern overlay */}
        <div className="absolute inset-0 dot-pattern" />
        
        {/* Content */}
        <div className="relative z-10 flex flex-col justify-between p-12 xl:p-16 w-full">
          <div>
            <div className="login-logo-float inline-block">
              <img src="/logo.png" alt="Croce Gialla Home" className="h-20 drop-shadow-lg" />
            </div>
          </div>
          
          <div className="space-y-6">
            <h1 className="text-4xl xl:text-5xl font-bold text-white leading-tight">
              Croce Gialla<br />
              <span className="text-cyan-200">Home</span>
            </h1>
            <p className="text-lg text-cyan-100 max-w-md leading-relaxed">
              Piattaforma di gestione accessi domiciliari, turni e report per operatori sanitari.
            </p>
            <div className="flex gap-6 pt-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-white">24/7</div>
                <div className="text-xs text-cyan-200 uppercase tracking-wider mt-1">Accesso</div>
              </div>
              <div className="w-px bg-cyan-400/30" />
              <div className="text-center">
                <div className="text-2xl font-bold text-white">Report</div>
                <div className="text-xs text-cyan-200 uppercase tracking-wider mt-1">Tempo reale</div>
              </div>
              <div className="w-px bg-cyan-400/30" />
              <div className="text-center">
                <div className="text-2xl font-bold text-white">Sicuro</div>
                <div className="text-xs text-cyan-200 uppercase tracking-wider mt-1">GDPR</div>
              </div>
            </div>
          </div>

          <p className="text-xs text-cyan-300/60">Croce Gialla Home &copy; {new Date().getFullYear()}</p>
        </div>

        {/* Decorative shapes */}
        <div className="absolute -bottom-20 -right-20 w-80 h-80 rounded-full bg-cyan-500/20 blur-3xl" />
        <div className="absolute -top-20 -right-10 w-60 h-60 rounded-full bg-teal-400/10 blur-2xl" />
      </div>

      {/* Form Panel - Right */}
      <div className="flex-1 flex items-center justify-center p-6 sm:p-10 login-form-panel">
        <div className="w-full max-w-sm">
          {/* Mobile logo */}
          <div className="lg:hidden text-center mb-8 form-element">
            <img src="/logo.png" alt="Croce Gialla Home" className="h-20 mx-auto mb-3" />
            <h2 className="text-xl font-bold text-gray-900">Croce Gialla Home</h2>
          </div>

          <div className="form-element">
            <h2 className="text-2xl font-bold text-gray-900 mb-1">Bentornato</h2>
            <p className="text-sm text-gray-500 mb-8">Accedi alla piattaforma</p>
          </div>

          <form onSubmit={handleLogin} className="space-y-5">
            <div className="space-y-2 form-element">
              <Label htmlFor="username" className="text-sm font-medium text-gray-700">Username</Label>
              <Input
                id="username"
                data-testid="login-username-input"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="Inserisci username"
                required
                className="h-11 bg-gray-50 border-gray-200 focus:bg-white focus:border-cyan-500 focus:ring-cyan-500/20 transition-colors"
              />
            </div>
            <div className="space-y-2 form-element">
              <Label htmlFor="password" className="text-sm font-medium text-gray-700">Password</Label>
              <Input
                id="password"
                data-testid="login-password-input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Inserisci password"
                required
                className="h-11 bg-gray-50 border-gray-200 focus:bg-white focus:border-cyan-500 focus:ring-cyan-500/20 transition-colors"
              />
            </div>
            <div className="form-element">
              <Button
                data-testid="login-submit-button"
                type="submit"
                className="w-full h-11 bg-cyan-600 hover:bg-cyan-700 text-white font-semibold shadow-sm hover:shadow-md transition-all rounded-lg"
                disabled={loading}
              >
                {loading ? 'Accesso in corso...' : 'Accedi'}
              </Button>
            </div>
            
            <div className="text-center form-element">
              <button
                type="button"
                onClick={() => setShowForgotPassword(true)}
                className="text-sm text-cyan-600 hover:text-cyan-700 hover:underline transition-colors"
              >
                Password dimenticata?
              </button>
            </div>
            
            <div className="text-center pt-2 form-element">
              <a
                href="/register"
                className="text-gray-500 text-sm"
              >
                Non hai un account? <span className="text-cyan-600 hover:text-cyan-700 font-medium">Registrati</span>
              </a>
            </div>
          </form>
        </div>
      </div>

      {/* Forgot Password Dialog */}
      <Dialog open={showForgotPassword} onOpenChange={setShowForgotPassword}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Recupera Password</DialogTitle>
            <DialogDescription>
              Inserisci l&apos;email associata al tuo account. Riceverai un link per reimpostare la password.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleForgotPassword} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="forgot-email">Email</Label>
              <Input
                id="forgot-email"
                type="email"
                value={forgotPasswordEmail}
                onChange={(e) => setForgotPasswordEmail(e.target.value)}
                placeholder="tua.email@esempio.com"
                required
                className="h-11"
              />
            </div>
            <div className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setShowForgotPassword(false);
                  setForgotPasswordEmail('');
                }}
                className="flex-1"
              >
                Annulla
              </Button>
              <Button
                type="submit"
                disabled={forgotPasswordLoading}
                className="flex-1 bg-cyan-600 hover:bg-cyan-700 text-white font-semibold"
              >
                {forgotPasswordLoading ? 'Invio...' : 'Invia Link'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Reset Password Page
const ResetPasswordPage = () => {
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [tokenValid, setTokenValid] = useState(null);
  const [email, setEmail] = useState('');
  const [token, setToken] = useState('');

  useEffect(() => {
    // Get token from URL
    const urlParams = new URLSearchParams(window.location.search);
    const tokenParam = urlParams.get('token');
    
    if (!tokenParam) {
      setTokenValid(false);
      return;
    }

    setToken(tokenParam);

    // Verify token
    const verifyToken = async () => {
      try {
        const response = await axios.post(`${API}/auth/verify-reset-token?token=${tokenParam}`);
        setTokenValid(true);
        setEmail(response.data.email);
      } catch (error) {
        setTokenValid(false);
        toast.error(error.response?.data?.detail || 'Token non valido o scaduto');
      }
    };

    verifyToken();
  }, []);

  const handleResetPassword = async (e) => {
    e.preventDefault();

    if (newPassword !== confirmPassword) {
      toast.error('Le password non coincidono');
      return;
    }

    if (newPassword.length < 6) {
      toast.error('La password deve essere di almeno 6 caratteri');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, {
        token,
        new_password: newPassword
      });
      toast.success('Password reimpostata con successo! Reindirizzamento al login...');
      
      setTimeout(() => {
        window.location.href = '/login?reset=success';
      }, 2000);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore durante il reset della password');
    } finally {
      setLoading(false);
    }
  };

  if (tokenValid === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Card className="w-full max-w-md shadow-2xl border-0">
          <CardContent className="p-8 text-center">
            <p className="text-gray-600">Verifica del token in corso...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (tokenValid === false) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Card className="w-full max-w-md shadow-2xl border-0">
          <CardHeader className="space-y-4 text-center">
            <div className="mx-auto">
              <img src="/logo.png" alt="Croce Gialla Home" className="h-32 mx-auto" />
            </div>
            <CardDescription className="text-base">Reset Password</CardDescription>
          </CardHeader>
          <CardContent className="text-center space-y-4">
            <p className="text-red-600 font-semibold">Token non valido o scaduto</p>
            <p className="text-gray-600 text-sm">Il link per il reset della password non è valido o è scaduto. Richiedi un nuovo link.</p>
            <Button
              onClick={() => window.location.href = '/login'}
              className="w-full bg-cyan-600 hover:bg-cyan-700 text-white font-semibold"
            >
              Torna al Login
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <Card className="w-full max-w-md shadow-2xl border-0">
        <CardHeader className="space-y-4 text-center">
          <div className="mx-auto">
            <img src="/logo.png" alt="Croce Gialla Home" className="h-32 mx-auto" />
          </div>
          <CardDescription className="text-base">Reimposta la tua password</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleResetPassword} className="space-y-5">
            <div className="space-y-2">
              <Label>Email</Label>
              <Input
                type="email"
                value={email}
                disabled
                className="h-11 bg-gray-100"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="new-password">Nuova Password</Label>
              <Input
                id="new-password"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Inserisci nuova password"
                required
                minLength={6}
                className="h-11"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirm-password">Conferma Password</Label>
              <Input
                id="confirm-password"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Conferma nuova password"
                required
                minLength={6}
                className="h-11"
              />
            </div>
            <Button
              type="submit"
              className="w-full h-11 bg-cyan-600 hover:bg-cyan-700 text-white font-semibold shadow-md transition-all"
              disabled={loading}
            >
              {loading ? 'Reimpostazione in corso...' : 'Reimposta Password'}
            </Button>
            <div className="text-center pt-2">
              <a
                href="/login"
                className="text-sm text-cyan-600 hover:text-cyan-700 hover:underline"
              >
                Torna al Login
              </a>
            </div>
          </form>
        </CardContent>
      </Card>
      
    </div>
  );
};

// Operator Dashboard
const OperatorDashboard = ({ user, onLogout, getAuthenticatedFileUrl }) => {
  const [activeTab, setActiveTab] = useState('home');
  const [reports, setReports] = useState([]);
  const [teamReports, setTeamReports] = useState([]); // Report del team per coordinatori
  const [settings, setSettings] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [formData, setFormData] = useState({
    accessi_data: {},
    assenze: {},
    note_turni: ''
  });
  const [editingReport, setEditingReport] = useState(null);
  const [showChangePasswordDialog, setShowChangePasswordDialog] = useState(false);
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  
  // Stati per fatture (liberi professionisti)
  const [invoices, setInvoices] = useState([]);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [showInvoiceDetailDialog, setShowInvoiceDetailDialog] = useState(false);
  const [showUploadInvoiceDialog, setShowUploadInvoiceDialog] = useState(false);
  const [showRequestVerificationDialog, setShowRequestVerificationDialog] = useState(false);
  const [verificationNote, setVerificationNote] = useState('');
  const [invoiceUploadData, setInvoiceUploadData] = useState({
    data_fattura: '',
    numero_fattura: ''
  });
  const [invoiceStats, setInvoiceStats] = useState(null);
  
  // Stati convenzioni
  const [convenzioni, setConvenzioni] = useState([]);
  const [showConvenzioneInfo, setShowConvenzioneInfo] = useState(false);
  const [selectedConvenzione, setSelectedConvenzione] = useState(null);

  // Stati formazione
  const [corsiAssegnati, setCorsiAssegnati] = useState([]);
  const [progressiUtente, setProgressiUtente] = useState([]);
  const [corsoCorrente, setCorsoCorrente] = useState(null);
  const [moduliCorso, setModuliCorso] = useState([]);
  const [moduloCorrente, setModuloCorrente] = useState(null);
  const [testModulo, setTestModulo] = useState(null);
  const [domandeTest, setDomandeTest] = useState([]);
  const [risposteUtente, setRisposteUtente] = useState({});
  const [showTestDialog, setShowTestDialog] = useState(false);
  const [testInCorso, setTestInCorso] = useState(false);
  const [moduliCountPerCorso, setModuliCountPerCorso] = useState({}); // {corso_id: count}
  const [showQuestionarioDialog, setShowQuestionarioDialog] = useState(false);
  const [showCompletamentoDialog, setShowCompletamentoDialog] = useState(false);
  const [certificatoCompletato, setCertificatoCompletato] = useState(null);
  const [questionario, setQuestionario] = useState(null);
  const [risposteQuestionario, setRisposteQuestionario] = useState({});
  const [commentiQuestionario, setCommentiQuestionario] = useState('');
  const [certificatiOperatore, setCertificatiOperatore] = useState([]);
  const [filtroStatoCorso, setFiltroStatoCorso] = useState('tutti');
  const [richiesteAssenze, setRichiesteAssenze] = useState([]);
  const [comunicazioniList, setComunicazioniList] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);

  // Variabili temporanee per compatibilità
  const accessTypes = getAccessTypes(user.figura_professionale);
  const usesStandardPlus = !["Infermiere", "Fisioterapista", "OSS"].includes(user.figura_professionale);

  useEffect(() => {
    if (user.tipo_contratto === 'libero_professionista') {
      fetchInvoices();
      fetchInvoiceStats();
    } else {
      fetchReports();
    }
    // Se coordinatore, carica anche i report del team
    if (user.is_coordinatore) {
      fetchTeamReports();
    }
    fetchSettings();
    fetchConvenzioni();
    fetchCorsiAssegnati();
    fetchCertificatiOperatore();
    fetchRichiesteAssenze();
    fetchComunicazioniList();
    if (user.is_coordinatore) {
      fetchTeamMembers();
    }
  }, []);
  
  const fetchRichiesteAssenze = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/richieste-assenze`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRichiesteAssenze(response.data || []);
    } catch (error) {
      console.error('Errore caricamento richieste assenze:', error);
    }
  };
  
  const fetchComunicazioniList = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/comunicazioni`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setComunicazioniList(response.data || []);
    } catch (error) {
      console.error('Errore caricamento comunicazioni:', error);
    }
  };
  
  const fetchTeamMembers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const myTeam = (response.data || []).filter(u => u.coordinatore_id === user.id);
      setTeamMembers(myTeam);
    } catch (error) {
      console.error('Errore caricamento team:', error);
    }
  };

  const fetchConvenzioni = async () => {
    try {
      const response = await axios.get(`${API}/convenzioni`);
      setConvenzioni(response.data);
    } catch (error) {
    }
  };

  const fetchCorsiAssegnati = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/formazione/corsi`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCorsiAssegnati(response.data);
      
      // Carica il conteggio moduli per ogni corso
      const counts = {};
      for (const corso of response.data) {
        try {
          const moduliResponse = await axios.get(`${API}/formazione/corsi/${corso.id}/moduli`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          counts[corso.id] = moduliResponse.data.length;
        } catch (err) {
          counts[corso.id] = 0;
        }
      }
      setModuliCountPerCorso(counts);
      
      fetchProgressiUtente();
    } catch (error) {
    }
  };

  const fetchProgressiUtente = async () => {
    try {
      const response = await axios.get(`${API}/formazione/progressi/user/${user.id}`);
      setProgressiUtente(response.data);
    } catch (error) {
    }
  };

  const fetchCertificatiOperatore = async () => {
    try {
      const response = await axios.get(`${API}/formazione/certificati?user_id=${user.id}`);
      setCertificatiOperatore(response.data);
    } catch (error) {
    }
  };

  const downloadPDFAttestato = async (certificatoId, nomeOperatore) => {
    try {
      const response = await axios.get(`${API}/formazione/certificati/${certificatoId}/pdf`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `attestato_${nomeOperatore.replace(/\s+/g, '_')}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Attestato scaricato con successo!');
    } catch (error) {
      toast.error('Errore nel download dell\'attestato');
    }
  };

  const downloadPDFTest = async (certificatoId, nomeOperatore) => {
    try {
      const response = await axios.get(`${API}/formazione/certificati/${certificatoId}/test-pdf`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `test_${nomeOperatore.replace(/\s+/g, '_')}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Test scaricato con successo!');
    } catch (error) {
      toast.error('Errore nel download del test');
    }
  };


  const iniziaCorso = async (corsoId) => {
    try {
      await axios.post(`${API}/formazione/corsi/${corsoId}/inizia`);
      toast.success('Corso iniziato!');
      fetchProgressiUtente();
      // Apri il corso
      const corso = corsiAssegnati.find(c => c.id === corsoId);
      if (corso) {
        viewCorsoDettaglio(corso);
      }
    } catch (error) {
      toast.error('Errore nell\'avvio del corso');
    }
  };

  const viewCorsoDettaglio = async (corso) => {
    setCorsoCorrente(corso);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/formazione/corsi/${corso.id}/moduli`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setModuliCorso(response.data);
    } catch (error) {
      toast.error('Errore nel caricamento dei moduli');
    }
  };

  const backToCorsiList = () => {
    setCorsoCorrente(null);
    setModuliCorso([]);
    setModuloCorrente(null);
  };

  const viewModuloDettaglio = async (modulo) => {
    setModuloCorrente(modulo);
    // Reset test per evitare che rimanga quello del modulo precedente
    setTestModulo(null);
    setDomandeTest([]);
    setRisposteUtente({});
    setTestInCorso(false);
    
    // Carica test se presente
    if (modulo.has_test && modulo.test_id) {
      try {
        const testResponse = await axios.get(`${API}/formazione/test/${modulo.test_id}`);
        setTestModulo(testResponse.data);
      } catch (error) {
        setTestModulo(null);
      }
    }
  };

  const backToModuliList = () => {
    setModuloCorrente(null);
    setTestModulo(null);
    setDomandeTest([]);
    setRisposteUtente({});
    setTestInCorso(false);
  };

  const iniziaTest = async () => {
    if (!testModulo) return;
    try {
      const response = await axios.get(`${API}/formazione/test/${testModulo.id}/domande`);
      setDomandeTest(response.data);
      setRisposteUtente({});
      setTestInCorso(true);
      setShowTestDialog(true);
    } catch (error) {
      toast.error('Errore nel caricamento del test');
    }
  };

  const handleRisposta = (domandaId, risposta) => {
    setRisposteUtente({
      ...risposteUtente,
      [domandaId]: risposta
    });
  };

  const inviaTest = async () => {
    try {
      // Invia risposte al backend
      const risposte = Object.entries(risposteUtente).map(([domandaId, risposta]) => ({
        domanda_id: domandaId,
        risposta: risposta
      }));

      const response = await axios.post(`${API}/formazione/test/${testModulo.id}/risposte`, { risposte });
      
      toast.success('Test inviato con successo!');
      setShowTestDialog(false);
      setTestInCorso(false);
      
      // Aggiorna progresso
      await fetchProgressiUtente();
      
      // Se è test finale e mostra questionario, aprilo
      if (response.data.mostra_questionario) {
        toast.success(`Punteggio: ${response.data.punteggio}% - Completa il questionario di gradimento!`, { duration: 5000 });
        setTimeout(async () => {
          // Carica questionario
          const token = localStorage.getItem('token');
          const questResponse = await axios.get(`${API}/formazione/corsi/${corsoCorrente.id}/questionario`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setQuestionario(questResponse.data);
          setRisposteQuestionario({});
          setCommentiQuestionario('');
          setShowQuestionarioDialog(true);
        }, 1500);
      } else {
        toast.success(`Punteggio: ${response.data.punteggio}%`, { duration: 3000 });
      }
    } catch (error) {
      toast.error('Errore nell\'invio del test');
    }
  };

  const inviaQuestionario = async () => {
    try {
      const response = await axios.post(`${API}/formazione/corsi/${corsoCorrente.id}/questionario/rispondi`, {
        risposte: risposteQuestionario,
        commenti: commentiQuestionario
      });
      
      setShowQuestionarioDialog(false);
      
      // Ricarica progresso per aggiornare stato test completato
      await fetchProgressiUtente();
      
      // Se corso completato mostra dialog congratulazioni
      if (response.data.corso_completato && response.data.certificato_id) {
        setCertificatoCompletato({
          certificato_id: response.data.certificato_id,
          corso_titolo: response.data.corso_titolo
        });
        setShowCompletamentoDialog(true);
        
        // Ricarica certificati operatore
        await fetchCertificatiOperatore();
      } else {
        toast.success('Grazie per il tuo feedback!', { duration: 5000 });
        // Torna alla lista corsi
        setTimeout(() => {
          backToCorsiList();
        }, 2000);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nell\'invio del questionario');
    }
  };

  const completaModulo = async (moduloId) => {
    try {
      await axios.post(`${API}/formazione/moduli/${moduloId}/completa`);
      toast.success('Modulo completato!');
      
      // Aggiorna progressi
      await fetchProgressiUtente();
      
      // Trova il modulo successivo
      const currentIndex = moduliCorso.findIndex(m => m.id === moduloId);
      if (currentIndex >= 0 && currentIndex < moduliCorso.length - 1) {
        const nextModulo = moduliCorso[currentIndex + 1];
        toast.success(`Prosegui con: ${nextModulo.titolo}`, { duration: 3000 });
        
        // Apri automaticamente il prossimo modulo dopo un breve delay
        setTimeout(() => {
          viewModuloDettaglio(nextModulo);
        }, 1500);
      } else {
        // Ultimo modulo completato
        toast.success('🎉 Hai completato tutti i moduli del corso!', { duration: 4000 });
        setTimeout(() => {
          backToModuliList();
        }, 2000);
      }
    } catch (error) {
      toast.error('Errore nel completamento del modulo');
    }
  };

  const fetchInvoices = async () => {
    try {
      const response = await axios.get(`${API}/invoices`);
      setInvoices(response.data);
    } catch (error) {
      toast.error('Errore nel caricamento delle fatture');
    }
  };

  const fetchInvoiceStats = async () => {
    try {
      const response = await axios.get(`${API}/invoices/stats/operator`);
      setInvoiceStats(response.data);
    } catch (error) {
    }
  };

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings/public`);
      setSettings(response.data);
    } catch (error) {
    }
  };

  const fetchReports = async () => {
    try {
      const response = await axios.get(`${API}/reports/my-reports`);
      setReports(response.data);
    } catch (error) {
      toast.error('Errore nel caricamento dei report');
    }
  };

  const fetchTeamReports = async () => {
    if (!user.is_coordinatore) return;
    try {
      const response = await axios.get(`${API}/reports/team`);
      setTeamReports(response.data);
    } catch (error) {
      toast.error('Errore nel caricamento dei report del team');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingReport) {
        await axios.put(`${API}/reports/${editingReport.id}`, formData);
        toast.success('Report aggiornato con successo!');
      } else {
        await axios.post(`${API}/reports/submit`, {
          month: selectedMonth,
          year: selectedYear,
          ...formData
        });
        toast.success('Report inserito con successo!');
      }
      setShowForm(false);
      setEditingReport(null);
      resetForm();
      fetchReports();
      // Se coordinatore e stava modificando un report del team, ricarica anche quelli
      if (user.is_coordinatore) {
        fetchTeamReports();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nell\'operazione');
    }
  };

  const resetForm = () => {
    setFormData({
      accessi_data: {},
      assenze: {},
      note_turni: ''
    });
  };

  // Funzioni per gestione report team (coordinatori)
  const handleApproveReport = async (reportId) => {
    if (!confirm('Confermi di voler approvare questo report?')) return;
    try {
      await axios.post(`${API}/reports/${reportId}/approval`, {
        status: 'approved'
      });
      toast.success('Report approvato con successo!');
      fetchTeamReports();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nell\'approvazione');
    }
  };

  const handleRejectReport = async (reportId) => {
    const reason = prompt('Inserisci il motivo del rifiuto:');
    if (!reason) return;
    try {
      await axios.post(`${API}/reports/${reportId}/approval`, {
        status: 'rejected',
        rejection_reason: reason
      });
      toast.success('Report rifiutato');
      fetchTeamReports();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nel rifiuto');
    }
  };

  const handleEditTeamReport = (report) => {
    setEditingReport(report);
    setFormData({
      accessi_data: report.accessi_data || {},
      assenze: report.assenze || {},
      note_turni: report.note_turni || ''
    });
    setShowForm(true);
  };

  const handleEdit = (report) => {
    setEditingReport(report);
    setFormData({
      accessi_data: report.accessi_data || {},
      assenze: report.assenze || {},
      note_turni: report.note_turni || ''
    });
    setShowForm(true);
  };

  // Check if report can be edited (within 5 days of creation)
  const canEditReport = (report) => {
    if (!report.created_at) return false;
    const createdDate = new Date(report.created_at);
    const now = new Date();
    const diffTime = Math.abs(now - createdDate);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays <= 5;
  };

  // Calculate days from date range
  const calculateDaysFromRange = (dal, al) => {
    if (!dal || !al) return 0;
    const start = new Date(dal);
    const end = new Date(al);
    const diffTime = Math.abs(end - start);
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1; // +1 to include both start and end days
  };

  // Calculate total absence days from report
  const calculateAbsenceDays = (assenze) => {
    let totalDays = 0;
    
    // Malattia - periodi con date
    if (assenze.malattia && Array.isArray(assenze.malattia)) {
      assenze.malattia.forEach(periodo => {
        totalDays += calculateDaysFromRange(periodo.dal, periodo.al);
      });
    }
    
    // Ferie - periodi con date
    if (assenze.ferie && Array.isArray(assenze.ferie)) {
      assenze.ferie.forEach(periodo => {
        totalDays += calculateDaysFromRange(periodo.dal, periodo.al);
      });
    }
    
    // Permessi, L104, Recupero, Pronta Disponibilità - conteggio giorni unici
    const uniqueDates = new Set();
    ['permesso_ore', 'permesso_l104_ore', 'recupero_ore', 'pronta_disponibilita_ore'].forEach(tipo => {
      if (assenze[tipo] && Array.isArray(assenze[tipo])) {
        assenze[tipo].forEach(item => {
          if (item.data) uniqueDates.add(item.data);
        });
      }
    });
    totalDays += uniqueDates.size;
    
    return totalDays;
  };

  // Change password function
  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    // Validazione
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.error('Le password non coincidono');
      return;
    }
    
    if (passwordData.newPassword.length < 8) {
      toast.error('La password deve essere di almeno 8 caratteri');
      return;
    }
    
    // Verifica requisiti password
    const hasNumber = /\d/.test(passwordData.newPassword);
    const hasSpecial = /[!@#$%^&*(),.?":{}|<>]/.test(passwordData.newPassword);
    
    if (!hasNumber || !hasSpecial) {
      toast.error('La password deve contenere almeno un numero e un carattere speciale');
      return;
    }
    
    try {
      // Use the new change-password endpoint
      await axios.post(`${API}/auth/change-password`, {
        old_password: passwordData.currentPassword,
        new_password: passwordData.newPassword
      });
      
      toast.success('Password modificata con successo!');
      setShowChangePasswordDialog(false);
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error('Password attuale non corretta');
      } else {
        toast.error('Errore nella modifica della password');
      }
    }
  };

  // Invoice management functions for operators
  const handleMarkInvoiceViewed = async (invoiceId) => {
    try {
      await axios.post(`${API}/invoices/${invoiceId}/mark-viewed`);
      toast.success('Fattura segnata come vista!');
      fetchInvoices();
    } catch (error) {
      toast.error('Errore nella marcatura della fattura');
    }
  };

  const handleRequestVerification = async (e) => {
    e.preventDefault();
    if (!selectedInvoice || !verificationNote.trim()) {
      toast.error('Inserisci una nota per la richiesta di verifica');
      return;
    }
    
    try {
      await axios.post(`${API}/invoices/${selectedInvoice.id}/request-verification`, {
        nota: verificationNote
      });
      toast.success('Richiesta di verifica inviata al Super Admin!');
      setShowRequestVerificationDialog(false);
      setVerificationNote('');
      fetchInvoices();
    } catch (error) {
      toast.error('Errore nell\'invio della richiesta');
    }
  };

  const openInvoiceDetail = (invoice) => {
    setSelectedInvoice(invoice);
    setShowInvoiceDetailDialog(true);
  };

  const openUploadInvoiceDialog = (invoice) => {
    setSelectedInvoice(invoice);
    setInvoiceUploadData({
      data_fattura: invoice.data_fattura || '',
      numero_fattura: invoice.numero_fattura || ''
    });
    setShowUploadInvoiceDialog(true);
  };

  const handleUploadInvoice = async (e) => {
    e.preventDefault();
    if (!selectedInvoice) return;
    
    try {
      await axios.post(`${API}/invoices/${selectedInvoice.id}/upload`, invoiceUploadData);
      toast.success('Estremi fattura caricati con successo!');
      setShowUploadInvoiceDialog(false);
      fetchInvoices();
    } catch (error) {
      toast.error('Errore nel caricamento degli estremi della fattura');
    }
  };

  // Calculate statistics for operator
  const getOperatorStats = () => {
    const statsByMonth = {};
    reports.forEach(report => {
      const monthKey = `${report.year}-${String(report.month).padStart(2, '0')}`;
      const totalAccessi = getTotalAccessiFromData(report.accessi_data);
      const absenceDays = calculateAbsenceDays(report.assenze || {});
      statsByMonth[monthKey] = { accessi: totalAccessi, absenceDays };
    });
    return statsByMonth;
  };

  const months = monthsList;

  const getNoteForFigura = () => {
    if (user.figura_professionale === "Infermiere") {
      return "Nota: Per i pacchetti multipli di un'ora (pacchetti sollievo), indicare il numero di ore nel campo 'Alta Complessità'.";
    }
    if (user.figura_professionale === "OSS") {
      return "Nota: Per i pacchetti multipli di un'ora (pacchetti sollievo), indicare il numero di ore nel campo 'Bassa Complessità'.";
    }
    return null;
  };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="operator-dashboard">
      {/* Header */}
      <div className="bg-white border-b shadow-sm sticky top-0 z-30">
        <div className="px-4 sm:px-6 py-3 sm:py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 sm:gap-4 pl-10 lg:pl-0">
              <img src="/logo.png" alt="Logo" className="h-10 sm:h-12" />
              <div>
                <h1 className="text-sm sm:text-base md:text-xl font-bold text-gray-900">Croce Gialla Home</h1>
                <p className="text-xs sm:text-sm text-gray-500">{user.full_name}{user.figura_professionale ? ` · ${user.figura_professionale}` : ''}</p>
              </div>
            </div>
            <div className="flex items-center gap-1 sm:gap-2">
              <Button
                onClick={() => setShowChangePasswordDialog(true)}
                variant="outline"
                size="sm"
                className="gap-1 sm:gap-2 text-xs sm:text-sm px-2 sm:px-3"
              >
                <svg className="w-3 h-3 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                </svg>
                <span className="hidden md:inline">Password</span>
              </Button>
              <Button
                data-testid="logout-button"
                onClick={onLogout}
                variant="outline"
                size="sm"
                className="gap-1 sm:gap-2 text-xs sm:text-sm px-2 sm:px-3"
              >
                <LogOut className="w-3 h-3 sm:w-4 sm:h-4" />
                <span className="hidden md:inline">Esci</span>
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Sidebar + Content */}
      <div className="flex min-h-[calc(100vh-65px)]">
        <OperatorSidebar activeTab={activeTab} onTabChange={setActiveTab} user={user} />

        <main className="flex-1 overflow-auto">
          <div className="p-4 sm:p-6 lg:p-8 max-w-7xl">

        {/* Home Tab */}
        {activeTab === 'home' && (
          <OperatorHome 
            user={user} 
            reports={reports} 
            corsiAssegnati={corsiAssegnati}
            progressiUtente={progressiUtente}
            richiesteAssenze={richiesteAssenze}
            comunicazioni={comunicazioniList}
            teamMembers={teamMembers}
            onNavigate={setActiveTab} 
          />
        )}

        {/* Reports Tab Content */}
        {activeTab === 'reports' && (
          <div className="space-y-4">
            <div className="mb-4 sm:mb-6 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
          <h2 className="text-lg sm:text-xl md:text-2xl font-bold text-gray-900">
            {user.tipo_contratto === 'libero_professionista' ? 'Le Mie Fatture' : 'I Miei Report Mensili'}
          </h2>
          {user.tipo_contratto !== 'libero_professionista' && (
            <>
              <Button 
                data-testid="new-report-button" 
                onClick={() => {
                  setEditingReport(null);
                  resetForm();
                  setShowForm(true);
                }}
                className="bg-cyan-600 hover:bg-cyan-700 text-white gap-2"
              >
                <Plus className="w-4 h-4" />
                Nuovo Report
              </Button>
            </>
          )}
        </div>

        {/* Dialog Nuovo/Modifica Report - Condiviso tra tutte le sezioni */}
        <Dialog open={showForm} onOpenChange={(open) => {
          setShowForm(open);
          if (!open) {
            setEditingReport(null);
            resetForm();
          }
        }}>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto p-4 sm:p-6">
              <DialogHeader>
                <DialogTitle>{editingReport ? 'Modifica Report' : 'Nuovo Report Mensile'}</DialogTitle>
                <DialogDescription>
                  {editingReport ? 'Aggiorna i dati del report' : 'Inserisci i dati mensili'}
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-6 mt-4">
                {!editingReport && (
                  <div className="space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-1.5">
                        <Label htmlFor="month-input" className="text-xs font-medium">Mese</Label>
                        <select
                          id="month-input"
                          data-testid="month-select"
                          className="flex h-9 w-full rounded-md border border-gray-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                          value={selectedMonth}
                          onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
                        >
                          {months.map((month, idx) => (
                            <option key={idx + 1} value={idx + 1}>{month}</option>
                          ))}
                        </select>
                      </div>
                      <div className="space-y-1.5">
                        <Label htmlFor="year-input" className="text-xs font-medium">Anno</Label>
                        <select
                          id="year-input"
                          data-testid="year-select"
                          className="flex h-9 w-full rounded-md border border-gray-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                          value={selectedYear}
                          onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                        >
                          {[2024, 2025, 2026].map(year => (
                            <option key={year} value={year}>{year}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                    {/* Avviso mese di riferimento */}
                    {(() => {
                      const now = new Date();
                      const prevMonth = now.getMonth(); // 0-indexed, so this is prev month (1-indexed)
                      const prevYear = prevMonth === 0 ? now.getFullYear() - 1 : now.getFullYear();
                      const expectedMonth = prevMonth === 0 ? 12 : prevMonth;
                      if (selectedMonth !== expectedMonth || selectedYear !== prevYear) {
                        return (
                          <div className="flex items-start gap-2 p-2.5 bg-amber-50 border border-amber-200 rounded-lg">
                            <svg className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                            </svg>
                            <p className="text-xs text-amber-800">
                              <strong>Attenzione:</strong> Il mese previsto per il report è <strong>{months[expectedMonth - 1]} {prevYear}</strong>. Assicurati di aver selezionato il mese corretto.
                            </p>
                          </div>
                        );
                      }
                      return null;
                    })()}
                  </div>
                )}

                {/* Sezione Accessi Dinamici */}
                <div className="space-y-4">
                  <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                    <Activity className="w-5 h-5 text-cyan-600" />
                    Numero Accessi
                  </h3>
                  
                  {settings && settings.figure_professionali && (() => {
                    const userFigura = settings.figure_professionali.find(f => f.nome === user.figura_professionale);
                    if (!userFigura) return <p className="text-sm text-gray-500">Caricamento tipologie accessi...</p>;
                    
                    return (
                      <>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                          {userFigura.tipologie_accessi.map((tipo) => (
                            <div key={tipo.nome} className="space-y-2">
                              <Label className="capitalize">{tipo.nome} ({tipo.minuti} min)</Label>
                              <Input
                                type="number"
                                min="0"
                                value={formData.accessi_data[tipo.nome] || 0}
                                onChange={(e) => setFormData({
                                  ...formData,
                                  accessi_data: {
                                    ...formData.accessi_data,
                                    [tipo.nome]: parseInt(e.target.value) || 0
                                  }
                                })}
                              />
                            </div>
                          ))}
                        </div>
                        
                        {/* Nota pacchetti sollievo */}
                        {userFigura.nota_pacchetti && (
                          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mt-3">
                            <p className="text-sm text-yellow-800">{userFigura.nota_pacchetti}</p>
                          </div>
                        )}
                      </>
                    );
                  })()}
                </div>

                {/* Sezione Assenze con nuovo componente */}
                <div className="space-y-4">
                  <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                    <Calendar className="w-5 h-5 text-cyan-600" />
                    Gestione Assenze
                  </h3>
                  <AssenzeManager
                    assenze={formData.assenze}
                    onChange={(newAssenze) => setFormData({...formData, assenze: newAssenze})}
                    noteTurni={formData.note_turni}
                    onNoteTurniChange={(note) => setFormData({...formData, note_turni: note})}
                  />
                </div>

                {/* Sezione KM Percorsi */}
                <div className="space-y-4">
                  <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                    <svg className="w-5 h-5 text-cyan-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
                    </svg>
                    Chilometri auto
                  </h3>
                  <p className="text-xs text-gray-600 italic">Da compilare solo se in possesso di auto aziendale</p>
                  <div className="space-y-3 bg-gray-50 p-4 rounded-lg">
                    <div className="space-y-2">
                      <Label htmlFor="km-tratte">Targa auto</Label>
                      <textarea
                        id="km-tratte"
                        rows="3"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500"
                        placeholder="Es: AB123CD"
                        value={formData.km_tratte || ''}
                        onChange={(e) => setFormData({...formData, km_tratte: e.target.value})}
                      />
                      <p className="text-xs text-gray-500">Indica la targa dell'auto aziendale utilizzata</p>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="km-totali">km auto</Label>
                      <Input
                        id="km-totali"
                        type="number"
                        min="0"
                        step="0.1"
                        placeholder="0"
                        value={formData.km_totali || ''}
                        onChange={(e) => setFormData({...formData, km_totali: parseFloat(e.target.value) || 0})}
                      />
                    </div>
                  </div>
                </div>

                <div className="flex gap-3 pt-4">
                  <Button
                    data-testid="submit-report-button"
                    type="submit"
                    className="flex-1 bg-cyan-600 hover:bg-cyan-700 text-white"
                  >
                    {editingReport ? 'Aggiorna' : 'Salva Report'}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setShowForm(false);
                      setEditingReport(null);
                      resetForm();
                    }}
                  >
                    Annulla
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>

        {/* Grafico Statistiche Accessi */}
        {reports.length > 0 && (
          <Card className="shadow-lg border-0 mb-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-cyan-600" />
                Statistiche Accessi per Mese
              </CardTitle>
              <CardDescription>Visualizzazione del numero totale di accessi mensili</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {(() => {
                  const stats = getOperatorStats();
                  const maxAccessi = Math.max(...Object.values(stats).map(s => s.accessi));
                  
                  return Object.entries(stats).sort((a, b) => b[0].localeCompare(a[0])).slice(0, 6).map(([monthKey, data]) => {
                    const [year, month] = monthKey.split('-');
                    const monthName = monthsList[parseInt(month) - 1];
                    const percentage = maxAccessi > 0 ? (data.accessi / maxAccessi) * 100 : 0;
                    
                    return (
                      <div key={monthKey} className="space-y-1">
                        <div className="flex justify-between items-center text-sm flex-wrap gap-2">
                          <span className="font-medium text-gray-700">{monthName} {year}</span>
                          <div className="flex gap-4 items-center">
                            <span className="font-bold text-cyan-600">{data.accessi} accessi</span>
                            {data.absenceDays > 0 && (
                              <span className="text-xs text-orange-600 bg-orange-50 px-2 py-1 rounded">
                                {data.absenceDays} {data.absenceDays === 1 ? 'giorno' : 'giorni'} assenza
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-3">
                          <div 
                            className="bg-cyan-500 h-3 rounded-full transition-all duration-300"
                            style={{width: `${percentage}%`}}
                          ></div>
                        </div>
                      </div>
                    );
                  });
                })()}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Card Fatture per Liberi Professionisti / Report per Dipendenti */}
        {user.tipo_contratto === 'libero_professionista' ? (
          <>
            <Card className="shadow-sm border-0">
              <CardContent className="p-4">
                {invoices.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <FileText className="w-10 h-10 mx-auto mb-2 opacity-30" />
                    <p className="text-sm">Nessuna fattura presente</p>
                    <p className="text-xs mt-1 text-gray-400">Le fatture verranno inserite dal Super Amministratore</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {invoices.map((invoice) => (
                      <div key={invoice.id} className="p-3 border border-gray-200 rounded-lg bg-white hover:shadow-sm transition-shadow">
                        <div className="flex items-start justify-between gap-3 mb-2">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <h3 className="font-semibold text-sm text-gray-900">{months[invoice.month - 1]} {invoice.year}</h3>
                              <span className={`px-2 py-0.5 rounded-md text-[10px] font-medium ${
                                invoice.status === 'uploaded' ? 'bg-emerald-100 text-emerald-700' :
                                invoice.status === 'viewed' ? 'bg-blue-100 text-blue-700' :
                                'bg-amber-100 text-amber-700'
                              }`}>
                                {invoice.status === 'uploaded' ? 'Caricata' : invoice.status === 'viewed' ? 'Visto' : 'In Attesa'}
                              </span>
                            </div>
                            <p className="text-lg font-bold text-gray-900 mt-0.5">€{invoice.totale_netto.toFixed(2)}</p>
                          </div>
                        </div>
                        
                        <div className="flex flex-wrap gap-1.5">
                          <Button size="sm" variant="outline" onClick={() => openInvoiceDetail(invoice)}
                            className="h-7 gap-1 text-xs text-gray-600 border-gray-200 hover:bg-gray-50" data-testid={`invoice-detail-${invoice.id}`}>
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
                            Dettaglio
                          </Button>
                          <Button size="sm" variant="outline" className="h-7 gap-1 text-xs text-gray-600 border-gray-200 hover:bg-gray-50"
                            onClick={async () => {
                              try {
                                const response = await fetch(`${API}/invoices/${invoice.id}/pdf`, {
                                  method: 'GET', headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
                                });
                                if (!response.ok) throw new Error('Errore download');
                                const blob = await response.blob();
                                const url = window.URL.createObjectURL(blob);
                                const link = document.createElement('a');
                                link.href = url; link.download = `Fattura_${invoice.month}_${invoice.year}.pdf`;
                                document.body.appendChild(link); link.click(); document.body.removeChild(link);
                                window.URL.revokeObjectURL(url);
                              } catch (error) { alert('Errore durante il download del PDF'); }
                            }}>
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                            PDF
                          </Button>
                          {invoice.status === 'pending' && (
                            <Button size="sm" onClick={() => handleMarkInvoiceViewed(invoice.id)}
                              className="h-7 gap-1 text-xs bg-cyan-600 hover:bg-cyan-700">
                              Segna Visto
                            </Button>
                          )}
                          {(invoice.status === 'viewed' || invoice.status === 'uploaded') && (
                            <Button size="sm" onClick={() => openUploadInvoiceDialog(invoice)}
                              className="h-7 gap-1 text-xs bg-cyan-600 hover:bg-cyan-700">
                              {invoice.status === 'uploaded' ? 'Modifica Estremi' : 'Carica Estremi'}
                            </Button>
                          )}
                          {invoice.status === 'pending' && (
                            <Button size="sm" variant="outline" onClick={() => { setSelectedInvoice(invoice); setShowRequestVerificationDialog(true); }}
                              className="h-7 gap-1 text-xs text-amber-600 border-amber-200 hover:bg-amber-50"
                              disabled={invoice.richiesta_verifica}>
                              {invoice.richiesta_verifica ? 'Verifica Richiesta' : 'Richiedi Verifica'}
                            </Button>
                          )}
                        </div>
                        
                        {invoice.numero_fattura && (
                          <div className="mt-2 text-xs text-gray-500 bg-gray-50 p-2 rounded border border-gray-100">
                            Fattura N. {invoice.numero_fattura} del {invoice.data_fattura}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
            </CardContent>
          </Card>
          
          {/* Grafico Statistiche per Liberi Professionisti */}
          {invoiceStats && invoiceStats.importi_per_mese.length > 0 && (
            <div className="mt-6">
              {/* Grafico Accessi per Tipologia */}
              <Card className="shadow-lg border-0">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <BarChart3 className="w-5 h-5 text-cyan-600" />
                    Distribuzione Accessi
                  </CardTitle>
                  <CardDescription>Accessi per tipologia di intensità</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {(() => {
                      const tipologie = [
                        { nome: 'Bassa Intensità', value: invoiceStats.accessi_per_tipologia.bassa, color: 'from-blue-400 to-blue-600' },
                        { nome: 'Media Intensità', value: invoiceStats.accessi_per_tipologia.media, color: 'from-cyan-400 to-cyan-600' },
                        { nome: 'Alta Intensità', value: invoiceStats.accessi_per_tipologia.alta, color: 'from-teal-400 to-teal-600' }
                      ];
                      const maxAccessi = Math.max(...tipologie.map(t => t.value));
                      return tipologie.map((tip) => {
                        const percentage = maxAccessi > 0 ? (tip.value / maxAccessi) * 100 : 0;
                        return (
                          <div key={tip.nome} className="space-y-1">
                            <div className="flex justify-between items-center text-sm">
                              <span className="font-medium text-gray-700">{tip.nome}</span>
                              <span className="font-bold text-cyan-600">{tip.value} accessi</span>
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-3">
                              <div 
                                className={`bg-gradient-to-r ${tip.color} h-3 rounded-full transition-all duration-300`}
                                style={{width: `${percentage}%`}}
                              ></div>
                            </div>
                          </div>
                        );
                      });
                    })()}
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Sezione Report per Liberi Professionisti */}
          <Card className="shadow-lg border-0 mt-6">
            <CardHeader>
              <CardTitle>I Miei Report Mensili</CardTitle>
              <CardDescription>Inserisci i tuoi report mensili come libero professionista</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <Button 
                  onClick={() => {
                    setEditingReport(null);
                    resetForm();
                    setShowForm(true);
                  }}
                  className="bg-cyan-600 hover:bg-cyan-700 text-white"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Nuovo Report
                </Button>

                {reports.length === 0 ? (
                  <div className="text-center py-12 text-gray-500">
                    <FileText className="w-12 h-12 mx-auto mb-3 opacity-30" />
                    <p>Nessun report presente</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Periodo</TableHead>
                          <TableHead className="text-center">Stato</TableHead>
                          <TableHead className="text-center">Tot. Accessi</TableHead>
                          <TableHead className="text-right">Azioni</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {reports.map((report) => {
                          const totalAccessi = getTotalAccessiFromData(report.accessi_data);
                          return (
                            <TableRow key={report.id}>
                              <TableCell className="font-medium">
                                {months[report.month - 1]} {report.year}
                              </TableCell>
                              <TableCell className="text-center">
                                {report.status === 'approved' && (
                                  <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-800">
                                    Approvato
                                  </span>
                                )}
                                {report.status === 'rejected' && (
                                  <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-800">
                                    Rifiutato
                                  </span>
                                )}
                                {report.status === 'pending' && (
                                  <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-yellow-100 text-yellow-800">
                                    In Attesa
                                  </span>
                                )}
                              </TableCell>
                              <TableCell className="text-center">{totalAccessi}</TableCell>
                              <TableCell className="text-right">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleEdit(report)}
                                  disabled={report.status === 'approved'}
                                  className="text-blue-600 hover:bg-blue-50"
                                >
                                  Modifica
                                </Button>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
          </>
        ) : (
          /* Card Report per Dipendenti */
          <Card className="shadow-lg border-0">
            <CardHeader>
              <CardTitle>Storico Report</CardTitle>
              <CardDescription>Visualizza tutti i report inviati</CardDescription>
            </CardHeader>
            <CardContent>
            {reports.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <FileText className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p>Nessun report presente</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Periodo</TableHead>
                      <TableHead className="text-center">Stato</TableHead>
                      <TableHead className="text-center">Totale Accessi</TableHead>
                      <TableHead className="text-center">Malattia</TableHead>
                      <TableHead className="text-center">Ferie</TableHead>
                      <TableHead className="text-center">Assenze</TableHead>
                      <TableHead className="text-right">Azioni</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {reports.map((report) => {
                      const totalAccessi = getTotalAccessiFromData(report.accessi_data);
                      const periodi_malattia = report.assenze?.malattia?.length || 0;
                      const periodi_ferie = report.assenze?.ferie?.length || 0;
                      const permessi = (report.assenze?.permesso_ore || 0) + (report.assenze?.permesso_l104_ore || 0);
                      
                      return (
                        <>
                          <TableRow key={report.id}>
                            <TableCell className="font-medium">
                              {months[report.month - 1]} {report.year}
                            </TableCell>
                            <TableCell className="text-center">
                              {report.status === 'approved' && (
                                <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-800 border border-green-200">
                                  <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                  </svg>
                                  Approvato
                                </span>
                              )}
                              {report.status === 'rejected' && (
                                <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-800 border border-red-200">
                                  <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                                  </svg>
                                  Rifiutato
                                </span>
                              )}
                              {report.status === 'pending' && (
                                <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-yellow-100 text-yellow-800 border border-yellow-200">
                                  <svg className="w-3 h-3 mr-1 animate-pulse" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                                  </svg>
                                  In Attesa
                                </span>
                              )}
                            </TableCell>
                            <TableCell className="text-center font-semibold">{totalAccessi}</TableCell>
                            <TableCell className="text-center">
                              {periodi_malattia > 0 ? `${periodi_malattia} periodo/i` : '-'}
                            </TableCell>
                            <TableCell className="text-center">
                              {periodi_ferie > 0 ? `${periodi_ferie} periodo/i` : '-'}
                            </TableCell>
                            <TableCell className="text-center text-xs">
                              {permessi > 0 ? `${permessi}h permessi` : '-'}
                            </TableCell>
                            <TableCell className="text-right">
                              {canEditReport(report) ? (
                                <Button
                                  data-testid={`edit-report-${report.id}`}
                                  onClick={() => handleEdit(report)}
                                  variant="outline"
                                  size="sm"
                                >
                                  Modifica
                                </Button>
                              ) : (
                                <span className="text-xs text-gray-400 italic">Non modificabile</span>
                              )}
                            </TableCell>
                          </TableRow>
                          {/* Riga aggiuntiva per motivo rifiuto */}
                          {report.status === 'rejected' && report.rejection_reason && (
                            <TableRow key={`${report.id}-rejection`}>
                              <TableCell colSpan={7} className="bg-red-50 border-l-4 border-l-red-500">
                                <div className="py-2 px-3">
                                  <p className="text-sm font-semibold text-red-800 mb-1">
                                    <svg className="w-4 h-4 inline mr-1" fill="currentColor" viewBox="0 0 20 20">
                                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                    </svg>
                                    Motivo del rifiuto:
                                  </p>
                                  <p className="text-sm text-red-700 ml-5">{report.rejection_reason}</p>
                                </div>
                              </TableCell>
                            </TableRow>
                          )}
                        </>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
        )}

        {/* Report Team da Gestire (Solo Coordinatori) */}
        {user.is_coordinatore && (
          <Card className="shadow-sm border-0 mt-4">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-base">
                <Users className="w-4 h-4 text-cyan-600" />
                Report Team
              </CardTitle>
              <CardDescription className="text-xs">Report degli operatori del tuo team</CardDescription>
            </CardHeader>
            <CardContent>
              {teamReports.length === 0 ? (
                <div className="text-center py-6 text-gray-400">
                  <FileText className="w-8 h-8 mx-auto mb-2 opacity-30" />
                  <p className="text-sm">Nessun report dal team</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {teamReports.map((report) => {
                    const totalAccessi = getTotalAccessiFromData(report.accessi_data);
                    return (
                      <div key={report.id} className="flex items-center gap-3 p-3 rounded-lg border border-gray-100 hover:border-gray-200 hover:bg-gray-50/50 transition-all group">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-medium text-sm text-gray-900">{report.user_full_name || 'Operatore'}</span>
                            <span className="text-xs text-gray-500">{months[report.month - 1]} {report.year}</span>
                            <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${
                              report.status === 'approved' ? 'bg-emerald-100 text-emerald-700' :
                              report.status === 'rejected' ? 'bg-red-100 text-red-700' :
                              'bg-amber-100 text-amber-700'
                            }`}>
                              {report.status === 'approved' ? 'Approvato' : report.status === 'rejected' ? 'Rifiutato' : 'In Attesa'}
                            </span>
                          </div>
                          <div className="text-xs text-gray-500 mt-0.5">{totalAccessi} accessi</div>
                        </div>
                        <Button size="sm" variant="outline" onClick={() => handleEditTeamReport(report)}
                          className="h-8 gap-1.5 text-xs text-gray-600 border-gray-200 hover:bg-gray-100 opacity-60 group-hover:opacity-100 transition-opacity">
                          <Edit className="w-3 h-3" />
                          Gestisci
                        </Button>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        )}

          </div>
        )}

        {/* Convenzioni Tab Content */}
        {activeTab === 'convenzioni' && (
      <Card className="shadow-lg border-0">
        <CardHeader className="bg-white border-b border-gray-100">
          <CardTitle className="flex items-center gap-2">
            <svg className="w-6 h-6 text-cyan-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7" />
            </svg>
            Convenzioni Aziendali
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-6">
          {convenzioni.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <svg className="w-16 h-16 mx-auto mb-4 opacity-30" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7" />
              </svg>
              <p className="text-lg font-medium mb-2">Nessuna convenzione disponibile al momento</p>
              <p className="text-sm">Le nuove convenzioni saranno visibili qui non appena disponibili</p>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Grid convenzioni */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {convenzioni.map((conv) => (
                  <Card key={conv.id} className="overflow-hidden hover:shadow-lg transition-shadow">
                    <div className="h-32 bg-cyan-600 relative overflow-hidden">
                      {conv.immagine_url ? (
                        <img src={getAuthenticatedFileUrl(conv.immagine_url)} alt={conv.titolo} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <svg className="w-16 h-16 text-white opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7" />
                          </svg>
                        </div>
                      )}
                      <div className="absolute top-2 right-2">
                        <span className="px-2 py-1 bg-white text-xs font-semibold rounded-full shadow capitalize">
                          {conv.categoria}
                        </span>
                      </div>
                    </div>
                    <CardContent className="p-4">
                      <h3 className="font-bold text-lg mb-2 text-gray-900">{conv.titolo}</h3>
                      <p className="text-sm text-gray-600 mb-3">{conv.descrizione}</p>
                      <div className="flex items-center gap-2 mb-3">
                        <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <span className="text-green-600 font-bold text-lg">{conv.offerta || conv.sconto}</span>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setSelectedConvenzione(conv);
                            setShowConvenzioneInfo(true);
                          }}
                          className="flex-1"
                        >
                          <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          Info
                        </Button>
                        {conv.link && (
                          <a 
                            href={conv.link} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="flex-1"
                          >
                            <Button className="w-full bg-cyan-600 hover:bg-cyan-700">
                              Vai →
                            </Button>
                          </a>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>
        )}

        {/* Formazione Section */}
        {activeTab === 'formazione' && (
          !corsoCorrente ? (
            <div className="space-y-4">
              {/* I Miei Corsi */}
              <Card className="shadow-lg border-0">
                <CardHeader>
                  <div className="flex flex-col gap-3">
                    <CardTitle>I Miei Corsi di Formazione</CardTitle>
                    {/* Filtro Stato Corso con stile calendario */}
                    <div className="flex items-center gap-2 px-4 py-3 bg-white border border-gray-200 rounded-xl w-fit">
                      <svg className="w-5 h-5 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                      </svg>
                      <select
                        className="bg-transparent border-none outline-none text-sm font-medium text-gray-700 cursor-pointer pr-2"
                        value={filtroStatoCorso}
                        onChange={(e) => setFiltroStatoCorso(e.target.value)}
                      >
                        <option value="tutti">Tutti i corsi</option>
                        <option value="completato">Completati</option>
                        <option value="in_corso">In corso</option>
                        <option value="nuovo">Nuovi corsi</option>
                      </select>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  {(() => {
                    // Filtra corsi basato sullo stato
                    const corsiFiltrati = corsiAssegnati.filter((corso) => {
                      const progresso = progressiUtente.find(p => p.corso_id === corso.id);
                      
                      if (filtroStatoCorso === 'tutti') return true;
                      if (filtroStatoCorso === 'completato') return progresso?.completato === true;
                      if (filtroStatoCorso === 'in_corso') return progresso && !progresso.completato;
                      if (filtroStatoCorso === 'nuovo') return !progresso;
                      
                      return true;
                    });
                    
                    return corsiFiltrati.length === 0 ? (
                      <div className="text-center py-12 text-gray-500">
                        <svg className="w-12 h-12 mx-auto mb-3 opacity-30" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 14l9-5-9-5-9 5 9 5z" />
                        </svg>
                        <p>Nessun corso {
                          filtroStatoCorso === 'completato' ? 'completato' :
                          filtroStatoCorso === 'in_corso' ? 'in corso' :
                          filtroStatoCorso === 'nuovo' ? 'nuovo' :
                          'trovato'
                        }</p>
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {corsiFiltrati.map((corso) => {
                        const progresso = progressiUtente.find(p => p.corso_id === corso.id);
                        return (
                          <Card key={corso.id} className="overflow-hidden hover:shadow-md transition-shadow border border-gray-200">
                            <div className="h-28 bg-cyan-600 relative">
                              {corso.immagine_copertina_url && (
                                <img 
                                  src={getAuthenticatedFileUrl(corso.immagine_copertina_url)} 
                                  alt={corso.titolo} 
                                  className="w-full h-full object-cover" 
                                />
                              )}
                              {corso.obbligatorio && (
                                <div className="absolute top-2 right-2">
                                  <span className="px-2 py-0.5 bg-white/90 text-red-600 text-[10px] font-bold rounded-md shadow-sm">OBBLIGATORIO</span>
                                </div>
                              )}
                            </div>
                            <CardContent className="p-4">
                              <h3 className="font-bold text-lg mb-2">{corso.titolo}</h3>
                              <p className="text-sm text-gray-600 mb-3 line-clamp-2">{corso.descrizione}</p>
                              {progresso ? (
                                <div className="mb-3">
                                  {(() => {
                                    const moduliCompletatiCount = progresso.moduli_completati?.length || 0;
                                    const totaleModuli = moduliCountPerCorso[corso.id] || 0;
                                    const percentuale = progresso.completato 
                                      ? 100 
                                      : totaleModuli > 0 
                                        ? Math.round((moduliCompletatiCount / totaleModuli) * 100)
                                        : 0;
                                    
                                    return (
                                      <>
                                        <div className="flex justify-between text-xs mb-1">
                                          <span>Progresso</span>
                                          <span>{percentuale}%</span>
                                        </div>
                                        <div className="w-full bg-gray-100 rounded-full h-1.5">
                                          <div 
                                            className="bg-cyan-500 h-1.5 rounded-full transition-all" 
                                            style={{ width: `${percentuale}%` }}
                                          />
                                        </div>
                                        {totaleModuli > 0 && !progresso.completato && (
                                          <p className="text-xs text-gray-500 mt-1">
                                            {moduliCompletatiCount} di {totaleModuli} {totaleModuli === 1 ? 'modulo completato' : 'moduli completati'}
                                          </p>
                                        )}
                                        {progresso.completato && (
                                          <p className="text-xs text-green-600 font-semibold mt-1 flex items-center gap-1">
                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                            </svg>
                                            Corso Completato!
                                          </p>
                                        )}
                                      </>
                                    );
                                  })()}
                                </div>
                              ) : null}
                              <Button 
                                onClick={() => progresso ? viewCorsoDettaglio(corso) : iniziaCorso(corso.id)}
                                className={`w-full ${progresso?.completato ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-cyan-600 hover:bg-cyan-700'}`}
                              >
                                {progresso?.completato ? 'Visualizza Corso' : progresso ? 'Continua Corso' : 'Inizia Corso'}
                              </Button>
                            </CardContent>
                          </Card>
                        );
                      })}
                    </div>
                    );
                  })()}
                </CardContent>
              </Card>

              {/* I Miei Attestati */}
              {certificatiOperatore && certificatiOperatore.length > 0 && (
                <Card className="shadow-lg border-0">
                  <CardHeader>
                    <CardTitle>I Miei Attestati</CardTitle>
                    <CardDescription>Scarica i tuoi attestati di formazione completati</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {certificatiOperatore.map((cert) => (
                        <Card key={cert.id} className="overflow-hidden border-green-200">
                          <CardContent className="p-4">
                            <div className="flex items-start justify-between mb-3">
                              <div className="flex-1">
                                <h3 className="font-bold text-md mb-1">{cert.titolo_corso}</h3>
                                <p className="text-xs text-gray-600">
                                  Completato il {new Date(cert.data_completamento).toLocaleDateString('it-IT')}
                                </p>
                              </div>
                              <div className="ml-2">
                                <span className="inline-flex items-center px-2 py-1 bg-green-100 text-green-700 text-xs font-semibold rounded">
                                  {cert.punteggio_finale}%
                                </span>
                              </div>
                            </div>
                            <div className="flex gap-2 mt-4">
                              <Button 
                                size="sm"
                                onClick={() => downloadPDFAttestato(cert.id, cert.nome_operatore)}
                                className="flex-1 bg-cyan-600 hover:bg-cyan-700"
                              >
                                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                </svg>
                                Attestato
                              </Button>
                              <Button 
                                size="sm"
                                variant="outline"
                                onClick={() => downloadPDFTest(cert.id, cert.nome_operatore)}
                                className="flex-1"
                              >
                                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                </svg>
                                Test
                              </Button>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          ) : !moduloCorrente ? (
            <div className="space-y-4">
              {/* Corso Dettaglio */}
              <Card className="shadow-lg border-0">
              <CardHeader>
                <Button onClick={backToCorsiList} variant="ghost" size="sm" className="mb-2">
                  ← Torna ai Corsi
                </Button>
                <CardTitle>{corsoCorrente.titolo}</CardTitle>
                <CardDescription>Seleziona un modulo per iniziare</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {moduliCorso.map((modulo, idx) => {
                    const progresso = progressiUtente.find(p => p.corso_id === corsoCorrente.id);
                    const isCompletato = progresso?.moduli_completati?.includes(modulo.id);
                    const isAccessibile = idx === 0 || (progresso?.moduli_completati?.includes(moduliCorso[idx - 1]?.id));
                    
                    return (
                      <Card 
                        key={modulo.id} 
                        className={`transition-shadow ${
                          isAccessibile 
                            ? 'cursor-pointer hover:shadow-md' 
                            : 'opacity-60 cursor-not-allowed bg-gray-50'
                        }`}
                        onClick={() => isAccessibile && viewModuloDettaglio(modulo)}
                      >
                        <CardContent className="p-4">
                          <div className="flex items-center gap-3">
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                              isCompletato 
                                ? 'bg-green-100' 
                                : isAccessibile 
                                  ? 'bg-blue-100' 
                                  : 'bg-gray-200'
                            }`}>
                              {isCompletato ? (
                                <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                              ) : isAccessibile ? (
                                <span className="text-blue-600 font-bold">{modulo.ordine}</span>
                              ) : (
                                <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                                </svg>
                              )}
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <h4 className="font-semibold">{modulo.titolo}</h4>
                                {isCompletato && (
                                  <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded-full font-medium">
                                    Completato
                                  </span>
                                )}
                                {!isAccessibile && (
                                  <span className="px-2 py-0.5 bg-gray-200 text-gray-600 text-xs rounded-full font-medium">
                                    Bloccato
                                  </span>
                                )}
                              </div>
                              {modulo.descrizione && <p className="text-sm text-gray-600">{modulo.descrizione}</p>}
                              <div className="flex gap-2 mt-2 text-xs text-gray-500">
                                {modulo.video_url && <span className="flex items-center gap-1">📹 Video</span>}
                                {modulo.testo_markdown && <span className="flex items-center gap-1">📝 Testo</span>}
                                {modulo.pdf_url && <span className="flex items-center gap-1">📄 PDF</span>}
                                {modulo.has_test && <span className="flex items-center gap-1">✅ Test</span>}
                              </div>
                            </div>
                            {isAccessibile && (
                              <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                              </svg>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              </CardContent>
            </Card>

            {/* Test Finale Card - Shown after all modules are completed */}
            {(() => {
              const progresso = progressiUtente.find(p => p.corso_id === corsoCorrente.id);
              const moduliCompletatiCount = progresso?.moduli_completati?.length || 0;
              const totaleModuli = moduliCountPerCorso[corsoCorrente.id] || 0;
              const tuttiModuliCompletati = moduliCompletatiCount === totaleModuli && totaleModuli > 0;
              
              // Verifica se test finale esiste e se è stato completato
              const testFinale = corsoCorrente.test_finale_id;
              let testFinaleCompletato = false;
              if (testFinale && progresso?.test_completati) {
                if (typeof progresso.test_completati === 'object' && !Array.isArray(progresso.test_completati)) {
                  // Struttura dict: {test_id: {superato, punteggio, ...}}
                  const testInfo = progresso.test_completati[testFinale];
                  testFinaleCompletato = testInfo?.superato === true;
                } else if (Array.isArray(progresso.test_completati)) {
                  // Struttura array (dati vecchi)
                  testFinaleCompletato = progresso.test_completati.some(t => 
                    (typeof t === 'object' && t.test_id === testFinale && t.superato === true) ||
                    (typeof t === 'string' && t === testFinale)
                  );
                }
              }
              
              // Mostra la card se c'è test finale e i moduli sono completati
              if (!testFinale || !tuttiModuliCompletati) return null;
              
              return (
                <Card className="shadow-sm border border-amber-200 bg-amber-50/30">
                  <CardHeader>
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-full bg-yellow-100 flex items-center justify-center">
                        <svg className="w-7 h-7 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
                        </svg>
                      </div>
                      <div>
                        <CardTitle className="text-yellow-900">🏆 Test Finale</CardTitle>
                        <CardDescription className="text-yellow-700">
                          {testFinaleCompletato 
                            ? "Test completato con successo!" 
                            : "Completa il test per ottenere l'attestato"}
                        </CardDescription>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {testFinaleCompletato ? (
                      <div className="space-y-3">
                        <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
                          <div className="flex items-center gap-3">
                            <svg className="w-8 h-8 text-green-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <div>
                              <p className="font-semibold text-green-900">Test Finale Superato!</p>
                              <p className="text-sm text-green-700">
                                Hai completato con successo il corso. Il tuo attestato è pronto.
                              </p>
                            </div>
                          </div>
                        </div>
                        <Button 
                          disabled 
                          className="w-full bg-green-600 hover:bg-green-600 cursor-not-allowed opacity-100"
                        >
                          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                          ✓ Completato
                        </Button>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                          <p className="text-sm text-blue-900">
                            <strong>📋 Pronto per il test finale?</strong>
                          </p>
                          <p className="text-sm text-blue-700 mt-2">
                            Hai completato tutti i {totaleModuli} moduli del corso. 
                            Ora puoi sostenere il test finale per ottenere il tuo attestato di completamento.
                          </p>
                        </div>
                        <Button 
                          onClick={async () => {
                            try {
                              // Carica il test finale
                              const testResponse = await axios.get(`${API}/formazione/test/${testFinale}`);
                              setTestModulo(testResponse.data);
                              
                              // Carica le domande
                              const domandeResponse = await axios.get(`${API}/formazione/test/${testFinale}/domande`);
                              setDomandeTest(domandeResponse.data);
                              setRisposteUtente({});
                              setTestInCorso(true);
                              setShowTestDialog(true);
                            } catch (error) {
                              toast.error(error.response?.data?.detail || 'Errore nel caricamento del test');
                            }
                          }}
                          className="w-full bg-yellow-600 hover:bg-yellow-700 text-white"
                        >
                          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          Inizia Test Finale
                        </Button>
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })()}
            </div>
          ) : (
            // Vista Dettaglio Modulo
            <Card className="shadow-lg border-0">
              <CardHeader>
                <Button onClick={backToModuliList} variant="ghost" size="sm" className="mb-2">
                  ← Torna ai Moduli
                </Button>
                <CardTitle>{moduloCorrente.titolo}</CardTitle>
                {moduloCorrente.descrizione && <CardDescription>{moduloCorrente.descrizione}</CardDescription>}
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Video */}
                {moduloCorrente.video_url && (
                  <div>
                    <h3 className="font-semibold mb-3 flex items-center gap-2">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      Video
                    </h3>
                    <div className="aspect-video bg-gray-100 rounded-lg overflow-hidden">
                      {moduloCorrente.video_type === 'upload' ? (
                        <video 
                          controls 
                          preload="metadata"
                          className="w-full h-full"
                          controlsList="nodownload"
                          onError={(e) => {
                            toast.error('Errore nel caricamento del video');
                          }}
                          onLoadedMetadata={() => {
                          }}
                        >
                          <source src={getAuthenticatedFileUrl(moduloCorrente.video_url)} type="video/mp4" />
                          <source src={getAuthenticatedFileUrl(moduloCorrente.video_url)} type="video/webm" />
                          Il tuo browser non supporta la riproduzione video.
                        </video>
                      ) : moduloCorrente.video_type === 'gdrive' ? (
                        <iframe 
                          src={moduloCorrente.video_url.replace('/view', '/preview')}
                          className="w-full h-full"
                          allow="autoplay"
                          title={moduloCorrente.titolo}
                          loading="lazy"
                        />
                      ) : (
                        <iframe 
                          src={`https://www.youtube.com/embed/${new URL(moduloCorrente.video_url).searchParams.get('v')}`}
                          className="w-full h-full"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                          title={moduloCorrente.titolo}
                          loading="lazy"
                        />
                      )}
                    </div>
                  </div>
                )}

                {/* Testo Markdown */}
                {moduloCorrente.testo_markdown && (
                  <div>
                    <h3 className="font-semibold mb-3 flex items-center gap-2">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      Contenuto
                    </h3>
                    <div className="prose max-w-none bg-gray-50 p-4 rounded-lg">
                      <ReactMarkdown>{moduloCorrente.testo_markdown}</ReactMarkdown>
                    </div>
                  </div>
                )}

                {/* PDF */}
                {moduloCorrente.pdf_url && (
                  <div>
                    <h3 className="font-semibold mb-3 flex items-center gap-2">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                      </svg>
                      Documento PDF
                    </h3>
                    <a 
                      href={getAuthenticatedFileUrl(moduloCorrente.pdf_url)}
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 px-4 py-2 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition-colors"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      Scarica PDF
                    </a>
                  </div>
                )}

                {/* Test */}
                {testModulo && (() => {
                  const progresso = progressiUtente.find(p => p.corso_id === corsoCorrente.id);
                  // Supporta sia struttura dict che array (retrocompatibilità)
                  let testSuperato = false;
                  if (progresso?.test_completati) {
                    if (typeof progresso.test_completati === 'object' && !Array.isArray(progresso.test_completati)) {
                      // Struttura dict: {test_id: {superato, punteggio, ...}}
                      const testInfo = progresso.test_completati[testModulo.id];
                      testSuperato = testInfo?.superato === true;
                    } else if (Array.isArray(progresso.test_completati)) {
                      // Struttura array (dati vecchi)
                      testSuperato = progresso.test_completati.some(t => t.test_id === testModulo.id && t.superato === true);
                    }
                  }
                  
                  return (
                    <div className="border-t pt-6">
                      <h3 className="font-semibold mb-3 flex items-center gap-2">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        {testModulo.titolo}
                      </h3>
                      <p className="text-sm text-gray-600 mb-4">{testModulo.descrizione}</p>
                      <div className="bg-blue-50 p-4 rounded-lg mb-4">
                        <div className="grid grid-cols-2 gap-4 text-sm">
                          <div>
                            <span className="text-gray-600">Punteggio minimo:</span>
                            <span className="ml-2 font-semibold">{testModulo.punteggio_minimo}%</span>
                          </div>
                          <div>
                            <span className="text-gray-600">Tentativi disponibili:</span>
                            <span className="ml-2 font-semibold">{testModulo.tentativi_max}</span>
                          </div>
                        </div>
                      </div>
                      {testSuperato ? (
                        <Button 
                          disabled 
                          className="w-full bg-green-600 hover:bg-green-600 cursor-not-allowed opacity-100"
                        >
                          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                          ✓ Completato
                        </Button>
                      ) : (
                        <Button onClick={iniziaTest} className="w-full">
                          Inizia Test
                        </Button>
                      )}
                    </div>
                  );
                })()}

                {/* Pulsante Completa Modulo */}
                <div className="border-t pt-6 space-y-4">
                  {(() => {
                    const progresso = progressiUtente.find(p => p.corso_id === corsoCorrente.id);
                    const isCompletato = progresso?.moduli_completati?.includes(moduloCorrente.id);
                    const moduliCompletatiCount = progresso?.moduli_completati?.length || 0;
                    const totaleModuli = moduliCountPerCorso[corsoCorrente.id] || 0;
                    
                    // Verifica se questo è l'ultimo modulo e tutti gli altri sono completati
                    const isUltimoModulo = moduloCorrente.ordine === Math.max(...moduliCorso.map(m => m.ordine));
                    const tuttiAltriModuliCompletati = moduliCompletatiCount === totaleModuli - 1;
                    const tuttiModuliCompletati = moduliCompletatiCount === totaleModuli;
                    
                    // Verifica se test finale è stato completato
                    let testFinaleCompletato = false;
                    if (corsoCorrente.test_finale_id && progresso?.test_completati) {
                      if (typeof progresso.test_completati === 'object' && !Array.isArray(progresso.test_completati)) {
                        // Struttura dict: {test_id: {superato, punteggio, ...}}
                        const testInfo = progresso.test_completati[corsoCorrente.test_finale_id];
                        testFinaleCompletato = testInfo?.superato === true;
                      } else if (Array.isArray(progresso.test_completati)) {
                        // Struttura array (dati vecchi)
                        testFinaleCompletato = progresso.test_completati.some(t => 
                          (typeof t === 'object' && t.test_id === corsoCorrente.test_finale_id && t.superato === true) ||
                          (typeof t === 'string' && t === corsoCorrente.test_finale_id)
                        );
                      }
                    }
                    
                    // Verifica se esiste un certificato
                    const certificato = certificatiOperatore.find(c => c.corso_id === corsoCorrente.id);
                    
                    return (
                      <>
                        {/* Riepilogo Completamento Corso */}
                        {isUltimoModulo && (tuttiAltriModuliCompletati || tuttiModuliCompletati) && (
                          <Card className={`border-2 ${testFinaleCompletato ? 'border-green-200 bg-gradient-to-r from-green-50 to-emerald-50' : 'border-blue-200 bg-gradient-to-r from-blue-50 to-indigo-50'}`}>
                            <CardContent className="p-6">
                              <div className="flex items-start gap-4">
                                <div className="flex-shrink-0">
                                  <div className={`w-16 h-16 rounded-full ${testFinaleCompletato ? 'bg-green-100' : 'bg-blue-100'} flex items-center justify-center`}>
                                    {testFinaleCompletato ? (
                                      <svg className="w-10 h-10 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                      </svg>
                                    ) : tuttiModuliCompletati ? (
                                      <svg className="w-10 h-10 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                      </svg>
                                    ) : (
                                      <svg className="w-10 h-10 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                                      </svg>
                                    )}
                                  </div>
                                </div>
                                <div className="flex-1">
                                  <h3 className={`text-xl font-bold ${testFinaleCompletato ? 'text-green-900' : 'text-blue-900'} mb-2`}>
                                    {testFinaleCompletato ? '🎉 Corso Completato!' : tuttiModuliCompletati ? '🎉 Complimenti!' : '🚀 Ultimo Modulo!'}
                                  </h3>
                                  <p className={`${testFinaleCompletato ? 'text-green-800' : 'text-blue-800'} mb-3`}>
                                    {testFinaleCompletato 
                                      ? `Hai completato tutti i ${totaleModuli} moduli e superato il test finale!` 
                                      : tuttiModuliCompletati 
                                        ? `Hai completato tutti i ${totaleModuli} moduli del corso!` 
                                        : `Hai completato ${moduliCompletatiCount} su ${totaleModuli} moduli. Questo è l'ultimo!`
                                    }
                                  </p>
                                  
                                  {/* Progress Bar */}
                                  <div className="mb-4">
                                    <div className={`flex justify-between text-sm ${testFinaleCompletato ? 'text-green-700' : 'text-blue-700'} mb-1`}>
                                      <span>Progresso Totale</span>
                                      <span className="font-semibold">{testFinaleCompletato ? '100%' : Math.round((moduliCompletatiCount / totaleModuli) * 100) + '%'}</span>
                                    </div>
                                    <div className={`w-full ${testFinaleCompletato ? 'bg-green-200' : 'bg-blue-200'} rounded-full h-3`}>
                                      <div 
                                        className={`${testFinaleCompletato ? 'bg-green-600' : 'bg-blue-600'} h-3 rounded-full transition-all duration-500`}
                                        style={{ width: testFinaleCompletato ? '100%' : `${(moduliCompletatiCount / totaleModuli) * 100}%` }}
                                      />
                                    </div>
                                  </div>
                                  
                                  {testFinaleCompletato ? (
                                    <div className="bg-white/60 rounded-lg p-4 border border-green-300">
                                      <p className="text-sm text-green-900 font-medium mb-2">✅ Test Finale Superato!</p>
                                      <p className="text-sm text-green-800 mb-3">
                                        Hai completato con successo il corso. Il tuo attestato è disponibile.
                                      </p>
                                      {certificato && (
                                        <Button 
                                          onClick={() => downloadPDFAttestato(certificato.id)}
                                          className="w-full bg-green-600 hover:bg-green-700 text-white"
                                        >
                                          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                          </svg>
                                          Scarica Attestato
                                        </Button>
                                      )}
                                    </div>
                                  ) : tuttiModuliCompletati ? (
                                    <div className="bg-white/60 rounded-lg p-4 border border-blue-300">
                                      <p className="text-sm text-blue-900 font-medium mb-2">📝 Prossimo Step:</p>
                                      <p className="text-sm text-blue-800">
                                        Torna alla lista dei moduli e completa il <strong>Test Finale</strong> per terminare il corso e ottenere l'attestato di completamento.
                                      </p>
                                    </div>
                                  ) : (
                                    <div className="bg-white/60 rounded-lg p-4 border border-blue-300">
                                      <p className="text-sm text-blue-800">
                                        Completa questo modulo per procedere con il test finale!
                                      </p>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </CardContent>
                          </Card>
                        )}
                        
                        {/* Pulsante Completa/Completato */}
                        {isCompletato ? (
                          <div className="flex items-center justify-center gap-2 p-4 bg-green-50 border border-green-200 rounded-lg text-green-700">
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <span className="font-semibold">Modulo Completato</span>
                          </div>
                        ) : (
                          <Button 
                            onClick={() => completaModulo(moduloCorrente.id)}
                            className="w-full bg-green-600 hover:bg-green-700"
                          >
                            Segna come Completato
                          </Button>
                        )}
                      </>
                    );
                  })()}
                </div>
              </CardContent>
            </Card>
          )
        )}

        {/* Turni Tab Content */}
        {activeTab === 'turni' && (
          <GestioneTurni key="gestione-turni-operatore" user={user} />
        )}
        
        {/* Planning Orario Tab Content */}
        {activeTab === 'planning' && user.has_planning_access && (
          <PlanningOrario key="planning-orario-operatore" user={user} />
        )}

        {/* Preventivi Tab Content - Solo con accesso */}
        {activeTab === 'preventivi' && (user.has_preventivi_access === true || user.role === 'super_amministratore') && (
          <Preventivi user={user} />
        )}

        {/* Informative Tab Content - Comunicazioni e Privacy */}
        {activeTab === 'comunicazioni' && (
          <ComunicazioniOperatore user={user} />
        )}

        {activeTab === 'procedure' && (
          <Procedure user={user} />
        )}

        {activeTab === 'privacy' && (
          <PrivacyPolicyView />
        )}
          </div>
        </main>
      </div>

      {/* Dialog Cambio Password */}
      <Dialog open={showChangePasswordDialog} onOpenChange={setShowChangePasswordDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Modifica Password</DialogTitle>
            <DialogDescription>
              Inserisci la password attuale e la nuova password
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleChangePassword} className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label>Password Attuale *</Label>
              <Input
                type="password"
                value={passwordData.currentPassword}
                onChange={(e) => setPasswordData({...passwordData, currentPassword: e.target.value})}
                placeholder="Inserisci la password attuale"
                required
              />
            </div>
            
            <div className="space-y-2">
              <Label>Nuova Password *</Label>
              <Input
                type="password"
                value={passwordData.newPassword}
                onChange={(e) => setPasswordData({...passwordData, newPassword: e.target.value})}
                placeholder="Inserisci la nuova password"
                required
              />
              <p className="text-xs text-gray-500">
                Minimo 8 caratteri, deve contenere almeno un numero e un carattere speciale
              </p>
            </div>
            
            <div className="space-y-2">
              <Label>Conferma Nuova Password *</Label>
              <Input
                type="password"
                value={passwordData.confirmPassword}
                onChange={(e) => setPasswordData({...passwordData, confirmPassword: e.target.value})}
                placeholder="Conferma la nuova password"
                required
              />
            </div>

            {/* Indicatore forza password */}
            {passwordData.newPassword && (
              <div className="space-y-1">
                <div className="flex gap-1">
                  <div className={`h-1 flex-1 rounded ${passwordData.newPassword.length >= 8 ? 'bg-green-500' : 'bg-gray-300'}`}></div>
                  <div className={`h-1 flex-1 rounded ${/\d/.test(passwordData.newPassword) ? 'bg-green-500' : 'bg-gray-300'}`}></div>
                  <div className={`h-1 flex-1 rounded ${/[!@#$%^&*(),.?":{}|<>]/.test(passwordData.newPassword) ? 'bg-green-500' : 'bg-gray-300'}`}></div>
                </div>
                <div className="text-xs text-gray-600 space-y-0.5">
                  <p className={passwordData.newPassword.length >= 8 ? 'text-green-600' : ''}>
                    {passwordData.newPassword.length >= 8 ? '✓' : '○'} Almeno 8 caratteri
                  </p>
                  <p className={/\d/.test(passwordData.newPassword) ? 'text-green-600' : ''}>
                    {/\d/.test(passwordData.newPassword) ? '✓' : '○'} Contiene un numero
                  </p>
                  <p className={/[!@#$%^&*(),.?":{}|<>]/.test(passwordData.newPassword) ? 'text-green-600' : ''}>
                    {/[!@#$%^&*(),.?":{}|<>]/.test(passwordData.newPassword) ? '✓' : '○'} Contiene un carattere speciale
                  </p>
                  {passwordData.confirmPassword && (
                    <p className={passwordData.newPassword === passwordData.confirmPassword ? 'text-green-600' : 'text-red-600'}>
                      {passwordData.newPassword === passwordData.confirmPassword ? '✓' : '✗'} Le password coincidono
                    </p>
                  )}
                </div>
              </div>
            )}

            <div className="flex gap-3">
              <Button 
                type="submit" 
                className="flex-1"
                disabled={
                  !passwordData.currentPassword || 
                  !passwordData.newPassword || 
                  !passwordData.confirmPassword ||
                  passwordData.newPassword !== passwordData.confirmPassword ||
                  passwordData.newPassword.length < 8 ||
                  !/\d/.test(passwordData.newPassword) ||
                  !/[!@#$%^&*(),.?":{}|<>]/.test(passwordData.newPassword)
                }
              >
                Modifica Password
              </Button>
              <Button 
                type="button"
                variant="outline" 
                onClick={() => {
                  setShowChangePasswordDialog(false);
                  setPasswordData({
                    currentPassword: '',
                    newPassword: '',
                    confirmPassword: ''
                  });
                }}
              >
                Annulla
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Dialog Info Convenzione */}
      <Dialog open={showConvenzioneInfo} onOpenChange={setShowConvenzioneInfo}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          {selectedConvenzione && (
            <>
              <DialogHeader>
                <DialogTitle className="text-2xl">{selectedConvenzione.titolo}</DialogTitle>
                <DialogDescription>
                  <span className="inline-block px-3 py-1 bg-cyan-600 text-white text-xs font-semibold rounded-full capitalize">
                    {selectedConvenzione.categoria}
                  </span>
                </DialogDescription>
              </DialogHeader>
              
              {/* Immagine Banner */}
              {selectedConvenzione.immagine_url && (
                <div className="w-full h-48 rounded-lg overflow-hidden mb-4">
                  <img 
                    src={getAuthenticatedFileUrl(selectedConvenzione.immagine_url)} 
                    alt={selectedConvenzione.titolo}
                    className="w-full h-full object-cover"
                  />
                </div>
              )}
              
              {/* Offerta */}
              <div className="flex items-center gap-2 mb-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span className="text-green-700 font-bold text-xl">{selectedConvenzione.offerta || selectedConvenzione.sconto}</span>
              </div>
              
              {/* Descrizione */}
              <div className="space-y-3">
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">Descrizione</h4>
                  <p className="text-gray-700">{selectedConvenzione.descrizione}</p>
                </div>
                
                {/* Istruzioni con Markdown */}
                {selectedConvenzione.istruzioni && (
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">Come accedere alla convenzione</h4>
                    <div className="prose prose-sm max-w-none bg-gray-50 p-4 rounded-lg border border-gray-200">
                      <ReactMarkdown
                        components={{
                          h1: ({node, ...props}) => <h1 className="text-xl font-bold mb-2" {...props} />,
                          h2: ({node, ...props}) => <h2 className="text-lg font-bold mb-2" {...props} />,
                          h3: ({node, ...props}) => <h3 className="text-base font-bold mb-1" {...props} />,
                          p: ({node, ...props}) => <p className="mb-2 text-gray-700" {...props} />,
                          ul: ({node, ...props}) => <ul className="list-disc list-inside mb-2 space-y-1" {...props} />,
                          ol: ({node, ...props}) => <ol className="list-decimal list-inside mb-2 space-y-1" {...props} />,
                          li: ({node, ...props}) => <li className="text-gray-700" {...props} />,
                          strong: ({node, ...props}) => <strong className="font-bold text-gray-900" {...props} />,
                          em: ({node, ...props}) => <em className="italic" {...props} />,
                          a: ({node, ...props}) => <a className="text-cyan-600 hover:text-cyan-700 underline" {...props} />,
                          code: ({node, ...props}) => <code className="bg-gray-200 px-1 py-0.5 rounded text-sm font-mono" {...props} />
                        }}
                      >
                        {selectedConvenzione.istruzioni}
                      </ReactMarkdown>
                    </div>
                  </div>
                )}
              </div>
              
              {/* Link */}
              {selectedConvenzione.link && (
                <div className="mt-6">
                  <a 
                    href={selectedConvenzione.link} 
                    target="_blank" 
                    rel="noopener noreferrer"
                  >
                    <Button className="w-full bg-cyan-600 hover:bg-cyan-700 h-12 text-lg">
                      Vai all'offerta →
                    </Button>
                  </a>
                </div>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Dialog Test */}
      <Dialog open={showTestDialog} onOpenChange={setShowTestDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{testModulo?.titolo}</DialogTitle>
            <DialogDescription>
              Rispondi a tutte le domande e invia il test. Punteggio minimo: {testModulo?.punteggio_minimo}%
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-6 mt-4">
            {domandeTest.map((domanda, idx) => (
              <div key={domanda.id} className="border-b pb-4">
                <div className="flex gap-2 mb-3">
                  <span className="flex-shrink-0 w-6 h-6 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-sm font-semibold">
                    {idx + 1}
                  </span>
                  <div className="flex-1">
                    <p className="font-medium text-gray-900">{domanda.testo}</p>
                    <span className="text-xs text-gray-500">{domanda.punti} {domanda.punti === 1 ? 'punto' : 'punti'}</span>
                  </div>
                </div>
                
                {domanda.tipo === 'multipla' && (
                  <div className="space-y-2 ml-8">
                    {domanda.opzioni.map((opzione, optIdx) => (
                      <label key={optIdx} className="flex items-center gap-2 cursor-pointer p-2 hover:bg-gray-50 rounded">
                        <input
                          type="radio"
                          name={`domanda-${domanda.id}`}
                          value={optIdx.toString()}
                          checked={risposteUtente[domanda.id] === optIdx.toString()}
                          onChange={(e) => handleRisposta(domanda.id, e.target.value)}
                          className="w-4 h-4"
                        />
                        <span className="text-sm">{String.fromCharCode(65 + optIdx)}. {opzione}</span>
                      </label>
                    ))}
                  </div>
                )}
                
                {domanda.tipo === 'vero_falso' && (
                  <div className="space-y-2 ml-8">
                    <label className="flex items-center gap-2 cursor-pointer p-2 hover:bg-gray-50 rounded">
                      <input
                        type="radio"
                        name={`domanda-${domanda.id}`}
                        value="vero"
                        checked={risposteUtente[domanda.id] === 'vero'}
                        onChange={(e) => handleRisposta(domanda.id, e.target.value)}
                        className="w-4 h-4"
                      />
                      <span className="text-sm">Vero</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer p-2 hover:bg-gray-50 rounded">
                      <input
                        type="radio"
                        name={`domanda-${domanda.id}`}
                        value="falso"
                        checked={risposteUtente[domanda.id] === 'falso'}
                        onChange={(e) => handleRisposta(domanda.id, e.target.value)}
                        className="w-4 h-4"
                      />
                      <span className="text-sm">Falso</span>
                    </label>
                  </div>
                )}
                
                {domanda.tipo === 'testuale' && (
                  <div className="ml-8">
                    <textarea
                      className="w-full p-3 border rounded-md min-h-[100px]"
                      placeholder="Scrivi la tua risposta..."
                      value={risposteUtente[domanda.id] || ''}
                      onChange={(e) => handleRisposta(domanda.id, e.target.value)}
                    />
                  </div>
                )}
              </div>
            ))}
          </div>
          <div className="flex gap-3 mt-6">
            <Button 
              onClick={inviaTest}
              className="flex-1 bg-green-600 hover:bg-green-700"
              disabled={Object.keys(risposteUtente).length !== domandeTest.length}
            >
              Invia Test
            </Button>
            <Button 
              variant="outline"
              onClick={() => {
                setShowTestDialog(false);
                setTestInCorso(false);
              }}
            >
              Annulla
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog Questionario Soddisfacimento */}
      <Dialog open={showQuestionarioDialog} onOpenChange={setShowQuestionarioDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Questionario di Gradimento</DialogTitle>
            <DialogDescription>
              Aiutaci a migliorare! La tua opinione è importante per noi.
            </DialogDescription>
          </DialogHeader>
          {questionario && (
            <div className="space-y-6 mt-4">
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <p className="text-sm text-blue-800">
                  Valuta ogni domanda da <strong>{questionario.scala_min}</strong> ({questionario.etichetta_min}) a <strong>{questionario.scala_max}</strong> ({questionario.etichetta_max})
                </p>
              </div>

              {questionario.domande.map((domanda, idx) => (
                <div key={idx} className="space-y-3 pb-4 border-b">
                  <p className="font-medium text-gray-900">{domanda}</p>
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-xs text-gray-500">{questionario.etichetta_min}</span>
                    <div className="flex gap-2">
                      {Array.from({ length: questionario.scala_max - questionario.scala_min + 1 }, (_, i) => {
                        const valore = questionario.scala_min + i;
                        return (
                          <button
                            key={valore}
                            onClick={() => setRisposteQuestionario({
                              ...risposteQuestionario,
                              [idx]: valore
                            })}
                            className={`w-12 h-12 rounded-full border-2 font-semibold transition-all ${
                              risposteQuestionario[idx] === valore
                                ? 'bg-blue-600 text-white border-blue-600 scale-110'
                                : 'bg-white text-gray-700 border-gray-300 hover:border-blue-400 hover:bg-blue-50'
                            }`}
                          >
                            {valore}
                          </button>
                        );
                      })}
                    </div>
                    <span className="text-xs text-gray-500">{questionario.etichetta_max}</span>
                  </div>
                </div>
              ))}

              {questionario.abilita_commenti && (
                <div className="space-y-2 pt-2">
                  <label className="font-medium text-gray-900">Commenti o suggerimenti (opzionale)</label>
                  <textarea
                    className="w-full p-3 border rounded-md min-h-[100px]"
                    value={commentiQuestionario}
                    onChange={(e) => setCommentiQuestionario(e.target.value)}
                    placeholder="Condividi la tua esperienza o suggerimenti per migliorare il corso..."
                  />
                </div>
              )}

              <div className="flex gap-3 mt-6 pt-4 border-t">
                <Button 
                  onClick={inviaQuestionario}
                  className="flex-1 bg-green-600 hover:bg-green-700"
                  disabled={Object.keys(risposteQuestionario).length !== questionario.domande.length}
                >
                  {Object.keys(risposteQuestionario).length !== questionario.domande.length 
                    ? `Rispondi a tutte le domande (${Object.keys(risposteQuestionario).length}/${questionario.domande.length})`
                    : 'Invia Questionario'
                  }
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Dialog Completamento Corso */}
      <Dialog open={showCompletamentoDialog} onOpenChange={setShowCompletamentoDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-green-100">
              <svg className="h-10 w-10 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <DialogTitle className="text-center text-2xl">🎉 Complimenti!</DialogTitle>
            <DialogDescription className="text-center text-base">
              {certificatoCompletato && (
                <>
                  Hai completato con successo il corso<br/>
                  <span className="font-semibold text-gray-900">{certificatoCompletato.corso_titolo}</span>
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
              <p className="text-sm font-medium text-green-900 mb-2">
                ✅ Il tuo attestato di completamento è pronto!
              </p>
              <p className="text-xs text-green-700">
                Puoi scaricarlo ora o trovarlo nella sezione "I Miei Attestati"
              </p>
            </div>
            <div className="flex flex-col gap-2">
              <Button 
                onClick={() => {
                  if (certificatoCompletato) {
                    downloadPDFAttestato(certificatoCompletato.certificato_id, `${user.first_name}_${user.last_name}`);
                  }
                }}
                className="w-full bg-green-600 hover:bg-green-700"
              >
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                Scarica Attestato
              </Button>
              <Button 
                variant="outline"
                onClick={() => {
                  setShowCompletamentoDialog(false);
                  backToCorsiList();
                }}
                className="w-full"
              >
                Torna ai Miei Corsi
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>


      {/* Dialog Dettaglio Fattura Operatore */}
      <Dialog open={showInvoiceDetailDialog} onOpenChange={setShowInvoiceDetailDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Dettaglio Conteggio Fattura</DialogTitle>
            <DialogDescription>
              Riepilogo del conteggio mensile
            </DialogDescription>
          </DialogHeader>
          {selectedInvoice && (
            <div className="space-y-4 mt-4">
              {/* Header */}
              <div className="bg-gradient-to-r from-cyan-50 to-blue-50 p-4 rounded-lg border border-cyan-200">
                <h3 className="font-bold text-lg mb-1">{months[selectedInvoice.month - 1]} {selectedInvoice.year}</h3>
                <p className="text-sm text-gray-600">Periodo di riferimento</p>
                {selectedInvoice.asl_riferimento && (
                  <p className="text-sm font-medium text-cyan-700 mt-1">ASL: {selectedInvoice.asl_riferimento}</p>
                )}
              </div>

              {/* Note (se presenti) */}
              {selectedInvoice.note && (
                <div className="border rounded-lg p-4 bg-yellow-50 border-yellow-200">
                  <h4 className="font-semibold mb-2">Note</h4>
                  <p className="text-sm">{selectedInvoice.note}</p>
                </div>
              )}

              {/* Scadenza fattura */}
              <div className="border rounded-lg p-4 bg-orange-50 border-orange-200">
                <p className="text-sm font-medium text-orange-800">
                  ⏰ Le chiediamo gentilmente di emettere la fattura entro 10 giorni.
                </p>
              </div>

              {/* Destinatario */}
              <div className="border rounded-lg p-4 bg-gray-50">
                <h4 className="font-semibold mb-2">Destinatario</h4>
                <div className="text-sm space-y-1">
                  <p className="font-medium">Cooperativa Sociale Croce Gialla soc.coop.</p>
                  <p>Contrada Mozzoni 13</p>
                  <p>66030 Treglio CH</p>
                  <p>P.I. 02501790691</p>
                  <p>SDI: M5UXCR1</p>
                </div>
              </div>

              {/* Dettaglio Accessi */}
              <div>
                <h4 className="font-semibold mb-3">Dettaglio Accessi</h4>
                <div className="space-y-2">
                  {selectedInvoice.accessi_data.map((accesso, idx) => (
                    <div key={idx} className="flex justify-between items-center p-3 bg-cyan-50 rounded-lg border border-cyan-200">
                      <div>
                        <p className="font-medium">{accesso.tipo_accesso}</p>
                        <p className="text-sm text-gray-600">{accesso.numero_accessi} accessi × €{accesso.costo_unitario.toFixed(2)}</p>
                      </div>
                      <p className="font-bold text-cyan-700">€{accesso.totale.toFixed(2)}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Calcolo Totale */}
              <div className="border rounded-lg p-4 bg-gradient-to-br from-green-50 to-cyan-50">
                <h4 className="font-semibold mb-3">Riepilogo Importi</h4>
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span>Totale Accessi:</span>
                    <span className="font-medium">€{selectedInvoice.totale_accessi.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span>Reperibilità:</span>
                    <span className="font-medium">€{selectedInvoice.importo_reperibilita.toFixed(2)}</span>
                  </div>
                  {selectedInvoice.voci_libere && selectedInvoice.voci_libere.length > 0 && (
                    <>
                      {selectedInvoice.voci_libere.map((voce, idx) => (
                        <div key={idx} className="flex justify-between text-sm text-blue-600">
                          <span>{voce.descrizione}:</span>
                          <span className="font-medium">€{voce.importo.toFixed(2)}</span>
                        </div>
                      ))}
                    </>
                  )}
                  <div className="flex justify-between border-t pt-2 font-semibold">
                    <span>Totale Lordo:</span>
                    <span>€{selectedInvoice.totale_lordo.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-sm text-red-600">
                    <span>Costo Auto:</span>
                    <span className="font-medium">€{selectedInvoice.costo_auto.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between border-t pt-2">
                    <span>Imponibile:</span>
                    <span className="font-medium">€{selectedInvoice.imponibile.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span>Cassa Previdenziale ({selectedInvoice.percentuale_cassa}%):</span>
                    <span className="font-medium">€{selectedInvoice.importo_cassa_previdenziale.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between border-t-2 border-green-600 pt-2 text-lg font-bold text-green-600">
                    <span>TOTALE NETTO:</span>
                    <span>€{selectedInvoice.totale_netto.toFixed(2)}</span>
                  </div>
                </div>
              </div>

              <div className="flex justify-between gap-3">
                {selectedInvoice.status === 'pending' && (
                  <Button 
                    variant="outline"
                    className="gap-1 text-orange-700 border-orange-300 hover:bg-orange-50"
                    onClick={() => {
                      setShowRequestVerificationDialog(true);
                      setShowInvoiceDetailDialog(false);
                    }}
                    disabled={selectedInvoice.richiesta_verifica}
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                    {selectedInvoice.richiesta_verifica ? 'Verifica Richiesta' : 'Richiedi Verifica'}
                  </Button>
                )}
                <Button 
                  variant="outline" 
                  onClick={() => setShowInvoiceDetailDialog(false)}
                  className={selectedInvoice.status === 'pending' ? '' : 'ml-auto'}
                >
                  Chiudi
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Dialog Upload Estremi Fattura */}
      <Dialog open={showUploadInvoiceDialog} onOpenChange={setShowUploadInvoiceDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Carica Estremi Fattura</DialogTitle>
            <DialogDescription>
              Inserisci i dati della tua fattura emessa
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleUploadInvoice} className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label>Data Fattura *</Label>
              <Input
                type="date"
                value={invoiceUploadData.data_fattura}
                onChange={(e) => setInvoiceUploadData({...invoiceUploadData, data_fattura: e.target.value})}
                required
              />
            </div>
            
            <div className="space-y-2">
              <Label>Numero Fattura *</Label>
              <Input
                type="text"
                value={invoiceUploadData.numero_fattura}
                onChange={(e) => setInvoiceUploadData({...invoiceUploadData, numero_fattura: e.target.value})}
                placeholder="Es: 2024/001"
                required
              />
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <p className="text-sm text-blue-800">
                <span className="font-semibold">Nota:</span> Assicurati che i dati della fattura corrispondano al conteggio visualizzato.
              </p>
            </div>

            <div className="flex gap-3">
              <Button type="submit" className="flex-1">
                Salva Estremi
              </Button>
              <Button 
                type="button"
                variant="outline" 
                onClick={() => setShowUploadInvoiceDialog(false)}
              >
                Annulla
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Dialog Richiesta Verifica */}
      <Dialog open={showRequestVerificationDialog} onOpenChange={setShowRequestVerificationDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Richiedi Verifica Fattura</DialogTitle>
            <DialogDescription>
              Descrivi il problema o la richiesta di modifica
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleRequestVerification} className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label>Nota per il Super Admin *</Label>
              <Textarea
                value={verificationNote}
                onChange={(e) => setVerificationNote(e.target.value)}
                placeholder="Es: Ho notato un errore nel numero di accessi, dovrebbero essere 25 invece di 20..."
                rows={5}
                required
              />
            </div>

            <div className="bg-orange-50 border border-orange-200 rounded-lg p-3">
              <p className="text-sm text-orange-800">
                Il Super Admin riceverà la tua richiesta e verificherà i dati della fattura. Riceverai una notifica quando la fattura sarà aggiornata.
              </p>
            </div>

            <div className="flex gap-3">
              <Button type="submit" className="flex-1">
                Invia Richiesta
              </Button>
              <Button 
                type="button"
                variant="outline" 
                onClick={() => {
                  setShowRequestVerificationDialog(false);
                  setVerificationNote('');
                }}
              >
                Annulla
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
      {/* Footer */}
      <div className="bg-white border-t border-gray-200 mt-8 sm:mt-12 md:mt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 sm:py-6">
          <div className="flex flex-col sm:flex-row justify-center items-center gap-2 sm:gap-4">
            <p className="text-center text-gray-600 text-xs sm:text-sm">
              © 2025 Croce Gialla Home - Cure Domiciliari d&apos;Abruzzo. Tutti i diritti riservati.
            </p>
            <span className="hidden sm:inline text-gray-400">&bull;</span>
            <button
              onClick={() => setActiveTab('privacy')}
              className="text-xs sm:text-sm text-cyan-600 hover:text-cyan-700 hover:underline"
            >
              Privacy & GDPR
            </button>
          </div>
        </div>
      </div>

    </div>
  );
};


// Admin Dashboard - PARTE 1
const AdminDashboard = ({ user, onLogout, getAuthenticatedFileUrl }) => {
  const [activeTab, setActiveTab] = useState('reports'); // Tab navigation
  const [reports, setReports] = useState([]);
  const [users, setUsers] = useState([]);
  const [qualificheMaster, setQualificheMaster] = useState([]);
  const [contrattiTipo, setContrattiTipo] = useState([]);
  const [showOrganigramma, setShowOrganigramma] = useState(false);
  const [showHRImport, setShowHRImport] = useState(false);
  const [pendingUsers, setPendingUsers] = useState([]);
  const [settings, setSettings] = useState({ tempo_spostamento: 10, costi: {} });
  const [showCreateUser, setShowCreateUser] = useState(false);
  const [showEditUser, setShowEditUser] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showSettings, setShowSettings] = useState(false);
  const [adminActiveTab, setAdminActiveTab] = useState('home');
  const [showFiguraDialog, setShowFiguraDialog] = useState(false);
  const [editingFigura, setEditingFigura] = useState(null);
  const [newFigura, setNewFigura] = useState({
    nome: '',
    tipologie_accessi: [{ nome: '', minuti: 0 }],
    nota_pacchetti: ''
  });
  const [newUser, setNewUser] = useState({
    username: '',
    password: '',
    first_name: '',
    last_name: '',
    email: '',
    codice_fiscale: '',
    role: 'operatore',
    figura_professionale: 'Infermiere',
    asl_riferimento: 'Asl Pescara',
    tipo_contratto: 'dipendente',
    is_coordinatore: false,
    has_planning_access: false,
    planning_role: null,
    has_preventivi_access: false
  });
  const [editUser, setEditUser] = useState({
    id: '',
    username: '',
    password: '',
    first_name: '',
    last_name: '',
    email: '',
    codice_fiscale: '',
    role: 'operatore',
    figura_professionale: 'Infermiere',
    asl_riferimento: 'Asl Pescara',
    tipo_contratto: 'dipendente',
    is_coordinatore: false,
    has_planning_access: false,
    planning_role: null,
    has_preventivi_access: false
  });
  const [userSearch, setUserSearch] = useState('');
  const [userFilterRole, setUserFilterRole] = useState('');
  const [userFilterASL, setUserFilterASL] = useState('');
  const [userFilterFigura, setUserFilterFigura] = useState('');
  const [selectedReports, setSelectedReports] = useState([]);
  
  // Stati per Login Monitoring
  const [loginDashboard, setLoginDashboard] = useState(null);
  const [loginHistory, setLoginHistory] = useState([]);
  const [activeSessions, setActiveSessions] = useState([]);
  const [failedLogins, setFailedLogins] = useState([]);
  const [loginFilters, setLoginFilters] = useState({
    user_id: null,
    success: null,
    from_date: null,
    to_date: null
  });
  
  // Stati per Dati Completi Operatore
  const [datiCompleti, setDatiCompleti] = useState(null);
  const [filtriDatiCompleti, setFiltriDatiCompleti] = useState({
    operatore_id: '',
    anno: new Date().getFullYear(),
    mesi: [new Date().getMonth() + 1], // Array di mesi
    tipologia: 'tutti'
  });
  const [loadingDatiCompleti, setLoadingDatiCompleti] = useState(false);
  const [filtriExportMassivo, setFiltriExportMassivo] = useState({
    asl_riferimento: '',
    anno: new Date().getFullYear(),
    mese_inizio: new Date().getMonth() + 1,
    mese_fine: new Date().getMonth() + 1
  });
  const [showExportMassivoDialog, setShowExportMassivoDialog] = useState(false);

  const [expandedReports, setExpandedReports] = useState([]);
  const [reportFilters, setReportFilters] = useState({
    year: new Date().getFullYear(),
    month: null,
    asl: null
  });
  const [showApproveDialog, setShowApproveDialog] = useState(false);
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [showDeleteReportDialog, setShowDeleteReportDialog] = useState(false);
  const [selectedReport, setSelectedReport] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');
  
  // Stati fatture
  const [invoices, setInvoices] = useState([]);
  const [showInvoiceDialog, setShowInvoiceDialog] = useState(false);
  const [showEditInvoiceDialog, setShowEditInvoiceDialog] = useState(false);
  const [showBillingConfigDialog, setShowBillingConfigDialog] = useState(false);
  const [showInvoiceDetailDialog, setShowInvoiceDetailDialog] = useState(false);
  const [showAdminUploadInvoiceDialog, setShowAdminUploadInvoiceDialog] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [selectedInvoiceUser, setSelectedInvoiceUser] = useState(null);
  const [adminInvoiceUploadData, setAdminInvoiceUploadData] = useState({
    data_fattura: '',
    numero_fattura: ''
  });
  const [invoiceFilters, setInvoiceFilters] = useState({
    year: new Date().getFullYear(),
    month: null,
    asl: null,
    figura: null
  });
  const [newInvoice, setNewInvoice] = useState({
    user_id: '',
    month: new Date().getMonth() + 1,
    year: new Date().getFullYear(),
    asl_riferimento: '',
    note: '',
    accessi_data: [],
    voci_libere: [],
    importo_reperibilita: 150,
    costo_auto: 0
  });

  // Stati convenzioni
  const [convenzioni, setConvenzioni] = useState([]);
  const [showConvenzioneDialog, setShowConvenzioneDialog] = useState(false);
  const [showDeleteConvenzioneDialog, setShowDeleteConvenzioneDialog] = useState(false);
  const [selectedConvenzione, setSelectedConvenzione] = useState(null);
  const [isEditingConvenzione, setIsEditingConvenzione] = useState(false);
  const [convenzioneForm, setConvenzioneForm] = useState({
    titolo: '',
    descrizione: '',
    categoria: 'moda',
    offerta: '',
    link: '',
    immagine_url: '',
    immagine_file: null,
    istruzioni: ''
  });
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadError, setUploadError] = useState('');
  const [billingConfig, setBillingConfig] = useState({
    costi_per_accesso: {},
    percentuale_cassa_previdenziale: 4.0,
    importo_reperibilita_default: 150.0,
    costo_auto_default: -100.0
  });

  // Stati Formazione
  const [corsi, setCorsi] = useState([]);
  const [selectedCorso, setSelectedCorso] = useState(null);
  const [caroselloIndex, setCaroselloIndex] = useState(0);
  const [showCorsoDialog, setShowCorsoDialog] = useState(false);
  const [showDeleteCorsoDialog, setShowDeleteCorsoDialog] = useState(false);
  const [isEditingCorso, setIsEditingCorso] = useState(false);
  const [corsoForm, setCorsoForm] = useState({
    titolo: '',
    descrizione: '',
    immagine_copertina_url: '',
    obbligatorio: false,
    data_scadenza: '',
    ruoli_assegnati: [],
    figure_professionali_assegnate: [],
    punteggio_minimo_attestato: 72,
    // Personalizzazione attestato
    logo_aggiuntivo_url: '',
    nome_firmatario: '',
    cognome_firmatario: '',
    ruolo_firmatario: '',
    firma_immagine_url: ''
  });
  const [moduli, setModuli] = useState([]);
  const [showModuloDialog, setShowModuloDialog] = useState(false);
  const [showDeleteModuloDialog, setShowDeleteModuloDialog] = useState(false);
  const [selectedModulo, setSelectedModulo] = useState(null);
  const [isEditingModulo, setIsEditingModulo] = useState(false);
  const [isSavingModulo, setIsSavingModulo] = useState(false);
  const [moduloForm, setModuloForm] = useState({
    titolo: '',
    descrizione: '',
    ordine: 1,
    video_url: '',
    video_type: 'gdrive',
    video_password: '',
    testo_markdown: '',
    pdf_url: ''
  });

  // Stati Test e Domande
  const [selectedTest, setSelectedTest] = useState(null);
  const [showTestDialog, setShowTestDialog] = useState(false);
  const [showDeleteTestDialog, setShowDeleteTestDialog] = useState(false);
  const [isEditingTest, setIsEditingTest] = useState(false);
  const [testForm, setTestForm] = useState({
    tipo: 'intermedio',
    titolo: '',
    descrizione: '',
    punteggio_minimo: 60,
    tentativi_max: 5,
    ordine_casuale: false
  });
  
  // Stati Test Finale
  const [showTestFinaleDialog, setShowTestFinaleDialog] = useState(false);
  const [testFinaleForm, setTestFinaleForm] = useState({
    titolo: '',
    descrizione: '',
    punteggio_minimo: 60,
    tentativi_max: 3,
    ordine_casuale: false
  });
  
  const [domande, setDomande] = useState([]);
  const [showDomandaDialog, setShowDomandaDialog] = useState(false);
  const [showDeleteDomandaDialog, setShowDeleteDomandaDialog] = useState(false);
  const [selectedDomanda, setSelectedDomanda] = useState(null);
  const [isEditingDomanda, setIsEditingDomanda] = useState(false);
  const [domandaForm, setDomandaForm] = useState({
    tipo: 'multipla',
    testo: '',
    ordine: 1,
    punti: 1,
    opzioni: ['', '', '', ''],
    risposta_corretta: '',
    risposte_corrette: [],
    richiede_correzione_manuale: false
  });

  // Stati Monitoraggio
  const [filtroCorsoMonitoraggio, setFiltroCorsoMonitoraggio] = useState('');
  const [filtroOperatoreMonitoraggio, setFiltroOperatoreMonitoraggio] = useState('');
  const [progressiOperatori, setProgressiOperatori] = useState([]);
  const [risposteDaCorreggere, setRisposteDaCorreggere] = useState([]);
  const [certificati, setCertificati] = useState([]);
  const [showCorrezioneDialog, setShowCorrezioneDialog] = useState(false);
  const [rispostaCorrente, setRispostaCorrente] = useState(null);
  const [correzioneForm, setCorrezioneForm] = useState({
    corretta: false,
    punti_ottenuti: 0,
    feedback: ''
  });
  const [showRiepilogoTestDialog, setShowRiepilogoTestDialog] = useState(false);
  const [riepilogoTest, setRiepilogoTest] = useState(null);
  
  // Stati Questionario Soddisfacimento
  const [showQuestionarioConfigDialog, setShowQuestionarioConfigDialog] = useState(false);
  const [questionarioCorso, setQuestionarioCorso] = useState(null);
  const [questionarioForm, setQuestionarioForm] = useState({
    domande: [
      "Come valuti la qualità complessiva del corso?",
      "I contenuti del corso erano chiari e ben strutturati?",
      "Il corso ha soddisfatto le tue aspettative?",
      "Consiglieresti questo corso ad altri colleghi?",
      "Come valuti i materiali didattici forniti?"
    ],
    scala_min: 1,
    scala_max: 5,
    etichetta_min: "Per niente",
    etichetta_max: "Moltissimo",
    abilita_commenti: true
  });
  const [risposteQuestionari, setRisposteQuestionari] = useState([]);
  const [showDettaglioRispostaDialog, setShowDettaglioRispostaDialog] = useState(false);
  const [rispostaSelezionata, setRispostaSelezionata] = useState(null);

  useEffect(() => {
    fetchData();
    if (user.role === 'super_amministratore') {
      fetchConvenzioni();
      fetchCorsi();
    }
    
    // Listener per aggiornamento lista utenti da altri componenti (es: Planning Orario)
    const handleRefreshUserList = () => {
      fetchData();
    };
    
    window.addEventListener('refreshUserList', handleRefreshUserList);
    
    return () => {
      window.removeEventListener('refreshUserList', handleRefreshUserList);
    };
  }, []);

  const fetchData = async () => {
    try {
      const requests = [
        axios.get(`${API}/users`),
        axios.get(`${API}/reports/all`),
        axios.get(`${API}/settings`)
      ];
      
      // Solo admin e super admin possono vedere utenti in attesa
      if (user.role === 'amministratore' || user.role === 'super_amministratore') {
        requests.push(axios.get(`${API}/users/pending-approval`));
      }
      
      const responses = await Promise.all(requests);
      
      setUsers(responses[0].data);
      setReports(responses[1].data);
      setSettings(responses[2].data);
      
      if (responses[3]) {
        setPendingUsers(responses[3].data);
      }

      // Fetch qualifiche master in background (non bloccante)
      try {
        const qres = await axios.get(`${API}/qualifiche`);
        setQualificheMaster(qres.data || []);
      } catch (qerr) {
        // Silent: qualifiche optional
      }

      // Fetch contratti tipo in background (non bloccante)
      try {
        const cres = await axios.get(`${API}/contratti-tipo`);
        setContrattiTipo(cres.data || []);
      } catch (cerr) {
        // Silent: contratti optional
      }
    } catch (error) {
      toast.error('Errore nel caricamento dei dati');
    }
  };
  
  // Fetch dati completi operatore (supporta multi-mese)
  const fetchDatiCompleti = async () => {
    if (!filtriDatiCompleti.operatore_id) {
      toast.error('Seleziona un operatore');
      return;
    }
    
    if (filtriDatiCompleti.mesi.length === 0) {
      toast.error('Seleziona almeno un mese');
      return;
    }
    
    setLoadingDatiCompleti(true);
    try {
      // Fetch dati per ogni mese selezionato
      const promises = filtriDatiCompleti.mesi.map(mese =>
        axios.get(
          `${API}/operatori/${filtriDatiCompleti.operatore_id}/dati-completi`,
          {
            params: {
              anno: filtriDatiCompleti.anno,
              mese: mese,
              tipologia: filtriDatiCompleti.tipologia
            }
          }
        )
      );
      
      const responses = await Promise.all(promises);
      
      // Aggrega i dati da tutti i mesi
      const datiAggregati = {
        operatore: responses[0].data.operatore,
        periodi: responses.map(r => r.data),
        mesi_selezionati: filtriDatiCompleti.mesi
      };
      
      setDatiCompleti(datiAggregati);
      toast.success(`Dati caricati per ${filtriDatiCompleti.mesi.length} mese/i`);
    } catch (error) {
      toast.error('Errore nel caricamento dei dati completi');
    } finally {
      setLoadingDatiCompleti(false);
    }
  };
  
  // Export PDF Dati Completi
  const exportDatiCompletiPDF = async () => {
    try {
      const response = await axios.get(
        `${API}/operatori/${filtriDatiCompleti.operatore_id}/dati-completi/export-pdf`,
        {
          params: {
            anno: filtriDatiCompleti.anno,
            mese: filtriDatiCompleti.mese,
            tipologia: filtriDatiCompleti.tipologia
          },
          responseType: 'blob'
        }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Dati_Completi_${filtriDatiCompleti.anno}_${filtriDatiCompleti.mese}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('PDF scaricato con successo');
    } catch (error) {
      toast.error('Errore nel download del PDF');
    }
  };
  
  // Export Excel Dati Completi
  const exportDatiCompletiExcel = async () => {
    try {
      const response = await axios.get(
        `${API}/operatori/${filtriDatiCompleti.operatore_id}/dati-completi/export-excel`,
        {
          params: {
            anno: filtriDatiCompleti.anno,
            mese: filtriDatiCompleti.mese,
            tipologia: filtriDatiCompleti.tipologia
          },
          responseType: 'blob'
        }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Dati_Completi_${filtriDatiCompleti.anno}_${filtriDatiCompleti.mese}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Excel scaricato con successo');
    } catch (error) {
      toast.error('Errore nel download dell\'Excel');
    }
  };
  
  // Export Massivo PDF
  const exportMassivoPDF = async () => {
    try {
      const response = await axios.get(
        `${API}/operatori/export-massivo/pdf`,
        {
          params: {
            asl_riferimento: filtriExportMassivo.asl_riferimento,
            anno: filtriExportMassivo.anno,
            mese_inizio: filtriExportMassivo.mese_inizio,
            mese_fine: filtriExportMassivo.mese_fine
          },
          responseType: 'blob'
        }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Export_Massivo.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      setShowExportMassivoDialog(false);
      toast.success('Export massivo PDF completato');
    } catch (error) {
      toast.error('Errore nell\'export massivo PDF');
    }
  };
  
  // Export Massivo Excel
  const exportMassivoExcel = async () => {
    try {
      const response = await axios.get(
        `${API}/operatori/export-massivo/excel`,
        {
          params: {
            asl_riferimento: filtriExportMassivo.asl_riferimento,
            anno: filtriExportMassivo.anno,
            mese_inizio: filtriExportMassivo.mese_inizio,
            mese_fine: filtriExportMassivo.mese_fine
          },
          responseType: 'blob'
        }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Export_Massivo.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      setShowExportMassivoDialog(false);
      toast.success('Export massivo Excel completato');
    } catch (error) {
      toast.error('Errore nell\'export massivo Excel');
    }
  };

  // Export singolo report PDF
  const exportSingleReportPDF = async (report) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API}/reports/${report.id}/export-pdf`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const monthName = months[report.month - 1];
      link.setAttribute('download', `Report_${report.full_name.replace(' ', '_')}_${monthName}_${report.year}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Report PDF scaricato');
    } catch (error) {
      toast.error('Errore nell\'export del report');
    }
  };

  // Export multiplo report PDF
  const exportMultipleReportsPDF = async () => {
    try {
      const token = localStorage.getItem('token');
      const filteredReports = getFilteredReports();
      const reportIds = filteredReports.map(r => r.id);
      
      if (reportIds.length === 0) {
        toast.error('Nessun report da esportare');
        return;
      }
      
      const response = await axios.post(
        `${API}/reports/export-multiple-pdf`,
        { report_ids: reportIds },
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/zip' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Report_Multipli_${new Date().toISOString().split('T')[0]}.zip`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`${reportIds.length} report esportati in ZIP`);
    } catch (error) {
      toast.error('Errore nell\'export multiplo');
    }
  };


  const getFilteredReports = () => {
    return reports.filter(report => {
      if (reportFilters.year && report.year !== reportFilters.year) return false;
      if (reportFilters.month && report.month !== reportFilters.month) return false;
      if (reportFilters.asl && report.asl_riferimento !== reportFilters.asl) return false;
      return true;
    });
  };


  const handleCreateUser = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/auth/register`, newUser);
      toast.success('Utente creato con successo!');
      setShowCreateUser(false);
      setNewUser({ 
        username: '', 
        password: '', 
        first_name: '', 
        last_name: '',
        email: '',
        codice_fiscale: '',
        role: 'operatore',
        figura_professionale: 'Infermiere',
        asl_riferimento: 'Asl Pescara',
        tipo_contratto: 'dipendente',
        has_planning_access: false,
        planning_role: null,
        privacy_consent: false,
        privacy_consent_date: null,
        privacy_consent_version: null
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nella creazione dell\'utente');
    }
  };

  const handleEditUserClick = (u) => {
    setSelectedUser(u);
    setEditUser({
      id: u.id,
      username: u.username,
      password: '',
      first_name: u.first_name || '',
      last_name: u.last_name || '',
      email: u.email || '',
      codice_fiscale: u.codice_fiscale || '',
      role: u.role,
      figura_professionale: u.figura_professionale || 'Infermiere',
      asl_riferimento: u.asl_riferimento || 'Asl Pescara',
      tipo_contratto: u.tipo_contratto || 'dipendente',
      is_coordinatore: u.is_coordinatore || false,
      has_planning_access: u.has_planning_access || false,
      planning_role: u.planning_role || null,
      has_preventivi_access: u.has_preventivi_access || false,
      has_procedure_editor_access: u.has_procedure_editor_access || false,
      has_accreditamenti_access: u.has_accreditamenti_access || false,
      qualifiche: u.qualifiche || [],
      manager_id: u.manager_id || '',
      contratto_tipo_id: u.contratto_tipo_id || '',
      contratto_ore_mensili_override: u.contratto_ore_mensili_override ?? '',
      contratto_ore_settimanali_override: u.contratto_ore_settimanali_override ?? '',
      contratto_ore_giornaliere_override: u.contratto_ore_giornaliere_override ?? '',
      contratto_giorni_settimana_override: u.contratto_giorni_settimana_override ?? '',
      contratto_ore_turni_override: u.contratto_ore_turni_override || {},
      contratto_note: u.contratto_note || '',
      coordinatore_id: u.coordinatore_id || null
    });
    setShowEditUser(true);
  };

  const handleUpdateUser = async (e) => {
    e.preventDefault();
    try {
      // Sanitizzazione campi contratto: stringhe vuote → null per il backend
      const toNum = (v) => v === '' || v === null || v === undefined ? null : parseFloat(v);
      const payload = {
        ...editUser,
        contratto_tipo_id: editUser.contratto_tipo_id || null,
        contratto_ore_mensili_override: toNum(editUser.contratto_ore_mensili_override),
        contratto_ore_settimanali_override: toNum(editUser.contratto_ore_settimanali_override),
        contratto_ore_giornaliere_override: toNum(editUser.contratto_ore_giornaliere_override),
        contratto_giorni_settimana_override: editUser.contratto_giorni_settimana_override === ''
          ? null : parseInt(editUser.contratto_giorni_settimana_override, 10),
      };
      await axios.put(`${API}/users/${selectedUser.id}`, payload);
      toast.success('Utente aggiornato con successo!');
      setShowEditUser(false);
      setSelectedUser(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nell\'aggiornamento dell\'utente');
    }
  };

  const handleDeleteUserClick = (u) => {
    setSelectedUser(u);
    setShowDeleteConfirm(true);
  };

  const handleDeleteUser = async () => {
    try {
      await axios.delete(`${API}/users/${selectedUser.id}`);
      toast.success('Utente eliminato con successo!');
      setShowDeleteConfirm(false);
      setSelectedUser(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nell\'eliminazione dell\'utente');
    }
  };

  const handleApproveUser = async (userId) => {
    try {
      await axios.post(`${API}/users/${userId}/approval`, {
        action: 'approve'
      });
      toast.success('Utente approvato con successo!');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nell\'approvazione dell\'utente');
    }
  };

  const handleRejectUser = async (userId, reason) => {
    try {
      await axios.post(`${API}/users/${userId}/approval`, {
        action: 'reject',
        rejection_reason: reason || 'Registrazione non approvata'
      });
      toast.success('Registrazione rifiutata');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nel rifiuto dell\'utente');
    }
  };

  const handleUpdateSettings = async () => {
    try {
      await axios.put(`${API}/settings`, settings);
      toast.success('Impostazioni aggiornate!');
      setShowSettings(false);
    } catch (error) {
      toast.error('Errore nell\'aggiornamento delle impostazioni');
    }
  };

  const handleSaveFigura = async (e) => {
    e.preventDefault();
    try {
      if (editingFigura) {
        await axios.put(`${API}/figure-professionali/${editingFigura.id}`, newFigura);
        toast.success('Figura professionale aggiornata!');
      } else {
        await axios.post(`${API}/figure-professionali`, newFigura);
        toast.success('Figura professionale creata!');
      }
      setShowFiguraDialog(false);
      setEditingFigura(null);
      setNewFigura({ nome: '', tipologie_accessi: [{ nome: '', minuti: 0 }], nota_pacchetti: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nell\'operazione');
    }
  };

  const handleDeleteFigura = async (figuraId) => {
    if (!window.confirm('Sei sicuro di voler eliminare questa figura professionale?')) return;
    try {
      await axios.delete(`${API}/figure-professionali/${figuraId}`);
      toast.success('Figura professionale eliminata!');
      fetchData();
    } catch (error) {
      toast.error('Errore nell\'eliminazione');
    }
  };

  // ====== GESTIONE CONVENZIONI ======
  const fetchConvenzioni = async () => {
    try {
      const response = await axios.get(`${API}/convenzioni`);
      setConvenzioni(response.data);
    } catch (error) {
      toast.error('Errore nel caricamento delle convenzioni');
    }
  };

  const openCreateConvenzioneDialog = () => {
    setConvenzioneForm({
      titolo: '',
      descrizione: '',
      categoria: 'moda',
      offerta: '',
      link: '',
      immagine_url: '',
      immagine_file: null,
      istruzioni: ''
    });
    setIsEditingConvenzione(false);
    setSelectedConvenzione(null);
    setShowConvenzioneDialog(true);
    setUploadProgress(0);
    setUploadError('');
  };

  const openEditConvenzioneDialog = (convenzione) => {
    setConvenzioneForm({
      titolo: convenzione.titolo,
      descrizione: convenzione.descrizione,
      categoria: convenzione.categoria,
      offerta: convenzione.offerta || convenzione.sconto || '', // Backward compatibility
      link: convenzione.link || '',
      immagine_url: convenzione.immagine_url || '',
      immagine_file: null,
      istruzioni: convenzione.istruzioni || ''
    });
    setIsEditingConvenzione(true);
    setSelectedConvenzione(convenzione);
    setShowConvenzioneDialog(true);
  };

  const handleSaveConvenzione = async (e) => {
    e.preventDefault();
    
    // Validazione istruzioni
    if (convenzioneForm.istruzioni && convenzioneForm.istruzioni.length > 1000) {
      toast.error('Le istruzioni non possono superare i 1000 caratteri');
      return;
    }
    
    try {
      setUploadError('');
      let imageUrl = convenzioneForm.immagine_url;
      
      // Se c'è un file immagine caricato, prima fai l'upload con retry
      if (convenzioneForm.immagine_file) {
        const file = convenzioneForm.immagine_file;
        
        // Validazione file
        if (file.size > 5 * 1024 * 1024) {
          toast.error('L\'immagine non può superare i 5MB');
          return;
        }
        
        if (!file.type.startsWith('image/')) {
          toast.error('Il file deve essere un\'immagine');
          return;
        }
        
        const formData = new FormData();
        formData.append('file', file);
        
        // Upload con progress e retry
        let uploadSuccess = false;
        let retries = 3;
        
        while (!uploadSuccess && retries > 0) {
          try {
            setUploadProgress(0);
            const uploadResponse = await axios.post(`${API}/convenzioni/upload-image`, formData, {
              onUploadProgress: (progressEvent) => {
                const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                setUploadProgress(percentCompleted);
              }
            });
            
            imageUrl = `${BACKEND_URL}${uploadResponse.data.image_url}`;
            uploadSuccess = true;
            setUploadProgress(100);
          } catch (uploadError) {
            retries--;
            if (retries === 0) {
              throw new Error('Upload fallito dopo 3 tentativi. Riprova più tardi.');
            }
            await new Promise(resolve => setTimeout(resolve, 1000)); // Wait 1s before retry
          }
        }
      }
      
      const convenzioneData = {
        titolo: convenzioneForm.titolo,
        descrizione: convenzioneForm.descrizione,
        categoria: convenzioneForm.categoria,
        offerta: convenzioneForm.offerta,
        link: convenzioneForm.link,
        immagine_url: imageUrl,
        istruzioni: convenzioneForm.istruzioni || null
      };
      
      if (isEditingConvenzione && selectedConvenzione) {
        await axios.put(`${API}/convenzioni/${selectedConvenzione.id}`, convenzioneData);
        toast.success('Convenzione aggiornata con successo!');
      } else {
        await axios.post(`${API}/convenzioni`, convenzioneData);
        toast.success('Convenzione creata con successo!');
      }
      
      setShowConvenzioneDialog(false);
      setUploadProgress(0);
      fetchConvenzioni();
    } catch (error) {
      const errorMessage = error.message || error.response?.data?.detail || 'Errore nel salvataggio della convenzione';
      setUploadError(errorMessage);
      toast.error(errorMessage);
    }
  };

  const openDeleteConvenzioneDialog = (convenzione) => {
    setSelectedConvenzione(convenzione);
    setShowDeleteConvenzioneDialog(true);
  };

  const handleDeleteConvenzione = async () => {
    if (!selectedConvenzione) return;
    try {
      await axios.delete(`${API}/convenzioni/${selectedConvenzione.id}`);
      toast.success('Convenzione eliminata con successo!');
      setShowDeleteConvenzioneDialog(false);
      fetchConvenzioni();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nell\'eliminazione della convenzione');
    }
  };
  // ====== END GESTIONE CONVENZIONI ======

  // ====== GESTIONE FORMAZIONE ======
  const fetchCorsi = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/formazione/corsi`, {
        params: { solo_attivi: true },
        headers: { Authorization: `Bearer ${token}` }
      });
      setCorsi(response.data);
    } catch (error) {
      toast.error('Errore nel caricamento dei corsi');
    }
  };

  const fetchModuli = async (corsoId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/formazione/corsi/${corsoId}/moduli`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setModuli(response.data);
    } catch (error) {
      toast.error('Errore nel caricamento dei moduli');
    }
  };

  const openCreateCorsoDialog = () => {
    setCorsoForm({
      titolo: '',
      descrizione: '',
      immagine_copertina_url: '',
      obbligatorio: false,
      data_scadenza: '',
      ruoli_assegnati: [],
      figure_professionali_assegnate: [],
      punteggio_minimo_attestato: 72
    });
    setIsEditingCorso(false);
    setSelectedCorso(null);
    setShowCorsoDialog(true);
  };

  const openEditCorsoDialog = (corso) => {
    setCorsoForm({
      titolo: corso.titolo,
      descrizione: corso.descrizione,
      immagine_copertina_url: corso.immagine_copertina_url || '',
      obbligatorio: corso.obbligatorio,
      data_scadenza: corso.data_scadenza || '',
      ruoli_assegnati: corso.ruoli_assegnati || [],
      figure_professionali_assegnate: corso.figure_professionali_assegnate || [],
      punteggio_minimo_attestato: corso.punteggio_minimo_attestato || 72,
      // Personalizzazione attestato
      logo_aggiuntivo_url: corso.logo_aggiuntivo_url || '',
      nome_firmatario: corso.nome_firmatario || '',
      cognome_firmatario: corso.cognome_firmatario || '',
      ruolo_firmatario: corso.ruolo_firmatario || '',
      firma_immagine_url: corso.firma_immagine_url || ''
    });
    setIsEditingCorso(true);
    setSelectedCorso(corso);
    setShowCorsoDialog(true);
  };

  const handleSaveCorso = async (e) => {
    e.preventDefault();
    try {
      if (isEditingCorso && selectedCorso) {
        await axios.put(`${API}/formazione/corsi/${selectedCorso.id}`, corsoForm);
        toast.success('Corso aggiornato con successo!');
      } else {
        await axios.post(`${API}/formazione/corsi`, corsoForm);
        toast.success('Corso creato con successo!');
      }
      setShowCorsoDialog(false);
      fetchCorsi();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nel salvataggio del corso');
    }
  };

  const openDeleteCorsoDialog = (corso) => {
    setSelectedCorso(corso);
    setShowDeleteCorsoDialog(true);
  };

  const handleDeleteCorso = async () => {
    if (!selectedCorso) return;
    try {
      await axios.delete(`${API}/formazione/corsi/${selectedCorso.id}`);
      toast.success('Corso eliminato con successo!');
      setShowDeleteCorsoDialog(false);
      fetchCorsi();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nell\'eliminazione del corso');
    }
  };

  const openCreateModuloDialog = () => {
    if (!selectedCorso) return;
    const nextOrdine = moduli.length > 0 ? Math.max(...moduli.map(m => m.ordine)) + 1 : 1;
    setModuloForm({
      titolo: '',
      descrizione: '',
      ordine: nextOrdine,
      video_url: '',
      video_type: 'gdrive',
      video_password: '',
      testo_markdown: '',
      pdf_url: ''
    });
    setIsEditingModulo(false);
    setSelectedModulo(null);
    setShowModuloDialog(true);
  };

  const openEditModuloDialog = (modulo) => {
    setModuloForm({
      titolo: modulo.titolo,
      descrizione: modulo.descrizione || '',
      ordine: modulo.ordine,
      video_url: modulo.video_url || '',
      video_type: modulo.video_type || 'gdrive',
      video_password: modulo.video_password || '',
      testo_markdown: modulo.testo_markdown || '',
      pdf_url: modulo.pdf_url || ''
    });
    setIsEditingModulo(true);
    setSelectedModulo(modulo);
    setShowModuloDialog(true);
  };

  const handleSaveModulo = async (e) => {
    e.preventDefault();
    if (isSavingModulo) return; // Previeni click multipli
    
    setIsSavingModulo(true);
    try {
      const token = localStorage.getItem('token');
      
      if (isEditingModulo && selectedModulo) {
        const response = await axios.put(
          `${API}/formazione/moduli/${selectedModulo.id}`, 
          moduloForm, 
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Modulo aggiornato con successo!');
      } else {
        const response = await axios.post(
          `${API}/formazione/corsi/${selectedCorso.id}/moduli`, 
          moduloForm, 
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Modulo creato con successo!');
      }
      setShowModuloDialog(false);
      await fetchModuli(selectedCorso.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nel salvataggio del modulo');
    } finally {
      setIsSavingModulo(false);
    }
  };

  const openDeleteModuloDialog = (modulo) => {
    setSelectedModulo(modulo);
    setShowDeleteModuloDialog(true);
  };

  const handleDeleteModulo = async () => {
    if (!selectedModulo) return;
    try {
      await axios.delete(`${API}/formazione/moduli/${selectedModulo.id}`);
      toast.success('Modulo eliminato con successo!');
      setShowDeleteModuloDialog(false);
      fetchModuli(selectedCorso.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nell\'eliminazione del modulo');
    }
  };

  // Funzioni Test Finale
  const openCreateTestFinaleDialog = () => {
    if (!selectedCorso) return;
    setTestFinaleForm({
      titolo: 'Test Finale - ' + selectedCorso.titolo,
      descrizione: 'Test finale per completare il corso',
      punteggio_minimo: 60,
      tentativi_max: 3,
      ordine_casuale: false
    });
    setShowTestFinaleDialog(true);
  };

  const handleSaveTestFinale = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(`${API}/formazione/corsi/${selectedCorso.id}/test-finale`, testFinaleForm);
      toast.success('Test finale creato con successo!');
      setShowTestFinaleDialog(false);
      
      // Aggiorna il corso corrente con il test_finale_id
      setSelectedCorso({...selectedCorso, test_finale_id: response.data.id});
      fetchCorsi(); // Ricarica tutti i corsi per aggiornare la lista
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nella creazione del test finale');
    }
  };

  const viewCorsoModuli = (corso) => {
    setSelectedCorso(corso);
    fetchModuli(corso.id);
  };

  const backToCorsiList = () => {
    setSelectedCorso(null);
    setModuli([]);
    setSelectedTest(null);
  };

  // ====== GESTIONE TEST ======
  const openCreateTestDialog = (modulo) => {
    setSelectedModulo(modulo);
    setTestForm({
      tipo: 'intermedio',
      titolo: '',
      descrizione: '',
      punteggio_minimo: 60,
      tentativi_max: 5,
      ordine_casuale: false
    });
    setIsEditingTest(false);
    setSelectedTest(null);
    setShowTestDialog(true);
  };

  const openEditTestDialog = (test) => {
    setTestForm({
      tipo: test.tipo,
      titolo: test.titolo,
      descrizione: test.descrizione || '',
      punteggio_minimo: test.punteggio_minimo,
      tentativi_max: test.tentativi_max,
      ordine_casuale: test.ordine_casuale || false
    });
    setIsEditingTest(true);
    setSelectedTest(test);
    setShowTestDialog(true);
  };

  const handleSaveTest = async (e) => {
    e.preventDefault();
    try {
      if (isEditingTest && selectedTest) {
        await axios.put(`${API}/formazione/test/${selectedTest.id}`, testForm);
        toast.success('Test aggiornato con successo!');
      } else {
        const payload = {
          ...testForm,
          modulo_id: selectedModulo?.id || null
        };
        const response = await axios.post(`${API}/formazione/corsi/${selectedCorso.id}/test`, payload);
        setSelectedTest(response.data);
        // Aggiorna il modulo per indicare che ha un test
        if (selectedModulo) {
          await axios.put(`${API}/formazione/moduli/${selectedModulo.id}`, {
            has_test: true,
            test_id: response.data.id
          });
          fetchModuli(selectedCorso.id);
        }
        toast.success('Test creato con successo!');
      }
      setShowTestDialog(false);
      if (selectedTest || !isEditingTest) {
        fetchDomande(isEditingTest ? selectedTest.id : null);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nel salvataggio del test');
    }
  };

  const fetchDomande = async (testId) => {
    if (!testId) return;
    try {
      const response = await axios.get(`${API}/formazione/test/${testId}/domande`);
      setDomande(response.data);
    } catch (error) {
      toast.error('Errore nel caricamento delle domande');
    }
  };

  const viewTestDomande = async (testId) => {
    try {
      const response = await axios.get(`${API}/formazione/test/${testId}`);
      setSelectedTest(response.data);
      fetchDomande(testId);
    } catch (error) {
      toast.error('Errore nel caricamento del test');
    }
  };

  const backToModuliList = () => {
    setSelectedTest(null);
    setDomande([]);
  };

  const openDeleteTestDialog = (test) => {
    setSelectedTest(test);
    setShowDeleteTestDialog(true);
  };

  const handleDeleteTest = async () => {
    if (!selectedTest) return;
    try {
      await axios.delete(`${API}/formazione/test/${selectedTest.id}`);
      toast.success('Test eliminato con successo!');
      setShowDeleteTestDialog(false);
      setSelectedTest(null);
      fetchModuli(selectedCorso.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nell\'eliminazione del test');
    }
  };

  // ====== GESTIONE DOMANDE ======
  const openCreateDomandaDialog = () => {
    if (!selectedTest) return;
    const nextOrdine = domande.length > 0 ? Math.max(...domande.map(d => d.ordine)) + 1 : 1;
    setDomandaForm({
      tipo: 'multipla',
      testo: '',
      ordine: nextOrdine,
      punti: 1,
      opzioni: ['', '', '', ''],
      risposta_corretta: '',
      risposte_corrette: [],
      richiede_correzione_manuale: false
    });
    setIsEditingDomanda(false);
    setSelectedDomanda(null);
    setShowDomandaDialog(true);
  };

  const openEditDomandaDialog = (domanda) => {
    setDomandaForm({
      tipo: domanda.tipo,
      testo: domanda.testo,
      ordine: domanda.ordine,
      punti: domanda.punti,
      opzioni: domanda.opzioni || ['', '', '', ''],
      risposta_corretta: domanda.risposta_corretta || '',
      risposte_corrette: domanda.risposte_corrette || [],
      richiede_correzione_manuale: domanda.richiede_correzione_manuale || false
    });
    setIsEditingDomanda(true);
    setSelectedDomanda(domanda);
    setShowDomandaDialog(true);
  };

  const handleSaveDomanda = async (e) => {
    e.preventDefault();
    try {
      if (isEditingDomanda && selectedDomanda) {
        await axios.put(`${API}/formazione/domande/${selectedDomanda.id}`, domandaForm);
        toast.success('Domanda aggiornata con successo!');
      } else {
        await axios.post(`${API}/formazione/test/${selectedTest.id}/domande`, domandaForm);
        toast.success('Domanda creata con successo!');
      }
      setShowDomandaDialog(false);
      fetchDomande(selectedTest.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nel salvataggio della domanda');
    }
  };

  const openDeleteDomandaDialog = (domanda) => {
    setSelectedDomanda(domanda);
    setShowDeleteDomandaDialog(true);
  };

  const handleDeleteDomanda = async () => {
    if (!selectedDomanda) return;
    try {
      await axios.delete(`${API}/formazione/domande/${selectedDomanda.id}`);
      toast.success('Domanda eliminata con successo!');
      setShowDeleteDomandaDialog(false);
      fetchDomande(selectedTest.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nell\'eliminazione della domanda');
    }
  };

  // ====== MONITORAGGIO FORMAZIONE ======
  const fetchProgressiOperatori = async () => {
    try {
      const params = {};
      if (filtroCorsoMonitoraggio) params.corso_id = filtroCorsoMonitoraggio;
      if (filtroOperatoreMonitoraggio) params.user_id = filtroOperatoreMonitoraggio;
      
      const response = await axios.get(`${API}/formazione/progressi`, { params });
      setProgressiOperatori(response.data);
    } catch (error) {
      toast.error('Errore nel caricamento dei progressi');
    }
  };

  useEffect(() => {
    if (user.role === 'super_amministratore') {
      fetchProgressiOperatori();
      fetchRisposteDaCorreggere();
      fetchCertificati();
      fetchRisposteQuestionari();
    }
  }, [filtroCorsoMonitoraggio, filtroOperatoreMonitoraggio]);

  const fetchRisposteDaCorreggere = async () => {
    try {
      const response = await axios.get(`${API}/formazione/risposte-da-correggere`);
      setRisposteDaCorreggere(response.data);
    } catch (error) {
    }
  };

  const fetchCertificati = async () => {
    try {
      const response = await axios.get(`${API}/formazione/certificati`);
      setCertificati(response.data);
    } catch (error) {
    }
  };

  const openCorrezioneDialog = (risposta) => {
    setRispostaCorrente(risposta);
    setCorrezioneForm({
      corretta: false,
      punti_ottenuti: 0,
      feedback: ''
    });
    setShowCorrezioneDialog(true);
  };

  const handleSalvaCorrezione = async () => {
    if (!rispostaCorrente) return;
    try {
      await axios.post(`${API}/formazione/risposte/${rispostaCorrente.id}/correggi`, correzioneForm);
      toast.success('Risposta corretta con successo!');
      setShowCorrezioneDialog(false);
      fetchRisposteDaCorreggere();
    } catch (error) {
      toast.error('Errore nel salvataggio della correzione');
    }
  };

  const visualizzaRiepilogoTest = async (certificatoId) => {
    try {
      const response = await axios.get(`${API}/formazione/certificati/${certificatoId}/test-finale`);
      setRiepilogoTest(response.data);
      setShowRiepilogoTestDialog(true);
    } catch (error) {
      toast.error('Errore nel caricamento del riepilogo test');
    }
  };

  const downloadPDFAttestato = async (certificatoId, nomeOperatore) => {
    try {
      const response = await axios.get(`${API}/formazione/certificati/${certificatoId}/pdf`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `attestato_${nomeOperatore.replace(/\s+/g, '_')}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Attestato scaricato con successo!');
    } catch (error) {
      toast.error('Errore nel download dell\'attestato');
    }
  };

  const downloadPDFTest = async (certificatoId, nomeOperatore) => {
    try {
      const response = await axios.get(`${API}/formazione/certificati/${certificatoId}/test-pdf`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `test_${nomeOperatore.replace(/\s+/g, '_')}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Test scaricato con successo!');
    } catch (error) {
      toast.error('Errore nel download del test');
    }
  };

  // ====== GESTIONE QUESTIONARIO SODDISFACIMENTO ======
  
  const openQuestionarioConfig = async (corso) => {
    setQuestionarioCorso(corso);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/formazione/corsi/${corso.id}/questionario`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setQuestionarioForm({
        domande: response.data.domande || questionarioForm.domande,
        scala_min: response.data.scala_min || 1,
        scala_max: response.data.scala_max || 5,
        etichetta_min: response.data.etichetta_min || "Per niente",
        etichetta_max: response.data.etichetta_max || "Moltissimo",
        abilita_commenti: response.data.abilita_commenti !== undefined ? response.data.abilita_commenti : true
      });
    } catch (error) {
    }
    setShowQuestionarioConfigDialog(true);
  };

  const handleSaveQuestionario = async (e) => {
    e.preventDefault();
    if (!questionarioCorso) return;
    
    try {
      await axios.put(`${API}/formazione/corsi/${questionarioCorso.id}/questionario`, questionarioForm);
      toast.success('Questionario aggiornato con successo!');
      setShowQuestionarioConfigDialog(false);
    } catch (error) {
      toast.error('Errore nel salvataggio del questionario');
    }
  };

  const fetchRisposteQuestionari = async () => {
    try {
      const response = await axios.get(`${API}/formazione/risposte-questionari`);
      setRisposteQuestionari(response.data);
    } catch (error) {
    }
  };

  const viewDettaglioRisposta = async (risposta) => {
    try {
      // Recupera i dettagli completi del questionario per questo corso
      const token = localStorage.getItem('token');
      const questionarioResponse = await axios.get(`${API}/formazione/corsi/${risposta.corso_id}/questionario`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const questionarioData = questionarioResponse.data;
      
      // Combina i dati della risposta con i dettagli del questionario
      setRispostaSelezionata({
        ...risposta,
        questionario: questionarioData
      });
      setShowDettaglioRispostaDialog(true);
    } catch (error) {
      toast.error('Errore nel caricamento dei dettagli');
    }
  };


  const handleExportExcelStatistiche = async () => {
    try {
      // Costruisci URL con parametri filtro
      let url = `${API}/formazione/statistiche/export-excel`;
      const params = new URLSearchParams();
      
      if (filtroCorsoMonitoraggio) {
        params.append('corso_id', filtroCorsoMonitoraggio);
      }
      if (filtroOperatoreMonitoraggio) {
        params.append('user_id', filtroOperatoreMonitoraggio);
      }
      
      if (params.toString()) {
        url += `?${params.toString()}`;
      }
      
      const response = await axios.get(url, {
        responseType: 'blob'
      });
      
      const urlBlob = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = urlBlob;
      
      // Nome file dinamico
      const filtroText = filtroCorsoMonitoraggio || filtroOperatoreMonitoraggio ? '_filtrato' : '';
      link.setAttribute('download', `progressi_formazione${filtroText}_${new Date().toISOString().split('T')[0]}.xlsx`);
      
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Excel scaricato con successo!');
    } catch (error) {
      toast.error('Errore nell\'export Excel');
    }
  };

  // ====== END GESTIONE QUESTIONARIO ======
  // ====== END GESTIONE FORMAZIONE ======


  const handleDownloadExcel = async () => {
    try {
      const response = await axios.get(`${API}/reports/export-excel`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `report_croce_gialla_${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Excel scaricato con successo!');
    } catch (error) {
      toast.error('Errore nel download dell\'Excel');
    }
  };

  const handleDownloadSinglePDF = async (reportId) => {
    try {
      const response = await axios.get(`${API}/reports/${reportId}/export-pdf`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `report_${reportId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('PDF scaricato con successo!');
    } catch (error) {
      toast.error('Errore nel download del PDF');
    }
  };

  const handleDownloadMultiplePDF = async () => {
    if (selectedReports.length === 0) {
      toast.error('Seleziona almeno un report');
      return;
    }
    try {
      const response = await axios.post(`${API}/reports/export-multiple-pdf`, {
        report_ids: selectedReports
      }, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `reports_${new Date().toISOString().split('T')[0]}.zip`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      setSelectedReports([]);
      toast.success('PDF scaricati con successo!');
    } catch (error) {
      toast.error('Errore nel download dei PDF');
    }
  };

  const toggleReportSelection = (reportId) => {
    setSelectedReports(prev => 
      prev.includes(reportId) 
        ? prev.filter(id => id !== reportId)
        : [...prev, reportId]
    );
  };

  const selectAllReports = () => {
    if (selectedReports.length === reports.length) {
      setSelectedReports([]);
    } else {
      setSelectedReports(reports.map(r => r.id));
    }
  };

  // Login Monitoring Functions
  const fetchLoginDashboard = async () => {
    try {
      const response = await axios.get(`${API}/admin/login/dashboard-stats`);
      setLoginDashboard(response.data);
    } catch (error) {
      toast.error('Errore nel caricamento dashboard login');
    }
  };

  const fetchLoginHistory = async () => {
    try {
      const params = new URLSearchParams();
      if (loginFilters.user_id) params.append('user_id', loginFilters.user_id);
      if (loginFilters.success !== null) params.append('success', loginFilters.success);
      if (loginFilters.from_date) params.append('from_date', loginFilters.from_date);
      if (loginFilters.to_date) params.append('to_date', loginFilters.to_date);
      
      const response = await axios.get(`${API}/admin/login/history?${params.toString()}`);
      setLoginHistory(response.data);
    } catch (error) {
      toast.error('Errore nel caricamento storico login');
    }
  };

  const fetchActiveSessions = async () => {
    try {
      const response = await axios.get(`${API}/admin/login/active-sessions`);
      setActiveSessions(response.data);
    } catch (error) {
      toast.error('Errore nel caricamento sessioni attive');
    }
  };

  const fetchFailedLogins = async () => {
    try {
      const response = await axios.get(`${API}/admin/login/failed-logins`);
      setFailedLogins(response.data);
    } catch (error) {
      toast.error('Errore nel caricamento login falliti');
    }
  };

  const handleTerminateSession = async (sessionId) => {
    if (!confirm('Vuoi davvero terminare questa sessione?')) return;
    try {
      await axios.post(`${API}/admin/login/terminate-session/${sessionId}`);
      toast.success('Sessione terminata');
      fetchActiveSessions();
    } catch (error) {
      toast.error('Errore nella terminazione sessione');
    }
  };


  const toggleReportExpansion = (reportId) => {
    setExpandedReports(prev => 
      prev.includes(reportId) 
        ? prev.filter(id => id !== reportId)
        : [...prev, reportId]
    );
  };


  // Report Management Functions
  const handleApproveReport = async () => {
    if (!selectedReport) return;
    try {
      await axios.post(`${API}/reports/${selectedReport.id}/approval`, {
        status: 'approved'
      });
      toast.success('Report approvato con successo!');
      setShowApproveDialog(false);
      setSelectedReport(null);
      fetchData();
    } catch (error) {
      toast.error('Errore nell\'approvazione del report');
    }
  };

  const handleRejectReport = async () => {
    if (!selectedReport || !rejectionReason.trim()) {
      toast.error('Inserisci una motivazione per il rifiuto');
      return;
    }
    try {
      await axios.post(`${API}/reports/${selectedReport.id}/approval`, {
        status: 'rejected',
        rejection_reason: rejectionReason
      });
      toast.success('Report rifiutato');
      setShowRejectDialog(false);
      setSelectedReport(null);
      setRejectionReason('');
      fetchData();
    } catch (error) {
      toast.error('Errore nel rifiuto del report');
    }
  };

  const handleDeleteReport = async () => {
    if (!selectedReport) return;
    try {
      await axios.delete(`${API}/reports/${selectedReport.id}`);
      toast.success('Report eliminato con successo!');
      setShowDeleteReportDialog(false);
      setSelectedReport(null);
      fetchData();
    } catch (error) {
      toast.error('Errore nell\'eliminazione del report');
    }
  };

  // Invoice Management Functions
  const fetchInvoices = async () => {
    try {
      const params = new URLSearchParams();
      if (invoiceFilters.year) params.append('year', invoiceFilters.year);
      if (invoiceFilters.month) params.append('month', invoiceFilters.month);
      if (invoiceFilters.asl) params.append('asl', invoiceFilters.asl);
      if (invoiceFilters.figura) params.append('figura', invoiceFilters.figura);
      
      const response = await axios.get(`${API}/invoices?${params.toString()}`);
      setInvoices(response.data);
    } catch (error) {
      toast.error('Errore nel caricamento delle fatture');
    }
  };

  const handleDeleteInvoice = async (invoice) => {
    if (!window.confirm(`Sei sicuro di voler eliminare la fattura di ${months[invoice.month - 1]} ${invoice.year}? Questa azione non può essere annullata.`)) {
      return;
    }
    
    try {
      await axios.delete(`${API}/invoices/${invoice.id}`);
      toast.success('Fattura eliminata con successo!');
      fetchInvoices();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nell\'eliminazione della fattura');
    }
  };

  const fetchBillingConfig = async (userId) => {
    try {
      const response = await axios.get(`${API}/users/${userId}/billing-config`);
      setBillingConfig(response.data);
    } catch (error) {
      // Se non esiste, usa i default
      setBillingConfig({
        costi_per_accesso: {},
        percentuale_cassa_previdenziale: 4.0,
        importo_reperibilita_default: 150.0,
        costo_auto_default: -100.0
      });
    }
  };

  const saveBillingConfig = async () => {
    if (!selectedInvoiceUser) return;
    try {
      await axios.post(`${API}/users/${selectedInvoiceUser.id}/billing-config`, {
        ...billingConfig,
        user_id: selectedInvoiceUser.id
      });
      toast.success('Configurazione salvata con successo!');
      setShowBillingConfigDialog(false);
    } catch (error) {
      toast.error('Errore nel salvataggio della configurazione');
    }
  };

  const openBillingConfigDialog = async (user) => {
    setSelectedInvoiceUser(user);
    await fetchBillingConfig(user.id);
    setShowBillingConfigDialog(true);
  };

  const openInvoiceDialog = async (user) => {
    setSelectedInvoiceUser(user);
    
    // Fetch billing config to get costs
    try {
      const configResponse = await axios.get(`${API}/users/${user.id}/billing-config`);
      const config = configResponse.data;
      
      // Prepara gli accessi_data basati sulla figura professionale dell'utente
      const userFigura = settings.figure_professionali?.find(f => f.nome === user.figura_professionale);
      const accessiData = userFigura ? userFigura.tipologie_accessi.map(tipo => ({
        tipo_accesso: tipo.nome,
        numero_accessi: 0,
        costo_unitario: config.costi_per_accesso[tipo.nome] || 0,
        totale: 0
      })) : [];
      
      setNewInvoice({
        user_id: user.id,
        month: new Date().getMonth() + 1,
        year: new Date().getFullYear(),
        asl_riferimento: user.asl_riferimento || '',
        note: '',
        accessi_data: accessiData,
        voci_libere: [],
        importo_reperibilita: config.importo_reperibilita_default || 150,
        costo_auto: config.costo_auto_default || 0
      });
    } catch (error) {
      // Fallback se non c'è config
      const userFigura = settings.figure_professionali?.find(f => f.nome === user.figura_professionale);
      const accessiData = userFigura ? userFigura.tipologie_accessi.map(tipo => ({
        tipo_accesso: tipo.nome,
        numero_accessi: 0,
        costo_unitario: 0,
        totale: 0
      })) : [];
      
      setNewInvoice({
        user_id: user.id,
        month: new Date().getMonth() + 1,
        year: new Date().getFullYear(),
        asl_riferimento: user.asl_riferimento || '',
        note: '',
        accessi_data: accessiData,
        voci_libere: [],
        importo_reperibilita: 150,
        costo_auto: 0
      });
    }
    
    setShowInvoiceDialog(true);
  };

  const updateInvoiceAccesso = (index, field, value) => {
    const newAccessiData = [...newInvoice.accessi_data];
    newAccessiData[index][field] = field === 'numero_accessi' || field === 'costo_unitario' 
      ? parseFloat(value) || 0 
      : value;
    
    // Ricalcola totale
    if (field === 'numero_accessi' || field === 'costo_unitario') {
      newAccessiData[index].totale = newAccessiData[index].numero_accessi * newAccessiData[index].costo_unitario;
    }
    
    setNewInvoice({ ...newInvoice, accessi_data: newAccessiData });
  };

  const handleCreateInvoice = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/invoices`, newInvoice);
      toast.success('Fattura creata con successo!');
      setShowInvoiceDialog(false);
      fetchInvoices();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nella creazione della fattura');
    }
  };

  const openEditInvoiceDialog = (invoice) => {
    setSelectedInvoice(invoice);
    setNewInvoice({
      user_id: invoice.user_id,
      month: invoice.month,
      year: invoice.year,
      asl_riferimento: invoice.asl_riferimento || '',
      note: invoice.note || '',
      accessi_data: invoice.accessi_data || [],
      voci_libere: invoice.voci_libere || [],
      importo_reperibilita: invoice.importo_reperibilita || 150,
      costo_auto: invoice.costo_auto || 0
    });
    setShowInvoiceDetailDialog(false);
    setShowEditInvoiceDialog(true);
  };

  const handleUpdateInvoice = async (e) => {
    e.preventDefault();
    if (!selectedInvoice) return;
    
    try {
      await axios.put(`${API}/invoices/${selectedInvoice.id}/recalculate`, newInvoice);
      toast.success('Fattura aggiornata con successo! L\'operatore riceverà una notifica via email.');
      setShowEditInvoiceDialog(false);
      setSelectedInvoice(null);
      fetchInvoices();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nell\'aggiornamento della fattura');
    }
  };

  const openInvoiceDetail = (invoice) => {
    setSelectedInvoice(invoice);
    setShowInvoiceDetailDialog(true);
  };

  const openAdminUploadInvoiceDialog = (invoice) => {
    setSelectedInvoice(invoice);
    setAdminInvoiceUploadData({
      data_fattura: invoice.data_fattura || '',
      numero_fattura: invoice.numero_fattura || ''
    });
    setShowAdminUploadInvoiceDialog(true);
  };

  const handleAdminUploadInvoice = async (e) => {
    e.preventDefault();
    if (!selectedInvoice) return;
    
    try {
      await axios.post(`${API}/invoices/${selectedInvoice.id}/upload`, adminInvoiceUploadData);
      toast.success('Estremi fattura salvati con successo!');
      setShowAdminUploadInvoiceDialog(false);
      fetchInvoices();
    } catch (error) {
      toast.error('Errore nel salvataggio degli estremi');
    }
  };


  const openEditFigura = (figura) => {
    setEditingFigura(figura);
    setNewFigura({
      nome: figura.nome,
      tipologie_accessi: [...figura.tipologie_accessi],
      nota_pacchetti: figura.nota_pacchetti || ''
    });
    setShowFiguraDialog(true);
  };

  const addTipologia = () => {
    setNewFigura({
      ...newFigura,
      tipologie_accessi: [...newFigura.tipologie_accessi, { nome: '', minuti: 0 }]
    });
  };

  const removeTipologia = (index) => {
    const newTipologie = newFigura.tipologie_accessi.filter((_, i) => i !== index);
    setNewFigura({ ...newFigura, tipologie_accessi: newTipologie });
  };

  const updateTipologia = (index, field, value) => {
    const newTipologie = [...newFigura.tipologie_accessi];
    newTipologie[index][field] = field === 'minuti' ? parseInt(value) || 0 : value;
    setNewFigura({ ...newFigura, tipologie_accessi: newTipologie });
  };

  const months = monthsList;

  // Calculate statistics
  const calculateMinutesForReport = (report) => {
    let totalMinutes = 0;
    const figura = report.figura_professionale;
    const accessiData = report.accessi_data || {};
    
    // Calculate minutes from accessi_data
    for (const [tipo, count] of Object.entries(accessiData)) {
      totalMinutes += getMinutesForAccess(figura, tipo, count);
    }
    
    // Add travel time for each access
    const totalAccessi = Object.values(accessiData).reduce((acc, count) => acc + count, 0);
    const tempoSpostamento = settings?.tempo_spostamento || 10;
    totalMinutes += totalAccessi * tempoSpostamento;
    
    return totalMinutes;
  };

  const getTotalAccessiForReport = (report) => {
    const accessiData = report.accessi_data || {};
    return Object.values(accessiData).reduce((acc, count) => acc + count, 0);
  };

  const calculateFerieDays = (report) => {
    const ferie = report.assenze?.ferie || [];
    if (!Array.isArray(ferie)) return 0;
    return ferie.length; // Count periods of vacation
  };

  const calculateMalattiaDays = (report) => {
    const malattia = report.assenze?.malattia || [];
    if (!Array.isArray(malattia)) return 0;
    return malattia.length; // Count periods of sick leave
  };

  const calculateAltriGiorni = (report) => {
    const assenze = report.assenze || {};
    let total = 0;
    
    // Donazione sangue
    const donazione = assenze.donazione_sangue || [];
    total += Array.isArray(donazione) ? donazione.length : 0;
    
    // Festivi lavorati
    const festivi = assenze.festivo_lavorato || [];
    total += Array.isArray(festivi) ? festivi.length : 0;
    
    // Riposi settimanali
    const riposi = assenze.riposo_settimanale || [];
    total += Array.isArray(riposi) ? riposi.length : 0;
    
    return total;
  };

  const totalAccessi = reports.reduce((acc, r) => acc + getTotalAccessiForReport(r), 0);
  const totalFerie = reports.reduce((acc, r) => acc + calculateFerieDays(r), 0);
  const totalMalattia = reports.reduce((acc, r) => acc + calculateMalattiaDays(r), 0);
  const totalAltro = reports.reduce((acc, r) => acc + calculateAltriGiorni(r), 0);
  const totalMinutes = reports.reduce((acc, r) => acc + calculateMinutesForReport(r), 0);

  // Group reports by user
  const reportsByUser = {};
  reports.forEach(report => {
    if (!reportsByUser[report.user_id]) {
      reportsByUser[report.user_id] = {
        full_name: report.full_name,
        figura_professionale: report.figura_professionale,
        asl_riferimento: report.asl_riferimento,
        totalMinutes: 0,
        totalAccessi: 0,
        reports: []
      };
    }
    reportsByUser[report.user_id].totalMinutes += calculateMinutesForReport(report);
    reportsByUser[report.user_id].totalAccessi += getTotalAccessiForReport(report);
    reportsByUser[report.user_id].reports.push(report);
  });

  return (
    <div className="min-h-screen bg-gray-50" data-testid="admin-dashboard">
      {/* Header */}
      <div className="bg-white border-b shadow-sm sticky top-0 z-30">
        <div className="px-4 sm:px-6 py-3 sm:py-4">
          <div className="flex items-center justify-between flex-wrap gap-2 sm:gap-4">
            <div className="flex items-center gap-2 sm:gap-4 pl-10 lg:pl-0">
              <img src="/logo.png" alt="Logo" className="h-10 sm:h-12" />
              <div>
                <h1 className="text-sm sm:text-base md:text-xl font-bold text-gray-900">Croce Gialla Home</h1>
                <p className="text-xs sm:text-sm text-gray-500">{user.full_name} &middot; {user.role === 'super_amministratore' ? 'Super Admin' : 'Coordinatore'}</p>
              </div>
            </div>
            <div className="flex gap-1 sm:gap-2 flex-shrink-0">
              {user.role === 'super_amministratore' && (
                <Button
                  data-testid="settings-button"
                  onClick={() => setShowSettings(true)}
                  variant="outline"
                  size="sm"
                  className="gap-1 sm:gap-2 text-xs sm:text-sm px-2 sm:px-3"
                >
                  <SettingsIcon className="w-3 h-3 sm:w-4 sm:h-4" />
                  <span className="hidden md:inline">Impostazioni</span>
                  <span className="md:hidden">Imp.</span>
                </Button>
              )}
              <Button
                data-testid="admin-logout-button"
                onClick={onLogout}
                variant="outline"
                size="sm"
                className="gap-1 sm:gap-2 text-xs sm:text-sm px-2 sm:px-3"
              >
                <LogOut className="w-3 h-3 sm:w-4 sm:h-4" />
                <span className="hidden md:inline">Esci</span>
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Sidebar + Content */}
      <div className="flex min-h-[calc(100vh-65px)]">
        <AdminSidebar activeTab={adminActiveTab} onTabChange={setAdminActiveTab} userRole={user.role} user={user} />

        <main className="flex-1 overflow-auto">
          <div className="p-4 sm:p-6 lg:p-8 max-w-7xl">

        <Tabs value={adminActiveTab} onValueChange={setAdminActiveTab} className="space-y-6">
          <TabsList className="hidden">
            <TabsTrigger value="home">Home</TabsTrigger>
            <TabsTrigger value="reports">Report</TabsTrigger>
            <TabsTrigger value="summary">Riepilogo</TabsTrigger>
            <TabsTrigger value="users">Utenti</TabsTrigger>
            <TabsTrigger value="figure">Figure</TabsTrigger>
            <TabsTrigger value="fatture">Fatture</TabsTrigger>
            <TabsTrigger value="convenzioni">Convenzioni</TabsTrigger>
            <TabsTrigger value="formazione">Formazione</TabsTrigger>
            <TabsTrigger value="procedure">Procedure</TabsTrigger>
            <TabsTrigger value="audit">Audit</TabsTrigger>
            <TabsTrigger value="accreditamenti">Accreditamenti</TabsTrigger>
            <TabsTrigger value="login-monitoring">Login</TabsTrigger>
            <TabsTrigger value="monitoraggio">Monitoraggio</TabsTrigger>
            <TabsTrigger value="privacy">Privacy</TabsTrigger>
            <TabsTrigger value="preventivi">Preventivi</TabsTrigger>
            <TabsTrigger value="turni">Turni</TabsTrigger>
            <TabsTrigger value="planning">Planning</TabsTrigger>
          </TabsList>

          {/* Home Tab */}
          <TabsContent value="home" className="mt-0">
            <AdminHome onNavigate={setAdminActiveTab} />
          </TabsContent>
          <TabsContent value="reports" className="space-y-4">
            <Card className="shadow-sm border-0">
              <CardHeader className="pb-3">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  <div>
                    <CardTitle className="text-base">Report</CardTitle>
                    <CardDescription className="text-xs">Tutti i report degli operatori</CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {/* Filtri inline */}
                <div className="flex flex-wrap items-center gap-2 mb-4">
                  <Input
                    type="number"
                    value={reportFilters.year || ''}
                    onChange={(e) => setReportFilters({...reportFilters, year: e.target.value ? parseInt(e.target.value) : null})}
                    placeholder="Anno"
                    className="h-8 w-24 text-xs"
                  />
                  <select
                    value={reportFilters.month || ''}
                    onChange={(e) => setReportFilters({...reportFilters, month: e.target.value ? parseInt(e.target.value) : null})}
                    className="h-8 px-2 text-xs rounded-md border border-gray-200 bg-white text-gray-700"
                  >
                    <option value="">Tutti i mesi</option>
                    {monthsList.map((m, idx) => (
                      <option key={idx} value={idx + 1}>{m}</option>
                    ))}
                  </select>
                  <select
                    value={reportFilters.asl || ''}
                    onChange={(e) => setReportFilters({...reportFilters, asl: e.target.value || null})}
                    className="h-8 px-2 text-xs rounded-md border border-gray-200 bg-white text-gray-700"
                  >
                    <option value="">Tutte le ASL</option>
                    <option value="Asl Pescara">Asl Pescara</option>
                    <option value="Asl Chieti">Asl Chieti</option>
                    <option value="Asl L'Aquila">Asl L'Aquila</option>
                    <option value="Asl Teramo">Asl Teramo</option>
                  </select>
                  <span className="text-xs text-gray-400 ml-1">{getFilteredReports().length} risultati</span>
                </div>

                {getFilteredReports().length === 0 ? (
                  <div className="text-center py-12 text-gray-500">
                    <FileText className="w-12 h-12 mx-auto mb-3 opacity-30" />
                    <p>Nessun report presente</p>
                  </div>
                ) : (
                  <>
                    {/* Export buttons */}
                    {getFilteredReports().length > 0 && (
                      <div className="mb-4 flex justify-end gap-1.5">
                        <Button 
                          onClick={async () => {
                            try {
                              const response = await fetch(`${API}/reports/export-excel`, {
                                method: 'GET',
                                headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
                              });
                              if (!response.ok) throw new Error('Errore export');
                              const blob = await response.blob();
                              const url = window.URL.createObjectURL(blob);
                              const link = document.createElement('a');
                              link.href = url;
                              link.download = `Report_${new Date().toISOString().split('T')[0]}.xlsx`;
                              document.body.appendChild(link);
                              link.click();
                              document.body.removeChild(link);
                              window.URL.revokeObjectURL(url);
                            } catch (error) { alert('Errore export Excel'); }
                          }}
                          variant="outline" size="sm"
                          className="h-8 gap-1.5 text-xs text-gray-500 border-gray-200 hover:text-emerald-600 hover:bg-emerald-50"
                        >
                          <FileText className="w-3.5 h-3.5" />
                          Excel
                        </Button>
                        <Button 
                          onClick={exportMultipleReportsPDF}
                          variant="outline" size="sm"
                          className="h-8 gap-1.5 text-xs text-gray-500 border-gray-200 hover:text-red-600 hover:bg-red-50"
                        >
                          <FileText className="w-3.5 h-3.5" />
                          {getFilteredReports().length} Report (ZIP)
                        </Button>
                      </div>
                    )}
                    
                  <div className="space-y-4">
                    {getFilteredReports().map((report) => {
                      const isExpanded = expandedReports.includes(report.id);
                      const totalAccessi = getTotalAccessiForReport(report);
                      const totalMinuti = calculateMinutesForReport(report);
                      const monthsIt = ['Gen', 'Feb', 'Mar', 'Apr', 'Mag', 'Giu', 'Lug', 'Ago', 'Set', 'Ott', 'Nov', 'Dic'];
                      
                      return (
                        <div 
                          key={report.id} 
                          className="border rounded-lg overflow-hidden hover:shadow-md transition-shadow"
                        >
                          {/* Header - Always Visible */}
                          <div 
                            className="p-3 sm:p-4 bg-gradient-to-r from-gray-50 to-gray-100 cursor-pointer hover:from-gray-100 hover:to-gray-150 transition-all border-b"
                            onClick={() => toggleReportExpansion(report.id)}
                          >
                            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                              <div className="flex-1 min-w-0">
                                <div className="flex flex-col gap-2">
                                  <div className="flex-1 min-w-0">
                                    {/* Nome e Badge */}
                                    <div className="flex items-center gap-2 flex-wrap mb-2">
                                      <h3 className="font-bold text-lg text-gray-900">{report.full_name}</h3>
                                      {report.status === 'approved' && (
                                        <div className="flex flex-col gap-1">
                                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-green-100 text-green-800 border border-green-200">
                                            <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                            </svg>
                                            Approvato
                                          </span>
                                          {report.approved_at && (
                                            <span className="text-xs text-gray-500">
                                              {new Date(report.approved_at).toLocaleString('it-IT', {
                                                day: '2-digit',
                                                month: '2-digit',
                                                year: 'numeric',
                                                hour: '2-digit',
                                                minute: '2-digit'
                                              })}
                                            </span>
                                          )}
                                        </div>
                                      )}
                                      {report.status === 'rejected' && (
                                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-red-100 text-red-800 border border-red-200">
                                          <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                                          </svg>
                                          Rifiutato
                                        </span>
                                      )}
                                      {report.status === 'pending' && (
                                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-yellow-100 text-yellow-800 border border-yellow-200">
                                          <svg className="w-3 h-3 mr-1 animate-pulse" fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                                          </svg>
                                          In Attesa
                                        </span>
                                      )}
                                    </div>
                                    {/* Info ruolo */}
                                    <p className="text-sm text-gray-600 mb-1 flex items-center gap-1.5">
                                      <svg className="w-4 h-4 text-cyan-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                      </svg>
                                      {report.figura_professionale} - {report.asl_riferimento}
                                    </p>
                                    {/* Motivo rifiuto */}
                                    {report.rejection_reason && (
                                      <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded-md inline-block">
                                        <p className="text-xs font-medium text-red-800">
                                          <span className="font-semibold">Motivo:</span> {report.rejection_reason}
                                        </p>
                                      </div>
                                    )}
                                  </div>
                                  {/* Stats cards */}
                                  <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-3 mt-3 sm:mt-0">
                                    <div className="text-center px-2 sm:px-3 py-1.5 sm:py-2 bg-white rounded-lg border border-gray-200 shadow-sm min-w-[80px]">
                                      <div className="text-xs text-gray-500 mb-0.5">Periodo</div>
                                      <div className="font-semibold text-gray-900 text-sm">{monthsIt[report.month - 1]} {report.year}</div>
                                    </div>
                                    <div className="text-center px-2 sm:px-3 py-1.5 sm:py-2 bg-gradient-to-br from-cyan-50 to-cyan-100 rounded-lg border border-cyan-200 shadow-sm min-w-[80px]">
                                      <div className="text-xs text-cyan-700 mb-0.5">Accessi</div>
                                      <div className="font-bold text-cyan-700 text-base sm:text-lg">{totalAccessi}</div>
                                    </div>
                                    <div className="text-center px-2 sm:px-3 py-1.5 sm:py-2 bg-gradient-to-br from-yellow-50 to-yellow-100 rounded-lg border border-yellow-200 shadow-sm min-w-[80px]">
                                      <div className="text-xs text-yellow-700 mb-0.5">Ore</div>
                                      <div className="font-bold text-yellow-700 text-base sm:text-lg">{(totalMinuti / 60).toFixed(1)}h</div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                              {/* Expand icon */}
                              <div className="ml-2 sm:ml-4 text-gray-400 shrink-0">
                                {isExpanded ? (
                                  <svg className="w-6 h-6 transform transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                                  </svg>
                                ) : (
                                  <svg className="w-6 h-6 transform transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                  </svg>
                                )}
                              </div>
                            </div>
                          </div>

                          {/* Dettagli Espandibili */}
                          {isExpanded && (
                            <div className="p-6 bg-white border-t space-y-6">
                              {/* Sezione Accessi */}
                              <div>
                                <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2 text-sm sm:text-base">
                                  <Activity className="w-4 h-4 sm:w-5 sm:h-5 text-cyan-500" />
                                  Dettaglio Accessi
                                </h4>
                                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-3">
                                  {report.accessi_data && Object.keys(report.accessi_data).length > 0 ? (
                                    Object.entries(report.accessi_data).map(([tipo, count]) => {
                                      const minuti = getMinutesForAccess(report.figura_professionale, tipo, count);
                                      const minutiSpostamento = count * (settings.tempo_spostamento || 10);
                                      const minutiTotali = minuti + minutiSpostamento;
                                      
                                      return (
                                        <div key={tipo} className="bg-cyan-50 rounded-lg p-2 sm:p-3 border border-cyan-200">
                                          <div className="text-xs text-gray-600 uppercase font-medium break-words">{tipo}</div>
                                          <div className="text-xl sm:text-2xl font-bold text-cyan-600">{count}</div>
                                          <div className="text-xs text-gray-500 mt-1">
                                            {minutiTotali} min<br className="sm:hidden" /><span className="hidden sm:inline"> </span>({(minutiTotali / 60).toFixed(1)}h)
                                          </div>
                                        </div>
                                      );
                                    })
                                  ) : (
                                    <div className="col-span-full text-center text-gray-500 py-4 text-sm">
                                      Nessun accesso registrato
                                    </div>
                                  )}
                                </div>
                                <div className="mt-3 pt-3 border-t flex flex-col sm:flex-row sm:justify-between text-xs sm:text-sm gap-1">
                                  <span className="font-medium text-gray-700">Totale Accessi:</span>
                                  <span className="font-bold text-cyan-600">{totalAccessi} accessi - {totalMinuti} min ({(totalMinuti / 60).toFixed(2)} ore)</span>
                                </div>
                              </div>

                              {/* Sezione Assenze */}
                              <div>
                                <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2 text-sm sm:text-base">
                                  <Calendar className="w-4 h-4 sm:w-5 sm:h-5 text-yellow-500" />
                                  Assenze e Note
                                </h4>
                                <div className="space-y-2 sm:space-y-3">
                                  {report.assenze && Object.keys(report.assenze).length > 0 ? (
                                    <>
                                      {/* Malattia */}
                                      {report.assenze.malattia && report.assenze.malattia.length > 0 && (
                                        <div className="bg-red-50 rounded-lg p-2 sm:p-3 border border-red-200">
                                          <div className="font-medium text-red-900 text-xs sm:text-sm mb-1 sm:mb-2">Malattia</div>
                                          <div className="text-xs sm:text-sm text-gray-700">
                                            {report.assenze.malattia.map((periodo, idx) => (
                                              <div key={idx} className="break-words">dal {periodo.dal} al {periodo.al}</div>
                                            ))}
                                          </div>
                                        </div>
                                      )}
                                      
                                      {/* Ferie */}
                                      {report.assenze.ferie && report.assenze.ferie.length > 0 && (
                                        <div className="bg-blue-50 rounded-lg p-2 sm:p-3 border border-blue-200">
                                          <div className="font-medium text-blue-900 text-xs sm:text-sm mb-1 sm:mb-2">Ferie</div>
                                          <div className="text-xs sm:text-sm text-gray-700">
                                            {report.assenze.ferie.map((periodo, idx) => (
                                              <div key={idx} className="break-words">dal {periodo.dal} al {periodo.al}</div>
                                            ))}
                                          </div>
                                        </div>
                                      )}
                                      
                                      {/* Permesso Ore */}
                                      {report.assenze.permesso_ore && (
                                        <div className="bg-yellow-50 rounded-lg p-2 sm:p-3 border border-yellow-200">
                                          <div className="font-medium text-yellow-900 text-xs sm:text-sm mb-1 sm:mb-2">Permesso</div>
                                          <div className="text-xs sm:text-sm text-gray-700">
                                            {Array.isArray(report.assenze.permesso_ore) ? (
                                              <>
                                                {report.assenze.permesso_ore.map((p, idx) => (
                                                  <div key={idx} className="break-words">{p.data}: {p.ore}h</div>
                                                ))}
                                                <div className="font-medium mt-1 sm:mt-2">
                                                  Totale: {report.assenze.permesso_ore.reduce((acc, p) => acc + (p.ore || 0), 0)}h
                                                </div>
                                              </>
                                            ) : (
                                              <div>{report.assenze.permesso_ore}h</div>
                                            )}
                                          </div>
                                        </div>
                                      )}
                                      
                                      {/* Permesso L104 */}
                                      {report.assenze.permesso_l104_ore && (
                                        <div className="bg-purple-50 rounded-lg p-2 sm:p-3 border border-purple-200">
                                          <div className="font-medium text-purple-900 text-xs sm:text-sm mb-1 sm:mb-2">Permesso L104</div>
                                          <div className="text-xs sm:text-sm text-gray-700">
                                            {Array.isArray(report.assenze.permesso_l104_ore) ? (
                                              <>
                                                {report.assenze.permesso_l104_ore.map((p, idx) => (
                                                  <div key={idx} className="break-words">{p.data}: {p.ore}h</div>
                                                ))}
                                                <div className="font-medium mt-1 sm:mt-2">
                                                  Totale: {report.assenze.permesso_l104_ore.reduce((acc, p) => acc + (p.ore || 0), 0)}h
                                                </div>
                                              </>
                                            ) : (
                                              <div>{report.assenze.permesso_l104_ore}h</div>
                                            )}
                                          </div>
                                        </div>
                                      )}
                                      
                                      {/* Recupero Ore */}
                                      {report.assenze.recupero_ore && (
                                        <div className="bg-green-50 rounded-lg p-2 sm:p-3 border border-green-200">
                                          <div className="font-medium text-green-900 text-xs sm:text-sm mb-1 sm:mb-2">Recupero Ore</div>
                                          <div className="text-xs sm:text-sm text-gray-700">
                                            {Array.isArray(report.assenze.recupero_ore) ? (
                                              <>
                                                {report.assenze.recupero_ore.map((r, idx) => (
                                                  <div key={idx} className="break-words">{r.data}: {r.ore}h</div>
                                                ))}
                                                <div className="font-medium mt-1 sm:mt-2">
                                                  Totale: {report.assenze.recupero_ore.reduce((acc, r) => acc + (r.ore || 0), 0)}h
                                                </div>
                                              </>
                                            ) : (
                                              <div>{report.assenze.recupero_ore}h</div>
                                            )}
                                          </div>
                                        </div>
                                      )}
                                      
                                      {/* Pronta Disponibilità */}
                                      {report.assenze.pronta_disponibilita_ore && (
                                        <div className="bg-indigo-50 rounded-lg p-2 sm:p-3 border border-indigo-200">
                                          <div className="font-medium text-indigo-900 text-xs sm:text-sm mb-1 sm:mb-2">Pronta Disponibilità</div>
                                          <div className="text-xs sm:text-sm text-gray-700">
                                            {Array.isArray(report.assenze.pronta_disponibilita_ore) ? (
                                              <>
                                                {report.assenze.pronta_disponibilita_ore.map((p, idx) => (
                                                  <div key={idx} className="break-words">{p.data}: {p.ore}h</div>
                                                ))}
                                                <div className="font-medium mt-1 sm:mt-2">
                                                  Totale: {report.assenze.pronta_disponibilita_ore.reduce((acc, p) => acc + (p.ore || 0), 0)}h
                                                </div>
                                              </>
                                            ) : (
                                              <div>{report.assenze.pronta_disponibilita_ore}h</div>
                                            )}
                                          </div>
                                        </div>
                                      )}
                                      
                                      {/* Altri tipi */}
                                      {report.assenze.donazione_sangue && report.assenze.donazione_sangue.length > 0 && (
                                        <div className="bg-gray-50 rounded-lg p-2 sm:p-3 border border-gray-200">
                                          <div className="font-medium text-gray-900 text-xs sm:text-sm mb-1 sm:mb-2">Donazione Sangue</div>
                                          <div className="text-xs sm:text-sm text-gray-700 break-words">
                                            {report.assenze.donazione_sangue.join(', ')}
                                          </div>
                                        </div>
                                      )}
                                      
                                      {report.assenze.festivo_lavorato && report.assenze.festivo_lavorato.length > 0 && (
                                        <div className="bg-gray-50 rounded-lg p-2 sm:p-3 border border-gray-200">
                                          <div className="font-medium text-gray-900 text-xs sm:text-sm mb-1 sm:mb-2">Festivi Lavorati</div>
                                          <div className="text-xs sm:text-sm text-gray-700 break-words">
                                            {report.assenze.festivo_lavorato.join(', ')}
                                          </div>
                                        </div>
                                      )}
                                      
                                      {report.assenze.riposo_settimanale && report.assenze.riposo_settimanale.length > 0 && (
                                        <div className="bg-gray-50 rounded-lg p-2 sm:p-3 border border-gray-200">
                                          <div className="font-medium text-gray-900 text-xs sm:text-sm mb-1 sm:mb-2">Riposi Settimanali</div>
                                          <div className="text-xs sm:text-sm text-gray-700 break-words">
                                            {report.assenze.riposo_settimanale.join(', ')}
                                          </div>
                                        </div>
                                      )}
                                    </>
                                  ) : (
                                    <div className="text-center text-gray-500 py-4 text-sm">
                                      Nessuna assenza registrata
                                    </div>
                                  )}
                                </div>
                              </div>

                              {/* Note Turni */}
                              {report.note_turni && (
                                <div>
                                  <h4 className="font-semibold text-gray-900 mb-2 flex items-center gap-2 text-sm sm:text-base">
                                    <FileText className="w-4 h-4 sm:w-5 sm:h-5 text-gray-500" />
                                    Note Turni Aggiuntivi
                                  </h4>
                                  <div className="bg-gray-50 rounded-lg p-2 sm:p-3 border border-gray-200">
                                    <p className="text-xs sm:text-sm text-gray-700 whitespace-pre-wrap break-words">{report.note_turni}</p>
                                  </div>
                                </div>
                              )}

                              {/* Chilometri auto */}
                              {(report.km_tratte || (report.km_totali && report.km_totali > 0)) && (
                                <div>
                                  <h4 className="font-semibold text-gray-900 mb-2 flex items-center gap-2 text-sm sm:text-base">
                                    <svg className="w-4 h-4 sm:w-5 sm:h-5 text-purple-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
                                    </svg>
                                    Chilometri auto
                                  </h4>
                                  <p className="text-xs text-gray-600 italic mb-2">Da compilare solo se in possesso di auto aziendale</p>
                                  <div className="space-y-2 sm:space-y-3">
                                    {report.km_tratte && (
                                      <div className="bg-purple-50 rounded-lg p-2 sm:p-3 border border-purple-200">
                                        <div className="font-medium text-purple-900 text-xs sm:text-sm mb-1 sm:mb-2">Targa auto</div>
                                        <p className="text-xs sm:text-sm text-gray-700 whitespace-pre-wrap break-words">{report.km_tratte}</p>
                                      </div>
                                    )}
                                    {report.km_totali && report.km_totali > 0 && (
                                      <div className="bg-purple-50 rounded-lg p-2 sm:p-3 border border-purple-200">
                                        <div className="font-medium text-purple-900 text-xs sm:text-sm mb-1 sm:mb-2">km auto</div>
                                        <p className="text-lg sm:text-2xl font-bold text-purple-600">{report.km_totali} km</p>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              )}

                              {/* Report Management Actions */}
                              {(user.role === 'amministratore' || user.role === 'super_amministratore') && (
                                <div className="mt-6 pt-4 border-t space-y-3">
                                  <div className="flex flex-wrap gap-2">
                                    <Button 
                                      onClick={() => exportSingleReportPDF(report)}
                                      size="sm"
                                      variant="outline"
                                      className="gap-2 text-red-600 border-red-300 hover:bg-red-50"
                                    >
                                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                      </svg>
                                      Esporta PDF
                                    </Button>
                                  </div>
                                  <div className="flex flex-wrap gap-2">
                                    <Button 
                                      onClick={() => {
                                        setSelectedReport(report);
                                        setShowApproveDialog(true);
                                      }}
                                      size="sm"
                                      className="gap-2 bg-green-600 hover:bg-green-700 text-white"
                                    >
                                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                      </svg>
                                      Approva Report
                                    </Button>
                                    <Button 
                                      onClick={() => {
                                        setSelectedReport(report);
                                        setShowRejectDialog(true);
                                      }}
                                      size="sm"
                                      variant="outline"
                                      className="gap-2 text-yellow-600 border-yellow-300 hover:bg-yellow-50"
                                    >
                                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                      </svg>
                                      Rifiuta Report
                                    </Button>
                                    <Button 
                                      onClick={() => {
                                        setSelectedReport(report);
                                        setShowDeleteReportDialog(true);
                                      }}
                                      size="sm"
                                      variant="outline"
                                      className="gap-2 text-red-600 border-red-300 hover:bg-red-50"
                                    >
                                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                      </svg>
                                      Elimina Report
                                    </Button>
                                  </div>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  </>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Summary Tab */}
          <TabsContent value="summary" className="space-y-4">
            <Card className="shadow-lg border-0">
              <CardHeader>
                <CardTitle>Riepilogo per Operatore</CardTitle>
                <CardDescription>Tempo totale in minuti per ogni operatore</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Operatore</TableHead>
                        <TableHead>Figura Professionale</TableHead>
                        <TableHead>ASL</TableHead>
                        <TableHead className="text-center">Totale Accessi</TableHead>
                        <TableHead className="text-center">Totale Minuti</TableHead>
                        <TableHead className="text-center">Totale Ore</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {Object.values(reportsByUser).map((userData, idx) => (
                        <TableRow key={idx}>
                          <TableCell className="font-medium">{userData.full_name}</TableCell>
                          <TableCell>{userData.figura_professionale}</TableCell>
                          <TableCell>{userData.asl_riferimento}</TableCell>
                          <TableCell className="text-center">{userData.totalAccessi}</TableCell>
                          <TableCell className="text-center font-semibold">{userData.totalMinutes}</TableCell>
                          <TableCell className="text-center">{(userData.totalMinutes / 60).toFixed(2)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Users Tab */}
          <TabsContent value="users" className="space-y-4">
            {/* Utenti in Attesa di Approvazione */}
            {pendingUsers.length > 0 && (user.role === 'amministratore' || user.role === 'super_amministratore') && (
              <Card className="shadow-sm border border-amber-200 bg-amber-50/30" data-testid="pending-users-card">
                <CardHeader className="pb-3">
                  <CardTitle className="flex items-center gap-2 text-base">
                    <Clock className="w-4 h-4 text-amber-600" />
                    In Attesa di Approvazione
                    <span className="ml-1 bg-amber-100 text-amber-700 text-xs font-bold rounded-full h-5 min-w-[20px] flex items-center justify-center px-1.5">{pendingUsers.length}</span>
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-0">
                  <div className="space-y-2">
                    {pendingUsers.map((pendingUser) => (
                      <div key={pendingUser.id} className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 p-3 bg-white rounded-lg border border-gray-100" data-testid={`pending-user-${pendingUser.id}`}>
                        <div className="min-w-0">
                          <div className="font-medium text-sm text-gray-900">{pendingUser.full_name}</div>
                          <div className="text-xs text-gray-500">@{pendingUser.username} &middot; {pendingUser.figura_professionale} &middot; {pendingUser.asl_riferimento}</div>
                        </div>
                        <div className="flex gap-1.5 shrink-0">
                          <Button
                            onClick={() => handleApproveUser(pendingUser.id)}
                            size="sm"
                            className="h-8 gap-1.5 text-xs bg-emerald-600 hover:bg-emerald-700 text-white flex-1 sm:flex-none"
                          >
                            <CheckCircle className="w-3.5 h-3.5" />
                            Approva
                          </Button>
                          <Button
                            onClick={() => handleRejectUser(pendingUser.id)}
                            size="sm"
                            variant="outline"
                            className="h-8 gap-1.5 text-xs text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200 flex-1 sm:flex-none"
                          >
                            <X className="w-3.5 h-3.5" />
                            Rifiuta
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            <Card className="shadow-sm border-0" data-testid="users-list-card">
              <CardHeader className="pb-3">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  <div>
                    <CardTitle className="text-lg">Gestione Utenti</CardTitle>
                    <CardDescription className="text-xs">{users.length} utenti registrati</CardDescription>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    {(user.role === 'super_amministratore' || user.role === 'amministratore') && (
                      <Button
                        type="button"
                        data-testid="btn-open-organigramma"
                        variant="outline"
                        size="sm"
                        onClick={() => setShowOrganigramma(true)}
                        className="h-9 gap-1.5 text-xs border-cyan-300 text-cyan-700 hover:bg-cyan-50"
                      >
                        <Network className="w-3.5 h-3.5" />
                        <span className="hidden sm:inline">Organigramma & Qualifiche</span>
                        <span className="sm:hidden">Organigramma</span>
                      </Button>
                    )}
                    {user.role === 'super_amministratore' && (
                      <Button
                        type="button"
                        data-testid="btn-open-hr-import"
                        variant="outline"
                        size="sm"
                        onClick={() => setShowHRImport(true)}
                        className="h-9 gap-1.5 text-xs border-violet-300 text-violet-700 hover:bg-violet-50"
                      >
                        <FileSpreadsheet className="w-3.5 h-3.5" />
                        <span className="hidden sm:inline">Importa Dati HR</span>
                        <span className="sm:hidden">Import HR</span>
                      </Button>
                    )}
                    <Dialog open={showCreateUser} onOpenChange={setShowCreateUser}>
                      <DialogTrigger asChild>
                        <Button data-testid="create-user-button" size="sm" className="bg-cyan-600 hover:bg-cyan-700 text-white gap-1.5 h-9">
                          <Plus className="w-4 h-4" />
                          <span className="hidden sm:inline">Nuovo Utente</span>
                          <span className="sm:hidden">Nuovo</span>
                        </Button>
                      </DialogTrigger>
                <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle>Crea Nuovo Utente</DialogTitle>
                    <DialogDescription>Aggiungi un nuovo operatore o amministratore</DialogDescription>
                  </DialogHeader>
                  <form onSubmit={handleCreateUser} className="space-y-4 mt-4">
                    <div className="space-y-2">
                      <Label>Username</Label>
                      <Input
                        data-testid="new-user-username-input"
                        value={newUser.username}
                        onChange={(e) => setNewUser({...newUser, username: e.target.value})}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Password</Label>
                      <Input
                        data-testid="new-user-password-input"
                        type="password"
                        value={newUser.password}
                        onChange={(e) => setNewUser({...newUser, password: e.target.value})}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Nome</Label>
                      <Input
                        data-testid="new-user-firstname-input"
                        value={newUser.first_name}
                        onChange={(e) => setNewUser({...newUser, first_name: e.target.value})}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Cognome</Label>
                      <Input
                        data-testid="new-user-lastname-input"
                        value={newUser.last_name}
                        onChange={(e) => setNewUser({...newUser, last_name: e.target.value})}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Email *</Label>
                      <Input
                        type="email"
                        placeholder="esempio@email.com"
                        value={newUser.email || ''}
                        onChange={(e) => setNewUser({...newUser, email: e.target.value})}
                        required
                      />
                      <p className="text-xs text-gray-500">Email per ricevere notifiche</p>
                    </div>
                    <div className="space-y-2">
                      <Label>Codice Fiscale *</Label>
                      <Input
                        placeholder="RSSMRA85M01H501Z"
                        value={newUser.codice_fiscale || ''}
                        onChange={(e) => {
                          const cf = e.target.value.toUpperCase().trim();
                          setNewUser({...newUser, codice_fiscale: cf});
                        }}
                        maxLength={16}
                        required
                        pattern="[A-Z]{6}\d{2}[A-Z]\d{2}[A-Z]\d{3}[A-Z]"
                        title="Formato: 6 lettere, 2 cifre, 1 lettera, 2 cifre, 1 lettera, 3 cifre, 1 lettera"
                      />
                      <p className="text-xs text-gray-500">16 caratteri alfanumerici (es. RSSMRA85M01H501Z)</p>
                    </div>
                    <div className="space-y-2">
                      <Label>Ruolo</Label>
                      <select
                        data-testid="new-user-role-select"
                        value={newUser.role}
                        onChange={(e) => setNewUser({...newUser, role: e.target.value})}
                        className="w-full h-10 px-3 rounded-md border border-gray-300 bg-white"
                        required
                      >
                        <option value="operatore">Operatore</option>
                        <option value="amministratore">Amministratore</option>
                        {user.role === 'super_amministratore' && (
                          <option value="super_amministratore">Super Amministratore</option>
                        )}
                      </select>
                    </div>
                    {newUser.role === 'operatore' && settings && (
                      <>
                        <div className="space-y-2">
                          <Label>Figura Professionale</Label>
                          <select
                            data-testid="new-user-figura-select"
                            value={newUser.figura_professionale}
                            onChange={(e) => setNewUser({...newUser, figura_professionale: e.target.value})}
                            className="w-full h-10 px-3 rounded-md border border-gray-300 bg-white"
                            required
                          >
                            {(settings.figure_professionali || []).map(fig => (
                              <option key={fig.id} value={fig.nome}>{fig.nome}</option>
                            ))}
                          </select>
                        </div>
                        <div className="space-y-2">
                          <Label>ASL di Riferimento</Label>
                          <select
                            data-testid="new-user-asl-select"
                            value={newUser.asl_riferimento}
                            onChange={(e) => setNewUser({...newUser, asl_riferimento: e.target.value})}
                            className="w-full h-10 px-3 rounded-md border border-gray-300 bg-white"
                            required
                          >
                            <option value="Asl Pescara">Asl Pescara</option>
                            <option value="Asl Chieti">Asl Chieti</option>
                            <option value="Asl L'Aquila">Asl L'Aquila</option>
                            <option value="Asl Teramo">Asl Teramo</option>
                          </select>
                        </div>
                        <div className="space-y-2">
                          <Label className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              checked={newUser.tipo_contratto === 'libero_professionista'}
                              onChange={(e) => setNewUser({
                                ...newUser,
                                tipo_contratto: e.target.checked ? 'libero_professionista' : 'dipendente'
                              })}
                              className="w-4 h-4 text-cyan-600 rounded border-gray-300 focus:ring-cyan-500"
                            />
                            <span>Libero Professionista</span>
                          </Label>
                          <p className="text-xs text-gray-500">
                            I liberi professionisti ricevono fatture invece di report mensili
                          </p>
                        </div>
                        <div className="space-y-2">
                          <Label className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              checked={newUser.is_coordinatore || false}
                              onChange={(e) => setNewUser({
                                ...newUser,
                                is_coordinatore: e.target.checked
                              })}
                              className="w-4 h-4 text-purple-600 rounded border-gray-300 focus:ring-purple-500"
                            />
                            <span>Abilita funzioni da Coordinatore</span>
                          </Label>
                          <p className="text-xs text-gray-500">
                            Il coordinatore può gestire i turni del proprio team
                          </p>
                        </div>
                        
                        <div className="space-y-2">
                          <Label className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              checked={newUser.has_planning_access || false}
                              onChange={(e) => setNewUser({
                                ...newUser,
                                has_planning_access: e.target.checked,
                                planning_role: e.target.checked ? 'viewer' : null
                              })}
                              className="w-4 h-4 text-cyan-600 rounded border-gray-300 focus:ring-cyan-500"
                            />
                            <span>Abilita Planning Orario</span>
                          </Label>
                          <p className="text-xs text-gray-500">
                            L'operatore potrà visualizzare i propri turni nel Planning Orario
                          </p>
                        </div>
                        
                        {/* Abilita Preventivi */}
                        <div className="space-y-2 p-3 border-2 border-purple-200 rounded-lg bg-purple-50">
                          <Label className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              checked={newUser.has_preventivi_access || false}
                              onChange={(e) => setNewUser({
                                ...newUser,
                                has_preventivi_access: e.target.checked
                              })}
                              className="w-4 h-4 text-purple-600 rounded border-gray-300 focus:ring-purple-500"
                            />
                            <span>Abilita Preventivi</span>
                          </Label>
                          <p className="text-xs text-gray-500">
                            L'utente potrà creare e gestire preventivi (tariffe gestite solo da Super Admin)
                          </p>
                        </div>
                        
                        <div className="space-y-2 p-3 border-2 border-green-200 rounded-lg bg-green-50">
                          <Label className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              checked={newUser.privacy_consent || false}
                              onChange={(e) => setNewUser({
                                ...newUser,
                                privacy_consent: e.target.checked,
                                privacy_consent_date: e.target.checked ? new Date().toISOString() : null,
                                privacy_consent_version: e.target.checked ? '1.0' : null
                              })}
                              className="w-4 h-4 text-green-600 rounded border-gray-300 focus:ring-green-500"
                            />
                            <span className="font-semibold text-green-900">Consenso Privacy</span>
                          </Label>
                          <p className="text-xs text-gray-700">
                            Confermo che l'informativa privacy è stata fornita all'operatore offline e il consenso è stato raccolto
                          </p>
                        </div>
                      </>
                    )}
                    <Button
                      data-testid="submit-new-user-button"
                      type="submit"
                      className="w-full bg-cyan-600 hover:bg-cyan-700 text-white"
                    >
                      Crea Utente
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {/* Search + Filters */}
                <div className="flex flex-col gap-3 mb-4">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      placeholder="Cerca per nome, username, email..."
                      value={userSearch}
                      onChange={(e) => setUserSearch(e.target.value)}
                      className="pl-9 h-9 text-sm"
                      data-testid="user-search-input"
                    />
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <select
                      value={userFilterRole}
                      onChange={(e) => setUserFilterRole(e.target.value)}
                      data-testid="user-filter-role"
                      className="h-8 px-2 text-xs rounded-md border border-gray-200 bg-white text-gray-700"
                    >
                      <option value="">Tutti i ruoli</option>
                      <option value="operatore">Operatore</option>
                      <option value="amministratore">Admin</option>
                      <option value="super_amministratore">Super Admin</option>
                    </select>
                    <select
                      value={userFilterASL}
                      onChange={(e) => setUserFilterASL(e.target.value)}
                      data-testid="user-filter-asl"
                      className="h-8 px-2 text-xs rounded-md border border-gray-200 bg-white text-gray-700"
                    >
                      <option value="">Tutte le ASL</option>
                      {[...new Set(users.filter(u => u.asl_riferimento).map(u => u.asl_riferimento))].sort().map(asl => (
                        <option key={asl} value={asl}>{asl}</option>
                      ))}
                    </select>
                    <select
                      value={userFilterFigura}
                      onChange={(e) => setUserFilterFigura(e.target.value)}
                      data-testid="user-filter-figura"
                      className="h-8 px-2 text-xs rounded-md border border-gray-200 bg-white text-gray-700"
                    >
                      <option value="">Tutte le figure</option>
                      {[...new Set(users.filter(u => u.figura_professionale).map(u => u.figura_professionale))].sort().map(fig => (
                        <option key={fig} value={fig}>{fig}</option>
                      ))}
                    </select>
                    {(userSearch || userFilterRole || userFilterASL || userFilterFigura) && (
                      <button
                        onClick={() => { setUserSearch(''); setUserFilterRole(''); setUserFilterASL(''); setUserFilterFigura(''); }}
                        className="h-8 px-2 text-xs text-gray-500 hover:text-gray-700 underline"
                      >
                        Azzera filtri
                      </button>
                    )}
                  </div>
                </div>

                {/* User list */}
                <div className="space-y-2">
                  {users
                    .filter(u => {
                      const search = userSearch.toLowerCase();
                      const matchSearch = !search || 
                        (u.full_name || '').toLowerCase().includes(search) ||
                        (u.username || '').toLowerCase().includes(search) ||
                        (u.email || '').toLowerCase().includes(search);
                      const matchRole = !userFilterRole || u.role === userFilterRole;
                      const matchASL = !userFilterASL || u.asl_riferimento === userFilterASL;
                      const matchFigura = !userFilterFigura || u.figura_professionale === userFilterFigura;
                      return matchSearch && matchRole && matchASL && matchFigura;
                    })
                    .map((u) => {
                      const teamSize = users.filter(op => op.coordinatore_id === u.id).length;
                      const coordinator = users.find(coord => coord.id === u.coordinatore_id);
                      
                      return (
                        <div key={u.id} className="flex items-center gap-3 p-3 rounded-lg border border-gray-100 hover:border-gray-200 hover:bg-gray-50/50 transition-all group" data-testid={`user-row-${u.id}`}>
                          {/* Avatar */}
                          <div className={`h-10 w-10 rounded-full flex items-center justify-center text-sm font-bold shrink-0 ${
                            u.role === 'super_amministratore' ? 'bg-cyan-100 text-cyan-700' :
                            u.role === 'amministratore' ? 'bg-violet-100 text-violet-700' :
                            'bg-gray-100 text-gray-600'
                          }`}>
                            {(u.first_name?.[0] || '').toUpperCase()}{(u.last_name?.[0] || '').toUpperCase()}
                          </div>
                          
                          {/* Info */}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="font-medium text-sm text-gray-900">{u.full_name}</span>
                              <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium ${
                                u.role === 'amministratore' ? 'bg-violet-100 text-violet-700' :
                                u.role === 'super_amministratore' ? 'bg-cyan-100 text-cyan-700' :
                                'bg-gray-100 text-gray-500'
                              }`}>
                                {u.role === 'super_amministratore' ? 'Super Admin' : u.role === 'amministratore' ? 'Admin' : 'Operatore'}
                              </span>
                              {u.is_coordinatore && (
                                <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-amber-100 text-amber-700">Coordinatore</span>
                              )}
                              {u.tipo_contratto === 'libero_professionista' && (
                                <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-teal-100 text-teal-700">P.IVA</span>
                              )}
                              {(u.qualifiche || []).slice(0, 3).map((q, qi) => (
                                <span key={qi} className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-amber-50 text-amber-800 border border-amber-200" data-testid={`user-qualifica-${u.id}-${qi}`}>
                                  {q}
                                </span>
                              ))}
                            </div>
                            <div className="text-xs text-gray-500 mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0">
                              <span>@{u.username}</span>
                              {u.email && <span className="hidden sm:inline">{u.email}</span>}
                              {u.figura_professionale && <span>{u.figura_professionale}</span>}
                              {u.asl_riferimento && <span>{u.asl_riferimento}</span>}
                              {coordinator && <span className="text-cyan-600">Coord: {coordinator.full_name}</span>}
                              {u.is_coordinatore && teamSize > 0 && <span className="text-amber-600">{teamSize} nel team</span>}
                              {u.has_planning_access && <span className="text-violet-500">Planning</span>}
                              {u.has_preventivi_access && <span className="text-violet-500">Preventivi</span>}
                              {u.has_procedure_editor_access && <span className="text-cyan-600 font-medium">Redattore</span>}
                            </div>
                          </div>
                          
                          {/* Actions */}
                          {user && user.role === 'super_amministratore' && (
                            <div className="flex gap-1 shrink-0 opacity-60 group-hover:opacity-100 transition-opacity">
                              <Button onClick={() => handleEditUserClick(u)} variant="ghost" size="sm" className="h-8 w-8 p-0 hover:bg-gray-100" title="Modifica">
                                <Edit className="h-3.5 w-3.5 text-gray-500" />
                              </Button>
                              <Button onClick={() => handleDeleteUserClick(u)} variant="ghost" size="sm" className="h-8 w-8 p-0 text-red-400 hover:text-red-600 hover:bg-red-50" title="Elimina">
                                <Trash2 className="h-3.5 w-3.5" />
                              </Button>
                            </div>
                          )}
                        </div>
                      );
                    })}
                    {users.filter(u => {
                      const search = userSearch.toLowerCase();
                      return (!search || (u.full_name || '').toLowerCase().includes(search) || (u.username || '').toLowerCase().includes(search) || (u.email || '').toLowerCase().includes(search))
                        && (!userFilterRole || u.role === userFilterRole)
                        && (!userFilterASL || u.asl_riferimento === userFilterASL)
                        && (!userFilterFigura || u.figura_professionale === userFilterFigura);
                    }).length === 0 && (
                      <div className="text-center py-8 text-gray-400 text-sm">Nessun utente trovato</div>
                    )}
                </div>
              </CardContent>
            </Card>

            {/* Dialog Modifica Utente */}
            <Dialog open={showEditUser} onOpenChange={setShowEditUser}>
              <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Modifica Utente</DialogTitle>
                  <DialogDescription>Aggiorna i dati dell'utente selezionato</DialogDescription>
                </DialogHeader>
                <form onSubmit={handleUpdateUser} className="space-y-4 mt-4">
                  <div className="space-y-2">
                    <Label>Username</Label>
                    <Input
                      value={editUser.username}
                      onChange={(e) => setEditUser({...editUser, username: e.target.value})}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Nome</Label>
                    <Input
                      value={editUser.first_name}
                      onChange={(e) => setEditUser({...editUser, first_name: e.target.value})}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Cognome</Label>
                    <Input
                      value={editUser.last_name}
                      onChange={(e) => setEditUser({...editUser, last_name: e.target.value})}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Email *</Label>
                    <Input
                      type="email"
                      placeholder="esempio@email.com"
                      value={editUser.email || ''}
                      onChange={(e) => setEditUser({...editUser, email: e.target.value})}
                      required
                    />
                    <p className="text-xs text-gray-500">Email per ricevere notifiche</p>
                  </div>
                  <div className="space-y-2">
                    <Label>Codice Fiscale {editUser.codice_fiscale ? '' : '(opzionale)'}</Label>
                    <Input
                      placeholder="RSSMRA85M01H501Z"
                      value={editUser.codice_fiscale || ''}
                      onChange={(e) => {
                        const cf = e.target.value.toUpperCase().trim();
                        setEditUser({...editUser, codice_fiscale: cf});
                      }}
                      maxLength={16}
                      pattern="[A-Z]{6}\d{2}[A-Z]\d{2}[A-Z]\d{3}[A-Z]"
                      title="Formato: 6 lettere, 2 cifre, 1 lettera, 2 cifre, 1 lettera, 3 cifre, 1 lettera"
                    />
                    <p className="text-xs text-gray-500">16 caratteri alfanumerici - Opzionale per utenti esistenti</p>
                  </div>
                  <div className="space-y-2">
                    <Label>Password (opzionale - lascia vuoto per non modificare)</Label>
                    <Input
                      type="password"
                      value={editUser.password || ''}
                      onChange={(e) => setEditUser({...editUser, password: e.target.value})}
                      placeholder="Nuova password"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Ruolo</Label>
                    <select
                      value={editUser.role}
                      onChange={(e) => setEditUser({...editUser, role: e.target.value})}
                      className="w-full h-10 px-3 rounded-md border border-gray-300 bg-white"
                      required
                    >
                      <option value="operatore">Operatore</option>
                      <option value="amministratore">Amministratore</option>
                      {user.role === 'super_amministratore' && (
                        <option value="super_amministratore">Super Amministratore</option>
                      )}
                    </select>
                  </div>
                  {editUser.role === 'operatore' && settings && (
                    <>
                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1.5">
                          <Label className="text-xs">Figura Professionale</Label>
                          <select
                            value={editUser.figura_professionale}
                            onChange={(e) => setEditUser({...editUser, figura_professionale: e.target.value})}
                            className="w-full h-9 px-3 text-sm rounded-md border border-gray-200 bg-white"
                            required
                          >
                            {(settings.figure_professionali || []).map(fig => (
                              <option key={fig.id} value={fig.nome}>{fig.nome}</option>
                            ))}
                          </select>
                        </div>
                        <div className="space-y-1.5">
                          <Label className="text-xs">ASL di Riferimento</Label>
                          <select
                            value={editUser.asl_riferimento}
                            onChange={(e) => setEditUser({...editUser, asl_riferimento: e.target.value})}
                            className="w-full h-9 px-3 text-sm rounded-md border border-gray-200 bg-white"
                            required
                          >
                            <option value="Asl Pescara">Asl Pescara</option>
                            <option value="Asl Chieti">Asl Chieti</option>
                            <option value="Asl L'Aquila">Asl L'Aquila</option>
                            <option value="Asl Teramo">Asl Teramo</option>
                          </select>
                        </div>
                      </div>

                      {/* Coordinatore assegnato */}
                      <div className="space-y-1.5">
                        <Label className="text-xs">Coordinatore assegnato</Label>
                        <select
                          value={editUser.coordinatore_id || ''}
                          onChange={async (e) => {
                            const newCoordId = e.target.value || null;
                            setEditUser({...editUser, coordinatore_id: newCoordId});
                            try {
                              const token = localStorage.getItem('token');
                              await axios.put(`${API}/users/${editUser.id}`, {
                                coordinatore_id: newCoordId
                              }, { headers: { Authorization: `Bearer ${token}` } });
                              const coordName = newCoordId ? users.find(u => u.id === newCoordId)?.full_name : null;
                              toast.success(newCoordId ? `Assegnato a ${coordName}` : 'Coordinatore rimosso');
                              fetchData();
                            } catch (error) {
                              toast.error('Errore aggiornamento coordinatore');
                            }
                          }}
                          className="w-full h-9 px-3 text-sm rounded-md border border-gray-200 bg-white"
                        >
                          <option value="">Nessun coordinatore</option>
                          {users.filter(u => u.is_coordinatore && u.id !== editUser.id).map(coord => (
                            <option key={coord.id} value={coord.id}>{coord.full_name}</option>
                          ))}
                        </select>
                      </div>

                      {/* Permessi */}
                      <div className="space-y-1.5">
                        <Label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Permessi</Label>
                        <div className="rounded-lg border border-gray-200 divide-y divide-gray-100">
                          <label className="flex items-center justify-between px-3 py-2.5 cursor-pointer hover:bg-gray-50">
                            <div>
                              <span className="text-sm font-medium text-gray-700">Libero Professionista</span>
                              <p className="text-[11px] text-gray-400">Riceve fatture invece di report</p>
                            </div>
                            <input
                              type="checkbox"
                              checked={editUser.tipo_contratto === 'libero_professionista'}
                              onChange={(e) => setEditUser({ ...editUser, tipo_contratto: e.target.checked ? 'libero_professionista' : 'dipendente' })}
                              className="w-4 h-4 text-cyan-600 rounded border-gray-300 focus:ring-cyan-500"
                            />
                          </label>
                          <label className="flex items-center justify-between px-3 py-2.5 cursor-pointer hover:bg-gray-50">
                            <div>
                              <span className="text-sm font-medium text-gray-700">Coordinatore</span>
                              <p className="text-[11px] text-gray-400">Gestisce turni del proprio team</p>
                            </div>
                            <input
                              type="checkbox"
                              checked={editUser.is_coordinatore || false}
                              onChange={(e) => setEditUser({ ...editUser, is_coordinatore: e.target.checked })}
                              className="w-4 h-4 text-cyan-600 rounded border-gray-300 focus:ring-cyan-500"
                            />
                          </label>
                          <label className="flex items-center justify-between px-3 py-2.5 cursor-pointer hover:bg-gray-50">
                            <div>
                              <span className="text-sm font-medium text-gray-700">Planning Orario</span>
                              <p className="text-[11px] text-gray-400">Visualizza turni nel planning</p>
                            </div>
                            <input
                              type="checkbox"
                              checked={editUser.has_planning_access || false}
                              onChange={(e) => setEditUser({ ...editUser, has_planning_access: e.target.checked, planning_role: e.target.checked ? 'viewer' : null })}
                              className="w-4 h-4 text-cyan-600 rounded border-gray-300 focus:ring-cyan-500"
                            />
                          </label>
                          <label className="flex items-center justify-between px-3 py-2.5 cursor-pointer hover:bg-gray-50">
                            <div>
                              <span className="text-sm font-medium text-gray-700">Preventivi</span>
                              <p className="text-[11px] text-gray-400">Crea e gestisce preventivi</p>
                            </div>
                            <input
                              type="checkbox"
                              checked={editUser.has_preventivi_access || false}
                              onChange={(e) => setEditUser({ ...editUser, has_preventivi_access: e.target.checked })}
                              className="w-4 h-4 text-cyan-600 rounded border-gray-300 focus:ring-cyan-500"
                            />
                          </label>
                          <label className="flex items-center justify-between px-3 py-2.5 cursor-pointer hover:bg-gray-50">
                            <div>
                              <span className="text-sm font-medium text-gray-700">Redattore Procedure</span>
                              <p className="text-[11px] text-gray-400">Può redigere procedure (richiede verifica di 3 approvatori)</p>
                            </div>
                            <input
                              type="checkbox"
                              checked={editUser.has_procedure_editor_access || false}
                              onChange={(e) => setEditUser({ ...editUser, has_procedure_editor_access: e.target.checked })}
                              className="w-4 h-4 text-cyan-600 rounded border-gray-300 focus:ring-cyan-500"
                              data-testid="edit-user-procedure-editor"
                            />
                          </label>
                          <label className="flex items-center justify-between px-3 py-2.5 cursor-pointer hover:bg-gray-50">
                            <div>
                              <span className="text-sm font-medium text-gray-700">Modulo Accreditamenti</span>
                              <p className="text-[11px] text-gray-400">Accesso al modulo Accreditamenti (gestione progetti e autocertificazioni)</p>
                            </div>
                            <input
                              type="checkbox"
                              checked={editUser.has_accreditamenti_access || false}
                              onChange={(e) => setEditUser({ ...editUser, has_accreditamenti_access: e.target.checked })}
                              className="w-4 h-4 text-cyan-600 rounded border-gray-300 focus:ring-cyan-500"
                              data-testid="edit-user-accreditamenti"
                            />
                          </label>
                        </div>
                      </div>
                    </>
                  )}

                  {/* === Sezioni disponibili per TUTTI i ruoli (Qualifiche + Organigramma) === */}
                  {settings && (
                    <>
                      {/* Qualifiche / Titoli organizzativi - max 3 */}
                      <div className="space-y-2 p-4 bg-amber-50 rounded-lg border border-amber-200">
                        <div className="flex items-start gap-3">
                          <div className="h-8 w-8 rounded-full bg-amber-100 flex items-center justify-center shrink-0">
                            <svg className="w-4 h-4 text-amber-700" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" /></svg>
                          </div>
                          <div className="flex-1">
                            <Label className="text-sm font-semibold text-amber-900">Qualifiche / Titoli</Label>
                            <p className="text-[11px] text-amber-700 mt-0.5">
                              Massimo 3 qualifiche organizzative (es. "Direttore Sanitario", "RSPP"). Visibili a tutti gli utenti se presenti, e usate per pre-compilare il "Ruolo / Titolo" dei verificatori procedure.
                            </p>
                          </div>
                        </div>

                        {/* Tag chips delle qualifiche selezionate */}
                        {(editUser.qualifiche?.length || 0) > 0 && (
                          <div className="flex flex-wrap gap-1.5 pt-1" data-testid="qualifiche-chips">
                            {editUser.qualifiche.map((q, idx) => (
                              <span key={idx} className="inline-flex items-center gap-1 px-2.5 py-1 bg-white border border-amber-300 rounded-full text-xs font-medium text-amber-900">
                                {q}
                                <button
                                  type="button"
                                  onClick={() => {
                                    const next = editUser.qualifiche.filter((_, i) => i !== idx);
                                    setEditUser({ ...editUser, qualifiche: next });
                                  }}
                                  className="ml-0.5 text-amber-500 hover:text-amber-700"
                                  data-testid={`qualifica-remove-${idx}`}
                                  title="Rimuovi"
                                >
                                  ×
                                </button>
                              </span>
                            ))}
                          </div>
                        )}

                        {/* Selettore: dropdown master + input custom */}
                        {(editUser.qualifiche?.length || 0) < 3 ? (
                          <div className="flex gap-1.5 items-center">
                            <select
                              value=""
                              onChange={(e) => {
                                const val = e.target.value;
                                if (!val) return;
                                const cur = editUser.qualifiche || [];
                                if (cur.some(q => q.toLowerCase() === val.toLowerCase())) {
                                  toast.error('Qualifica già assegnata');
                                  return;
                                }
                                setEditUser({ ...editUser, qualifiche: [...cur, val] });
                              }}
                              className="flex-1 h-9 px-2 text-xs rounded-md border border-amber-300 bg-white"
                              data-testid="qualifiche-select"
                            >
                              <option value="">— Aggiungi da elenco —</option>
                              {qualificheMaster
                                .filter(q => !(editUser.qualifiche || []).some(eq => eq.toLowerCase() === q.nome.toLowerCase()))
                                .map(q => (
                                  <option key={q.id} value={q.nome}>{q.nome}</option>
                                ))}
                            </select>
                            <input
                              type="text"
                              placeholder="oppure scrivi qualifica custom"
                              maxLength={120}
                              className="flex-1 h-9 px-2 text-xs rounded-md border border-amber-300 bg-white"
                              onKeyDown={async (e) => {
                                if (e.key !== 'Enter') return;
                                e.preventDefault();
                                const val = e.target.value.trim();
                                if (!val) return;
                                const cur = editUser.qualifiche || [];
                                if (cur.some(q => q.toLowerCase() === val.toLowerCase())) {
                                  toast.error('Qualifica già assegnata');
                                  return;
                                }
                                setEditUser({ ...editUser, qualifiche: [...cur, val] });
                                e.target.value = '';
                                // Aggiungi al master per riusabilita'
                                try {
                                  const token = localStorage.getItem('token');
                                  await axios.post(`${API}/qualifiche`, { nome: val }, { headers: { Authorization: `Bearer ${token}` } });
                                  const qres = await axios.get(`${API}/qualifiche`);
                                  setQualificheMaster(qres.data || []);
                                } catch { /* silent */ }
                              }}
                              data-testid="qualifiche-custom-input"
                            />
                          </div>
                        ) : (
                          <p className="text-[11px] text-amber-600 italic">Massimo 3 qualifiche raggiunto. Rimuovi una qualifica per aggiungerne un'altra.</p>
                        )}

                        {/* Responsabile gerarchico (organigramma) */}
                        <div className="pt-3 border-t border-amber-200">
                          <Label className="text-xs font-semibold text-amber-900 mb-1 block">Responsabile gerarchico (organigramma)</Label>
                          <select
                            value={editUser.manager_id || ''}
                            onChange={(e) => setEditUser({ ...editUser, manager_id: e.target.value })}
                            className="w-full h-9 px-2 text-xs rounded-md border border-amber-300 bg-white"
                            data-testid="user-manager-select"
                          >
                            <option value="">— Nessuno —</option>
                            {users
                              .filter(u => u.id !== editUser.id && u.is_active !== false)
                              .map(u => (
                                <option key={u.id} value={u.id}>
                                  {u.full_name}{(u.qualifiche || [])[0] ? ` · ${u.qualifiche[0]}` : ''}
                                </option>
                              ))}
                          </select>
                          <p className="text-[10px] text-amber-700 mt-1">Definisce a chi riporta questo utente nell'organigramma. Cicli e auto-riferimenti vengono bloccati automaticamente.</p>
                        </div>
                      </div>

                      {/* Contratto professionale */}
                      <div className="border-t border-gray-200 pt-4 space-y-2">
                        <div className="space-y-1 p-3 bg-cyan-50 rounded-lg border border-cyan-200">
                          <Label className="text-xs font-semibold text-cyan-900 mb-1 block">Contratto professionale</Label>
                          <p className="text-[10px] text-cyan-800/80 mb-2">
                            Seleziona il modello e, se necessario, sovrascrivi singoli valori per questo utente. Le ore per turno specificate qui hanno priorità sulla legenda turni globale.
                          </p>
                          <div className="grid grid-cols-1 gap-2">
                            <div>
                              <Label className="text-[11px]">Modello contratto</Label>
                              <select
                                value={editUser.contratto_tipo_id || ''}
                                onChange={(e) => {
                                  const cid = e.target.value || '';
                                  const model = contrattiTipo.find(c => c.id === cid);
                                  // Pre-compila dal modello (resterà sovrascrivibile)
                                  setEditUser({
                                    ...editUser,
                                    contratto_tipo_id: cid,
                                    contratto_ore_mensili_override: model?.ore_mensili ?? '',
                                    contratto_ore_settimanali_override: model?.ore_settimanali ?? '',
                                    contratto_ore_giornaliere_override: model?.ore_giornaliere ?? '',
                                    contratto_giorni_settimana_override: model?.giorni_settimana ?? 6,
                                    contratto_ore_turni_override: { ...(model?.ore_per_turno || {}) },
                                  });
                                }}
                                className="w-full h-9 px-2 text-sm rounded-md border border-gray-300 bg-white"
                                data-testid="edit-user-contratto-select"
                              >
                                <option value="">— Nessuno —</option>
                                {contrattiTipo.map(c => (
                                  <option key={c.id} value={c.id}>{c.nome}</option>
                                ))}
                              </select>
                            </div>
                            <div className="grid grid-cols-3 gap-2">
                              <div>
                                <Label className="text-[11px]">Ore mensili</Label>
                                <Input type="number" min="0" step="0.01"
                                  value={editUser.contratto_ore_mensili_override ?? ''}
                                  onChange={(e) => setEditUser({ ...editUser, contratto_ore_mensili_override: e.target.value })}
                                  className="h-8 text-sm" />
                              </div>
                              <div>
                                <Label className="text-[11px]">Ore settimanali</Label>
                                <Input type="number" min="0" step="0.01"
                                  value={editUser.contratto_ore_settimanali_override ?? ''}
                                  onChange={(e) => setEditUser({ ...editUser, contratto_ore_settimanali_override: e.target.value })}
                                  className="h-8 text-sm" />
                              </div>
                              <div>
                                <Label className="text-[11px]">Ore giornaliere</Label>
                                <Input type="number" min="0" step="0.01"
                                  value={editUser.contratto_ore_giornaliere_override ?? ''}
                                  onChange={(e) => setEditUser({ ...editUser, contratto_ore_giornaliere_override: e.target.value })}
                                  className="h-8 text-sm" />
                              </div>
                            </div>
                            <div className="border-t border-cyan-200 pt-2 mt-1">
                              <Label className="text-[11px] font-semibold text-cyan-900">Ore per turno (override individuale)</Label>
                              <div className="grid grid-cols-3 gap-2 mt-1">
                                {['M', 'P', 'N', 'P/N', 'REP', 'R'].map(sigla => {
                                  const map = editUser.contratto_ore_turni_override || {};
                                  return (
                                    <div key={sigla}>
                                      <Label className="text-[10px] text-gray-600">{sigla}</Label>
                                      <Input type="number" min="0" step="0.01"
                                        value={map[sigla] ?? ''}
                                        onChange={(e) => {
                                          const v = e.target.value;
                                          const next = { ...map };
                                          if (v === '') delete next[sigla];
                                          else next[sigla] = parseFloat(v);
                                          setEditUser({ ...editUser, contratto_ore_turni_override: next });
                                        }}
                                        className="h-7 text-xs" />
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </>
                  )}

                  {editUser.role === 'operatore' && settings && (
                    <>
                      {/* Selezione Team per Coordinatore */}
                      {editUser.is_coordinatore && (
                        <div className="space-y-2 p-4 bg-purple-50 rounded-lg border border-purple-200">
                          <Label className="font-semibold text-purple-900">Operatori assegnati al coordinatore</Label>
                          <p className="text-xs text-purple-700 mb-3">
                            Seleziona gli operatori che faranno parte del team di questo coordinatore
                          </p>
                          <div className="max-h-48 overflow-y-auto space-y-2 bg-white p-3 rounded border">
                            {users.filter(u => u.role === 'operatore' && u.id !== editUser.id).length === 0 ? (
                              <p className="text-sm text-gray-500 italic">Nessun operatore disponibile</p>
                            ) : (
                              users.filter(u => u.role === 'operatore' && u.id !== editUser.id).map(operator => {
                                const isAssigned = operator.coordinatore_id === editUser.id;
                                return (
                                  <Label key={operator.id} className="flex items-center gap-2 p-2 hover:bg-purple-50 rounded cursor-pointer">
                                    <input
                                      type="checkbox"
                                      checked={isAssigned}
                                      onChange={async (e) => {
                                        try {
                                          // Usa il valore PRIMA del click per il messaggio
                                          const wasAssigned = isAssigned;
                                          const willBeAssigned = e.target.checked;
                                          
                                          // Aggiorna il coordinatore_id dell'operatore
                                          const token = localStorage.getItem('token');
                                          await axios.put(`${API}/users/${operator.id}`, {
                                            coordinatore_id: willBeAssigned ? editUser.id : null
                                          }, {
                                            headers: { Authorization: `Bearer ${token}` }
                                          });
                                          
                                          // Messaggio corretto basato sulla direzione del cambio
                                          toast.success(willBeAssigned ? `${operator.full_name} assegnato al team` : `${operator.full_name} rimosso dal team`);
                                          fetchData(); // Ricarica i dati
                                        } catch (error) {
                                          toast.error(error.response?.data?.detail || 'Errore nell\'assegnazione');
                                        }
                                      }}
                                      className="w-4 h-4 text-purple-600 rounded border-gray-300"
                                    />
                                    <div className="flex-1">
                                      <span className="text-sm font-medium">{operator.full_name}</span>
                                      <p className="text-xs text-gray-500">{operator.figura_professionale} - {operator.asl_riferimento}</p>
                                    </div>
                                  </Label>
                                );
                              })
                            )}
                          </div>
                          <p className="text-xs text-purple-600 mt-2">
                            Team attuale: {users.filter(u => u.coordinatore_id === editUser.id).length} operatori
                          </p>
                        </div>
                      )}
                    </>
                  )}
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setShowEditUser(false)}
                      className="flex-1"
                    >
                      Annulla
                    </Button>
                    <Button
                      type="submit"
                      className="flex-1 bg-cyan-600 hover:bg-cyan-700 text-white"
                    >
                      Salva Modifiche
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>

            {/* Dialog Conferma Eliminazione */}
            <Dialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
              <DialogContent className="max-w-md">
                <DialogHeader>
                  <DialogTitle>Conferma Eliminazione</DialogTitle>
                  <DialogDescription>
                    Sei sicuro di voler eliminare l'utente <strong>{selectedUser?.full_name}</strong>?
                    Questa azione non può essere annullata.
                  </DialogDescription>
                </DialogHeader>
                <div className="flex gap-2 mt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setShowDeleteConfirm(false)}
                    className="flex-1"
                  >
                    Annulla
                  </Button>
                  <Button
                    type="button"
                    onClick={handleDeleteUser}
                    className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                  >
                    Elimina
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </TabsContent>

          {/* Figure Professionali Tab - Solo Super Admin */}
          {user.role === 'super_amministratore' && (
            <TabsContent value="figure" className="space-y-4">
              <div className="flex justify-between items-center">
                <h2 className="text-2xl font-bold text-gray-900">Gestione Figure Professionali</h2>
                <Button 
                  onClick={() => {
                    setEditingFigura(null);
                    setNewFigura({ nome: '', tipologie_accessi: [{ nome: '', minuti: 0 }], nota_pacchetti: '' });
                    setShowFiguraDialog(true);
                  }}
                  className="bg-cyan-600 hover:bg-cyan-700 text-white gap-2"
                >
                  <Plus className="w-4 h-4" />
                  Nuova Figura
                </Button>
              </div>

              <Card className="shadow-lg border-0">
                <CardHeader>
                  <CardTitle>Figure Professionali Configurate</CardTitle>
                  <CardDescription>Gestisci le figure professionali e le loro tipologie di accesso</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {settings && settings.figure_professionali && settings.figure_professionali.map((figura) => (
                      <div key={figura.id} className="border rounded-lg p-4">
                        <div className="flex justify-between items-start mb-3">
                          <div>
                            <h3 className="font-semibold text-lg">{figura.nome}</h3>
                            {figura.nota_pacchetti && (
                              <p className="text-sm text-gray-600 mt-1">{figura.nota_pacchetti}</p>
                            )}
                          </div>
                          <div className="flex gap-2">
                            <Button 
                              onClick={() => openEditFigura(figura)} 
                              variant="outline" 
                              size="sm"
                            >
                              Modifica
                            </Button>
                            <Button 
                              onClick={() => handleDeleteFigura(figura.id)} 
                              variant="outline" 
                              size="sm"
                              className="text-red-600 hover:text-red-700"
                            >
                              Elimina
                            </Button>
                          </div>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {figura.tipologie_accessi.map((tipo, idx) => (
                            <span key={idx} className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-cyan-100 text-cyan-800">
                              {tipo.nome}: {tipo.minuti} min
                            </span>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          )}

          {/* Fatture Tab - Solo Super Admin */}
          {/* Fatture Tab - Solo Super Admin */}
          {user.role === 'super_amministratore' && (
            <TabsContent value="fatture" className="space-y-4">
              <Card className="shadow-sm border-0">
                <CardHeader className="pb-3">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                    <div>
                      <CardTitle className="text-base">Fatture Liberi Professionisti</CardTitle>
                      <CardDescription className="text-xs">Gestisci fatture mensili dei liberi professionisti</CardDescription>
                    </div>
                    <Button onClick={fetchInvoices} size="sm" className="h-8 gap-1.5 text-xs bg-cyan-600 hover:bg-cyan-700">
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                      </svg>
                      Aggiorna
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  {/* Filtri inline */}
                  <div className="flex flex-wrap items-center gap-2 mb-4">
                    <select value={invoiceFilters.year} onChange={(e) => setInvoiceFilters({...invoiceFilters, year: parseInt(e.target.value)})}
                      className="h-8 px-2 text-xs rounded-md border border-gray-200 bg-white text-gray-700">
                      {[2024, 2025, 2026].map(y => <option key={y} value={y}>{y}</option>)}
                    </select>
                    <select value={invoiceFilters.month || ''} onChange={(e) => setInvoiceFilters({...invoiceFilters, month: e.target.value ? parseInt(e.target.value) : null})}
                      className="h-8 px-2 text-xs rounded-md border border-gray-200 bg-white text-gray-700">
                      <option value="">Tutti i mesi</option>
                      {months.map((m, i) => <option key={i} value={i+1}>{m}</option>)}
                    </select>
                    <select value={invoiceFilters.asl || ''} onChange={(e) => setInvoiceFilters({...invoiceFilters, asl: e.target.value || null})}
                      className="h-8 px-2 text-xs rounded-md border border-gray-200 bg-white text-gray-700">
                      <option value="">Tutte le ASL</option>
                      <option value="Asl Pescara">Asl Pescara</option>
                      <option value="Asl Chieti">Asl Chieti</option>
                      <option value="Asl L'Aquila">Asl L'Aquila</option>
                      <option value="Asl Teramo">Asl Teramo</option>
                    </select>
                    <select value={invoiceFilters.figura || ''} onChange={(e) => setInvoiceFilters({...invoiceFilters, figura: e.target.value || null})}
                      className="h-8 px-2 text-xs rounded-md border border-gray-200 bg-white text-gray-700">
                      <option value="">Tutte le figure</option>
                      {settings.figure_professionali?.map(fig => (
                        <option key={fig.id} value={fig.nome}>{fig.nome}</option>
                      ))}
                    </select>
                  </div>

                  {/* Lista Liberi Professionisti */}
                  <div className="mb-4">
                    <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Liberi Professionisti</h3>
                    <div className="space-y-2">
                      {users.filter(u => u.tipo_contratto === 'libero_professionista').map(user => (
                        <div key={user.id} className="flex items-center justify-between p-3 rounded-lg border border-gray-200 hover:bg-gray-50/50 transition-all">
                          <div>
                            <h4 className="font-medium text-sm text-gray-900">{user.full_name}</h4>
                            <p className="text-xs text-gray-500">{user.figura_professionale} — {user.asl_riferimento}</p>
                          </div>
                          <div className="flex gap-1.5">
                            <Button size="sm" variant="outline" onClick={() => openBillingConfigDialog(user)}
                              className="h-8 gap-1.5 text-xs text-gray-600 border-gray-200 hover:bg-gray-50">
                              <SettingsIcon className="w-3 h-3" />
                              <span className="hidden sm:inline">Configura</span>
                            </Button>
                            <Button 
                              size="sm"
                              onClick={() => openInvoiceDialog(user)}
                              className="bg-cyan-600 hover:bg-cyan-700 w-full sm:w-auto"
                            >
                              <Plus className="w-4 h-4 mr-1" />
                              Nuova Fattura
                            </Button>
                          </div>
                        </div>
                      ))}
                      {users.filter(u => u.tipo_contratto === 'libero_professionista').length === 0 && (
                        <p className="text-gray-500 text-center py-8">Nessun libero professionista registrato</p>
                      )}
                    </div>
                  </div>

                  {/* Tabella Fatture */}
                  {invoices.length > 0 && (
                    <div className="mt-6">
                      <h3 className="text-lg font-semibold mb-4">Fatture Inserite</h3>
                      <div className="overflow-x-auto">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Operatore</TableHead>
                              <TableHead>Periodo</TableHead>
                              <TableHead className="text-right">Lordo</TableHead>
                              <TableHead className="text-right">Costo Auto</TableHead>
                              <TableHead className="text-right">Cassa Prev.</TableHead>
                              <TableHead className="text-right">Netto</TableHead>
                              <TableHead>Stato</TableHead>
                              <TableHead>Fattura</TableHead>
                              <TableHead className="text-center">Azioni</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {invoices.map((invoice) => {
                              const invoiceUser = users.find(u => u.id === invoice.user_id);
                              return (
                                <TableRow key={invoice.id}>
                                  <TableCell className="font-medium">
                                    <div className="flex items-center gap-2">
                                      <span>{invoiceUser?.full_name || 'N/A'}</span>
                                      {invoice.richiesta_verifica && (
                                        <span className="px-2 py-1 bg-orange-100 text-orange-800 text-xs rounded-full font-semibold animate-pulse">
                                          ⚠️ VERIFICA
                                        </span>
                                      )}
                                    </div>
                                  </TableCell>
                                  <TableCell>{months[invoice.month - 1]} {invoice.year}</TableCell>
                                  <TableCell className="text-right">€{invoice.totale_lordo.toFixed(2)}</TableCell>
                                  <TableCell className="text-right text-red-600">€{invoice.costo_auto.toFixed(2)}</TableCell>
                                  <TableCell className="text-right">€{invoice.importo_cassa_previdenziale.toFixed(2)}</TableCell>
                                  <TableCell className="text-right font-bold text-green-600">€{invoice.totale_netto.toFixed(2)}</TableCell>
                                  <TableCell>
                                    {invoice.status === 'pending' && (
                                      <span className="px-2 py-1 bg-yellow-100 text-yellow-800 text-xs rounded-full">In Attesa</span>
                                    )}
                                    {invoice.status === 'viewed' && (
                                      <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">Visto</span>
                                    )}
                                    {invoice.status === 'uploaded' && (
                                      <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full">Caricata</span>
                                    )}
                                  </TableCell>
                                  <TableCell>
                                    {invoice.numero_fattura ? (
                                      <span className="text-sm">N. {invoice.numero_fattura} del {invoice.data_fattura}</span>
                                    ) : (
                                      <span className="text-sm text-gray-400">-</span>
                                    )}
                                  </TableCell>
                                  <TableCell className="text-center">
                                    <div className="flex gap-1 justify-center">
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={() => openInvoiceDetail(invoice)}
                                        className="gap-1"
                                      >
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                        </svg>
                                        Dettagli
                                      </Button>
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={async () => {
                                          try {
                                            const response = await fetch(`${API}/invoices/${invoice.id}/pdf`, {
                                              method: 'GET',
                                              headers: {
                                                'Authorization': `Bearer ${localStorage.getItem('token')}`
                                              }
                                            });
                                            
                                            if (!response.ok) {
                                              throw new Error('Errore durante il download del PDF');
                                            }
                                            
                                            const blob = await response.blob();
                                            const url = window.URL.createObjectURL(blob);
                                            const link = document.createElement('a');
                                            link.href = url;
                                            link.download = `Fattura_${invoice.month}_${invoice.year}.pdf`;
                                            document.body.appendChild(link);
                                            link.click();
                                            document.body.removeChild(link);
                                            window.URL.revokeObjectURL(url);
                                          } catch (error) {
                                            alert('Errore durante il download del PDF');
                                          }
                                        }}
                                        className="gap-1 text-red-600 border-red-600 hover:bg-red-50"
                                      >
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                        </svg>
                                        Scarica PDF
                                      </Button>
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={() => openAdminUploadInvoiceDialog(invoice)}
                                        className="gap-1 text-green-700 border-green-300 hover:bg-green-50"
                                      >
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                        </svg>
                                        {invoice.numero_fattura ? 'Modifica' : 'Inserisci'} Estremi
                                      </Button>
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={() => handleDeleteInvoice(invoice)}
                                        className="gap-1 text-red-700 border-red-300 hover:bg-red-50"
                                        title="Elimina questa fattura"
                                      >
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                        </svg>
                                        Elimina
                                      </Button>
                                    </div>
                                  </TableCell>
                                </TableRow>
                              );
                            })}
                          </TableBody>
                        </Table>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          )}

          {/* Tab Gestione Convenzioni - Solo Super Admin */}
          {user.role === 'super_amministratore' && (
            <TabsContent value="convenzioni" className="space-y-4">
              <Card className="shadow-lg border-0">
                <CardHeader>
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    <div>
                      <CardTitle>Gestione Convenzioni Aziendali</CardTitle>
                      <CardDescription>Crea e gestisci le convenzioni disponibili per gli operatori</CardDescription>
                    </div>
                    <Button onClick={openCreateConvenzioneDialog} className="bg-green-600 hover:bg-green-700 whitespace-nowrap">
                      <Plus className="w-4 h-4 mr-2" />
                      Nuova Convenzione
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  {convenzioni.length === 0 ? (
                    <div className="text-center py-12 text-gray-500">
                      <svg className="w-12 h-12 mx-auto mb-3 opacity-30" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7" />
                      </svg>
                      <p className="mb-4">Nessuna convenzione presente</p>
                      <Button onClick={() => { fetchConvenzioni(); }} variant="outline">
                        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                        </svg>
                        Ricarica
                      </Button>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {convenzioni.map((conv) => (
                        <Card key={conv.id} className="overflow-hidden hover:shadow-lg transition-shadow">
                          <div className="h-32 bg-cyan-600 relative overflow-hidden">
                            {conv.immagine_url ? (
                              <img src={getAuthenticatedFileUrl(conv.immagine_url)} alt={conv.titolo} className="w-full h-full object-cover" />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center">
                                <svg className="w-16 h-16 text-white opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7" />
                                </svg>
                              </div>
                            )}
                            <div className="absolute top-2 right-2">
                              <span className="px-2 py-1 bg-white text-xs font-semibold rounded-full shadow capitalize">
                                {conv.categoria}
                              </span>
                            </div>
                          </div>
                          <CardContent className="p-4">
                            <h3 className="font-bold text-lg mb-2 text-gray-900">{conv.titolo}</h3>
                            <p className="text-sm text-gray-600 mb-3 line-clamp-2">{conv.descrizione}</p>
                            <div className="flex items-center gap-2 mb-4">
                              <span className="text-green-600 font-bold text-lg">{conv.offerta || conv.sconto}</span>
                            </div>
                            {conv.link && (
                              <a 
                                href={conv.link} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="text-xs text-cyan-600 hover:text-cyan-700 underline block mb-3 truncate"
                              >
                                {conv.link}
                              </a>
                            )}
                            <div className="flex gap-2">
                              <Button 
                                size="sm" 
                                variant="outline"
                                onClick={() => openEditConvenzioneDialog(conv)}
                                className="flex-1"
                              >
                                <Edit className="w-3 h-3 mr-1" />
                                Modifica
                              </Button>
                              <Button 
                                size="sm" 
                                variant="outline"
                                onClick={() => openDeleteConvenzioneDialog(conv)}
                                className="text-red-600 border-red-300 hover:bg-red-50"
                              >
                                <Trash2 className="w-3 h-3 mr-1" />
                                Elimina
                              </Button>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          )}

          {/* Tab Preventivi - Super Admin e utenti con accesso abilitato */}
          {(user.has_preventivi_access === true || user.role === 'super_amministratore') && (
            <TabsContent value="preventivi" className="space-y-4">
              <Preventivi user={user} />
            </TabsContent>
          )}

          {/* Tab Formazione (E-learning) - Solo Super Admin */}
          {user.role === 'super_amministratore' && (
            <TabsContent value="formazione" className="space-y-4">
              {!selectedCorso ? (
                <Card className="shadow-lg border-0">
                  <CardHeader>
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                      <div>
                        <CardTitle>Gestione Corsi di Formazione</CardTitle>
                        <CardDescription>Crea e gestisci i corsi e-learning per gli operatori</CardDescription>
                      </div>
                      <Button onClick={openCreateCorsoDialog} className="bg-cyan-600 hover:bg-cyan-700 whitespace-nowrap">
                        <Plus className="w-4 h-4 mr-2" />
                        Nuovo Corso
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {corsi.length === 0 ? (
                      <div className="text-center py-12 text-gray-500">
                        <svg className="w-12 h-12 mx-auto mb-3 opacity-30" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 14l9-5-9-5-9 5 9 5z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 14l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14z" />
                        </svg>
                        <p className="mb-4">Nessun corso presente</p>
                        <Button onClick={() => { fetchCorsi(); }} variant="outline">
                          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                          </svg>
                          Ricarica
                        </Button>
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {corsi.map((corso) => (
                          <Card key={corso.id} className="overflow-hidden hover:shadow-md transition-shadow border border-gray-200">
                            <div className="h-28 bg-cyan-600 relative overflow-hidden">
                              {corso.immagine_copertina_url ? (
                                <img 
                                  src={getAuthenticatedFileUrl(corso.immagine_copertina_url)} 
                                  alt={corso.titolo} 
                                  className="w-full h-full object-cover" 
                                />
                              ) : (
                                <div className="w-full h-full flex items-center justify-center">
                                  <svg className="w-16 h-16 text-white opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 14l9-5-9-5-9 5 9 5z" />
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 14l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14z" />
                                  </svg>
                                </div>
                              )}
                              <div className="absolute top-2 right-2 flex gap-1">
                                {corso.obbligatorio && (
                                  <span className="px-2 py-0.5 bg-white/90 text-red-600 text-[10px] font-bold rounded-md shadow-sm">
                                    OBBLIGATORIO
                                  </span>
                                )}
                              </div>
                            </div>
                            <CardContent className="p-4">
                              <h3 className="font-bold text-lg mb-2 text-gray-900">{corso.titolo}</h3>
                              <p className="text-sm text-gray-600 mb-3 line-clamp-2">{corso.descrizione}</p>
                              <div className="space-y-2 mb-4 text-xs text-gray-500">
                                {corso.data_scadenza && (
                                  <div className="flex items-center gap-1">
                                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                    </svg>
                                    <span>Scadenza: {corso.data_scadenza}</span>
                                  </div>
                                )}
                                <div className="flex items-center gap-1">
                                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                                  </svg>
                                  <span className="truncate">
                                    {[
                                      ...(corso.ruoli_assegnati || []),
                                      ...(corso.figure_professionali_assegnate || [])
                                    ].join(', ') || 'Nessun ruolo assegnato'}
                                  </span>
                                </div>
                                <div className="flex items-center gap-1">
                                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                  </svg>
                                  <span>Punteggio minimo: {corso.punteggio_minimo_attestato}%</span>
                                </div>
                              </div>
                              <div className="flex flex-wrap gap-1.5">
                                <Button 
                                  size="sm" 
                                  variant="outline"
                                  onClick={() => viewCorsoModuli(corso)}
                                  className="flex-1 min-w-[70px] h-8 text-xs text-gray-600 border-gray-200 hover:bg-gray-50"
                                >
                                  <svg className="w-3 h-3 sm:mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                  </svg>
                                  <span className="hidden sm:inline">Moduli</span>
                                </Button>
                                <Button 
                                  size="sm" 
                                  variant="outline"
                                  onClick={() => openQuestionarioConfig(corso)}
                                  className="flex-1 min-w-[70px] h-8 text-xs text-gray-600 border-gray-200 hover:bg-gray-50"
                                  title="Personalizza Questionario"
                                >
                                  <svg className="w-3 h-3 sm:mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                                  </svg>
                                  <span className="hidden sm:inline">Quest.</span>
                                </Button>
                                <Button 
                                  size="sm" 
                                  variant="outline"
                                  onClick={() => openEditCorsoDialog(corso)}
                                  className="flex-1 min-w-[70px] h-8 text-xs text-gray-600 border-gray-200 hover:bg-gray-50"
                                >
                                  <Edit className="w-3 h-3 sm:mr-1" />
                                  <span className="hidden sm:inline">Modifica</span>
                                </Button>
                                <Button 
                                  size="sm" 
                                  variant="outline"
                                  onClick={() => openDeleteCorsoDialog(corso)}
                                  className="flex-1 min-w-[70px] h-8 text-xs text-red-500 border-gray-200 hover:bg-red-50 hover:border-red-200"
                                >
                                  <Trash2 className="w-3 h-3 sm:mr-1" />
                                  <span className="hidden sm:inline">Elimina</span>
                                </Button>
                              </div>
                            </CardContent>
                          </Card>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              ) : selectedTest ? (
                // Vista Domande Test
                <Card className="shadow-lg border-0">
                  <CardHeader>
                    <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                      <div className="flex-1">
                        <Button 
                          onClick={backToModuliList} 
                          variant="ghost" 
                          size="sm"
                          className="mb-2 text-gray-600 hover:text-gray-900"
                        >
                          <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                          </svg>
                          Torna ai Moduli
                        </Button>
                        <CardTitle>{selectedTest.titolo}</CardTitle>
                        <CardDescription>
                          Gestisci le domande del test • Tipo: {selectedTest.tipo} • Punteggio minimo: {selectedTest.punteggio_minimo}% • Tentativi: {selectedTest.tentativi_max}
                        </CardDescription>
                      </div>
                      <div className="flex gap-2">
                        <Button onClick={() => openEditTestDialog(selectedTest)} variant="outline" className="whitespace-nowrap">
                          <Edit className="w-4 h-4 mr-2" />
                          Modifica Test
                        </Button>
                        <Button onClick={openCreateDomandaDialog} className="bg-green-600 hover:bg-green-700 whitespace-nowrap">
                          <Plus className="w-4 h-4 mr-2" />
                          Nuova Domanda
                        </Button>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {domande.length === 0 ? (
                      <div className="text-center py-12 text-gray-500">
                        <svg className="w-12 h-12 mx-auto mb-3 opacity-30" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <p className="mb-4">Nessuna domanda presente in questo test</p>
                        <Button onClick={openCreateDomandaDialog} variant="outline">
                          <Plus className="w-4 h-4 mr-2" />
                          Aggiungi Prima Domanda
                        </Button>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {domande.map((domanda, index) => (
                          <Card key={domanda.id} className="hover:shadow-md transition-shadow">
                            <CardContent className="p-4">
                              <div className="flex items-start gap-4">
                                <div className="flex-shrink-0 w-10 h-10 bg-cyan-100 rounded-full flex items-center justify-center">
                                  <span className="text-cyan-600 font-bold text-sm">{domanda.ordine}</span>
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2 mb-2">
                                    <span className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded font-medium">
                                      {domanda.tipo === 'multipla' && 'Risposta Multipla'}
                                      {domanda.tipo === 'vero_falso' && 'Vero/Falso'}
                                      {domanda.tipo === 'matching' && 'Abbinamento'}
                                      {domanda.tipo === 'testuale' && 'Risposta Aperta'}
                                    </span>
                                    <span className="text-xs text-gray-500">{domanda.punti} {domanda.punti === 1 ? 'punto' : 'punti'}</span>
                                    {domanda.richiede_correzione_manuale && (
                                      <span className="px-2 py-1 bg-yellow-100 text-yellow-700 text-xs rounded">
                                        Correzione manuale
                                      </span>
                                    )}
                                  </div>
                                  <p className="text-sm text-gray-900 mb-2 font-medium">{domanda.testo}</p>
                                  {(domanda.tipo === 'multipla' || domanda.tipo === 'vero_falso') && domanda.opzioni && (
                                    <div className="space-y-1 mt-2">
                                      {domanda.tipo === 'vero_falso' ? (
                                        <div className="text-xs text-gray-600">
                                          Risposta corretta: <span className="font-semibold text-green-600 capitalize">{domanda.risposta_corretta}</span>
                                        </div>
                                      ) : (
                                        domanda.opzioni.map((opzione, idx) => (
                                          <div key={idx} className={`text-xs px-2 py-1 rounded ${idx.toString() === domanda.risposta_corretta ? 'bg-green-50 text-green-700 font-semibold' : 'text-gray-600'}`}>
                                            {String.fromCharCode(65 + idx)}. {opzione}
                                            {idx.toString() === domanda.risposta_corretta && ' ✓'}
                                          </div>
                                        ))
                                      )}
                                    </div>
                                  )}
                                </div>
                                <div className="flex gap-1">
                                  <Button 
                                    size="sm" 
                                    variant="ghost"
                                    onClick={() => openEditDomandaDialog(domanda)}
                                  >
                                    <Edit className="w-4 h-4" />
                                  </Button>
                                  <Button 
                                    size="sm" 
                                    variant="ghost"
                                    onClick={() => openDeleteDomandaDialog(domanda)}
                                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                </div>
                              </div>
                            </CardContent>
                          </Card>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              ) : (
                // Vista Moduli
                <Card className="shadow-lg border-0">
                  <CardHeader>
                    <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                      <div className="flex-1">
                        <Button 
                          onClick={backToCorsiList} 
                          variant="ghost" 
                          size="sm"
                          className="mb-2 text-gray-600 hover:text-gray-900"
                        >
                          <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                          </svg>
                          Torna ai Corsi
                        </Button>
                        <CardTitle>{selectedCorso.titolo}</CardTitle>
                        <CardDescription>Gestisci i moduli del corso</CardDescription>
                      </div>
                      <div className="flex gap-2">
                        <Button onClick={openCreateModuloDialog} size="sm" className="bg-cyan-600 hover:bg-cyan-700 whitespace-nowrap gap-1.5">
                          <Plus className="w-4 h-4" />
                          Nuovo Modulo
                        </Button>
                        <Button 
                          onClick={openCreateTestFinaleDialog} 
                          size="sm"
                          variant="outline"
                          className="whitespace-nowrap gap-1.5 border-gray-200 text-gray-600 hover:bg-gray-50"
                          disabled={!!selectedCorso.test_finale_id}
                        >
                          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
                          </svg>
                          {selectedCorso.test_finale_id ? 'Test Finale Esistente' : 'Crea Test Finale'}
                        </Button>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {moduli.length === 0 ? (
                      <div className="text-center py-12 text-gray-500">
                        <svg className="w-12 h-12 mx-auto mb-3 opacity-30" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        <p className="mb-4">Nessun modulo presente in questo corso</p>
                        <Button onClick={openCreateModuloDialog} variant="outline">
                          <Plus className="w-4 h-4 mr-2" />
                          Aggiungi Primo Modulo
                        </Button>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {moduli.map((modulo, index) => (
                          <Card key={modulo.id} className="hover:shadow-sm transition-shadow border border-gray-200">
                            <CardContent className="p-3">
                              <div className="flex items-start gap-3">
                                <div className="flex-shrink-0 w-8 h-8 bg-cyan-50 border border-cyan-200 rounded-full flex items-center justify-center">
                                  <span className="text-cyan-700 font-bold text-xs">{modulo.ordine}</span>
                                </div>
                                <div className="flex-1 min-w-0">
                                  <h4 className="font-medium text-sm text-gray-900 mb-1">{modulo.titolo}</h4>
                                  {modulo.descrizione && (
                                    <p className="text-xs text-gray-500 mb-1.5 line-clamp-2">{modulo.descrizione}</p>
                                  )}
                                  <div className="flex flex-wrap gap-1.5 text-[11px]">
                                    {modulo.video_url && (
                                      <span className="flex items-center gap-1 px-1.5 py-0.5 bg-gray-100 text-gray-600 rounded">Video</span>
                                    )}
                                    {modulo.testo_markdown && (
                                      <span className="flex items-center gap-1 px-1.5 py-0.5 bg-gray-100 text-gray-600 rounded">Testo</span>
                                    )}
                                    {modulo.pdf_url && (
                                      <span className="flex items-center gap-1 px-1.5 py-0.5 bg-gray-100 text-gray-600 rounded">PDF</span>
                                    )}
                                  </div>
                                </div>
                                <div className="flex gap-0.5 shrink-0">
                                  <Button 
                                    size="sm" 
                                    variant="ghost"
                                    onClick={() => modulo.has_test ? viewTestDomande(modulo.test_id) : openCreateTestDialog(modulo)}
                                    className="h-8 w-8 p-0 text-gray-400 hover:text-cyan-600 hover:bg-cyan-50"
                                    title={modulo.has_test ? "Visualizza test" : "Aggiungi test"}
                                  >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                      {!modulo.has_test && <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />}
                                    </svg>
                                  </Button>
                                  <Button 
                                    size="sm" 
                                    variant="ghost"
                                    onClick={() => openEditModuloDialog(modulo)}
                                    className="h-8 w-8 p-0 text-gray-400 hover:text-gray-700 hover:bg-gray-100"
                                  >
                                    <Edit className="w-3.5 h-3.5" />
                                  </Button>
                                  <Button 
                                    size="sm" 
                                    variant="ghost"
                                    onClick={() => openDeleteModuloDialog(modulo)}
                                    className="h-8 w-8 p-0 text-gray-400 hover:text-red-600 hover:bg-red-50"
                                  >
                                    <Trash2 className="w-3.5 h-3.5" />
                                  </Button>
                                </div>
                              </div>
                            </CardContent>
                          </Card>
                        ))}
                      </div>
                    )}
                    
                    {/* Test Finale Card */}
                    {selectedCorso?.test_finale_id && (
                      <div className="mt-6 pt-6 border-t border-gray-100">
                        <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Test Finale</h3>
                        <Card className="border border-gray-200 bg-gray-50">
                          <CardContent className="p-3">
                            <div className="flex items-center gap-3">
                              <div className="flex-shrink-0 w-9 h-9 bg-cyan-100 rounded-full flex items-center justify-center">
                                <svg className="w-4 h-4 text-cyan-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                              </div>
                              <div className="flex-1 min-w-0">
                                <h4 className="font-medium text-sm text-gray-900">Test Finale del Corso</h4>
                                <p className="text-xs text-gray-500">
                                  Test obbligatorio per completare il corso e ottenere l'attestato
                                </p>
                              </div>
                              <div className="flex gap-0.5 shrink-0">
                                <Button 
                                  size="sm" 
                                  variant="ghost"
                                  onClick={() => viewTestDomande(selectedCorso.test_finale_id)}
                                  className="h-8 w-8 p-0 text-gray-400 hover:text-cyan-600 hover:bg-cyan-50"
                                  title="Visualizza domande test finale"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                  </svg>
                                </Button>
                                <Button 
                                  size="sm" 
                                  variant="ghost"
                                  onClick={async () => {
                                    if (window.confirm('Sei sicuro di voler eliminare il test finale? Questa azione eliminerà anche tutte le domande associate.')) {
                                      try {
                                        await axios.delete(`${API}/formazione/test/${selectedCorso.test_finale_id}`);
                                        toast.success('Test finale eliminato con successo!');
                                        setSelectedCorso({...selectedCorso, test_finale_id: null});
                                        fetchCorsi();
                                      } catch (error) {
                                        toast.error(error.response?.data?.detail || 'Errore nell\'eliminazione del test finale');
                                      }
                                    }
                                  }}
                                  className="h-8 w-8 p-0 text-gray-400 hover:text-red-600 hover:bg-red-50"
                                  title="Elimina test finale"
                                >
                                  <Trash2 className="w-3.5 h-3.5" />
                                </Button>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}
            </TabsContent>
          )}


          {/* Tab Login Monitoring - Solo Super Admin */}
          {user.role === 'super_amministratore' && (
            <TabsContent value="login-monitoring" className="space-y-4">
              <Card className="shadow-lg border-0">
                <CardHeader className="flex flex-row items-center justify-between">
                  <div>
                    <CardTitle>Login & Monitoraggio Accessi</CardTitle>
                    <CardDescription>Dashboard login, sessioni attive e storico accessi</CardDescription>
                  </div>
                  <Button onClick={() => {
                    fetchLoginDashboard();
                    fetchLoginHistory();
                    fetchActiveSessions();
                    fetchFailedLogins();
                  }} variant="outline" size="sm">
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                    Aggiorna
                  </Button>
                </CardHeader>
                <CardContent className="space-y-6">
                  
                  {/* Dashboard KPIs */}
                  {loginDashboard && (
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                      <Card>
                        <CardContent className="p-4">
                          <div className="text-center">
                            <div className="text-2xl font-bold text-green-600">{loginDashboard.online_users}</div>
                            <div className="text-xs text-gray-600 mt-1">Online Ora</div>
                          </div>
                        </CardContent>
                      </Card>
                      <Card>
                        <CardContent className="p-4">
                          <div className="text-center">
                            <div className="text-2xl font-bold text-blue-600">{loginDashboard.logins_today}</div>
                            <div className="text-xs text-gray-600 mt-1">Login Oggi</div>
                          </div>
                        </CardContent>
                      </Card>
                      <Card>
                        <CardContent className="p-4">
                          <div className="text-center">
                            <div className="text-2xl font-bold text-purple-600">{loginDashboard.logins_this_week}</div>
                            <div className="text-xs text-gray-600 mt-1">Login Settimana</div>
                          </div>
                        </CardContent>
                      </Card>
                      <Card>
                        <CardContent className="p-4">
                          <div className="text-center">
                            <div className="text-2xl font-bold text-cyan-600">{loginDashboard.logins_this_month}</div>
                            <div className="text-xs text-gray-600 mt-1">Login Mese</div>
                          </div>
                        </CardContent>
                      </Card>
                      <Card>
                        <CardContent className="p-4">
                          <div className="text-center">
                            <div className="text-2xl font-bold text-red-600">{loginDashboard.failed_logins_today}</div>
                            <div className="text-xs text-gray-600 mt-1">Falliti Oggi</div>
                          </div>
                        </CardContent>
                      </Card>
                      <Card>
                        <CardContent className="p-4">
                          <div className="text-center">
                            <div className="text-2xl font-bold text-gray-600">{loginDashboard.total_sessions}</div>
                            <div className="text-xs text-gray-600 mt-1">Sessioni Totali</div>
                          </div>
                        </CardContent>
                      </Card>
                    </div>
                  )}

                  {/* Sessioni Attive */}
                  <div>
                    <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                      <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      Sessioni Attive ({activeSessions.length})
                    </h3>
                    {activeSessions.length === 0 ? (
                      <p className="text-gray-500 text-sm">Nessuna sessione attiva</p>
                    ) : (
                      <div className="overflow-x-auto">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Utente</TableHead>
                              <TableHead>Nome</TableHead>
                              <TableHead>Ruolo</TableHead>
                              <TableHead>Login</TableHead>
                              <TableHead>Ultima Attività</TableHead>
                              <TableHead>IP</TableHead>
                              <TableHead className="text-center">Azioni</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {activeSessions.map(session => (
                              <TableRow key={session.id}>
                                <TableCell className="font-medium">{session.username}</TableCell>
                                <TableCell>{session.full_name}</TableCell>
                                <TableCell>
                                  <span className={`px-2 py-1 rounded text-xs ${
                                    session.role === 'super_amministratore' ? 'bg-red-100 text-red-700' :
                                    session.role === 'amministratore' ? 'bg-blue-100 text-blue-700' :
                                    'bg-gray-100 text-gray-700'
                                  }`}>
                                    {session.role}
                                  </span>
                                </TableCell>
                                <TableCell className="text-sm">
                                  {new Date(session.login_time).toLocaleString('it-IT')}
                                </TableCell>
                                <TableCell className="text-sm">
                                  {new Date(session.last_activity).toLocaleString('it-IT')}
                                </TableCell>
                                <TableCell className="text-xs text-gray-500">{session.ip_address || 'N/A'}</TableCell>
                                <TableCell className="text-center">
                                  <Button
                                    size="sm"
                                    variant="destructive"
                                    onClick={() => handleTerminateSession(session.id)}
                                  >
                                    Termina
                                  </Button>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    )}
                  </div>

                  {/* Storico Login */}
                  <div>
                    <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                      <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      Storico Login (ultimi 100)
                    </h3>
                    {loginHistory.length === 0 ? (
                      <p className="text-gray-500 text-sm">Nessun login registrato</p>
                    ) : (
                      <div className="overflow-x-auto">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Data/Ora</TableHead>
                              <TableHead>Utente</TableHead>
                              <TableHead>Esito</TableHead>
                              <TableHead>IP</TableHead>
                              <TableHead>Motivo Fallimento</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {loginHistory.map(log => (
                              <TableRow key={log.id} className={!log.success ? 'bg-red-50' : ''}>
                                <TableCell className="text-sm">
                                  {new Date(log.timestamp).toLocaleString('it-IT')}
                                </TableCell>
                                <TableCell className="font-medium">{log.username}</TableCell>
                                <TableCell>
                                  {log.success ? (
                                    <span className="px-2 py-1 bg-green-100 text-green-700 rounded text-xs">✓ Riuscito</span>
                                  ) : (
                                    <span className="px-2 py-1 bg-red-100 text-red-700 rounded text-xs">✗ Fallito</span>
                                  )}
                                </TableCell>
                                <TableCell className="text-xs text-gray-500">{log.ip_address || 'N/A'}</TableCell>
                                <TableCell className="text-xs text-red-600">{log.failure_reason || '-'}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    )}
                  </div>

                  {/* Login Falliti */}
                  {failedLogins.length > 0 && (
                    <div>
                      <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                        <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                        </svg>
                        Login Falliti Recenti ({failedLogins.length})
                      </h3>
                      <div className="overflow-x-auto">
                        <Table>
                          <TableHeader>
                            <TableRow className="bg-red-50">
                              <TableHead>Data/Ora</TableHead>
                              <TableHead>Utente</TableHead>
                              <TableHead>Motivo</TableHead>
                              <TableHead>IP</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {failedLogins.slice(0, 20).map(log => (
                              <TableRow key={log.id} className="bg-red-50">
                                <TableCell className="text-sm">
                                  {new Date(log.timestamp).toLocaleString('it-IT')}
                                </TableCell>
                                <TableCell className="font-medium">{log.username}</TableCell>
                                <TableCell className="text-sm text-red-600">{log.failure_reason}</TableCell>
                                <TableCell className="text-xs text-gray-500">{log.ip_address || 'N/A'}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    </div>
                  )}

                </CardContent>
              </Card>
            </TabsContent>
          )}

          {/* Tab Monitoraggio - Solo Super Admin */}
          {user.role === 'super_amministratore' && (
            <TabsContent value="monitoraggio" className="space-y-4">
              <Card className="shadow-lg border-0">
                <CardHeader>
                  <CardTitle>Monitoraggio Formazione</CardTitle>
                  <CardDescription>Statistiche, progressi operatori e correzione test</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Sezione Statistiche */}
                  <div>
                    <h3 className="text-lg font-semibold mb-4">Statistiche Generali</h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <Card>
                        <CardContent className="p-4">
                          <div className="flex items-center gap-3">
                            <div className="p-3 bg-blue-100 rounded-lg">
                              <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                              </svg>
                            </div>
                            <div>
                              <p className="text-sm text-gray-600">Corsi Attivi</p>
                              <p className="text-2xl font-bold">{corsi.filter(c => c.attivo).length}</p>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                      <Card>
                        <CardContent className="p-4">
                          <div className="flex items-center gap-3">
                            <div className="p-3 bg-green-100 rounded-lg">
                              <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                              </svg>
                            </div>
                            <div>
                              <p className="text-sm text-gray-600">Operatori Attivi</p>
                              <p className="text-2xl font-bold">{users.filter(u => u.account_status === 'active').length}</p>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                      <Card>
                        <CardContent className="p-4">
                          <div className="flex items-center gap-3">
                            <div className="p-3 bg-yellow-100 rounded-lg">
                              <svg className="w-6 h-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                              </svg>
                            </div>
                            <div>
                              <p className="text-sm text-gray-600">Test da Correggere</p>
                              <p className="text-2xl font-bold text-yellow-600">-</p>
                              <p className="text-xs text-gray-500">In sviluppo</p>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    </div>
                  </div>

                  {/* Sezione Progressi Operatori */}
                  <div>
                    <div className="flex flex-col gap-3 mb-4">
                      <h3 className="text-lg font-semibold">Progressi Operatori</h3>
                      <div className="flex flex-col sm:flex-row flex-wrap gap-3">
                        {/* Filtro Corso */}
                        <div className="flex items-center gap-2 px-4 py-3 bg-white border-2 border-blue-200 rounded-xl w-full sm:flex-1 sm:min-w-[200px]">
                          <svg className="w-5 h-5 text-blue-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                          </svg>
                          <select
                            className="bg-transparent border-none outline-none text-sm font-medium text-gray-700 cursor-pointer flex-1 min-w-0"
                            value={filtroCorsoMonitoraggio}
                            onChange={(e) => setFiltroCorsoMonitoraggio(e.target.value)}
                          >
                            <option value="">Tutti i corsi</option>
                            {corsi.map((corso) => (
                              <option key={corso.id} value={corso.id}>{corso.titolo}</option>
                            ))}
                          </select>
                        </div>
                        
                        {/* Filtro Operatore */}
                        <div className="flex items-center gap-2 px-4 py-3 bg-white border-2 border-green-200 rounded-xl w-full sm:flex-1 sm:min-w-[200px]">
                          <svg className="w-5 h-5 text-green-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                          </svg>
                          <select
                            className="bg-transparent border-none outline-none text-sm font-medium text-gray-700 cursor-pointer flex-1 min-w-0"
                            value={filtroOperatoreMonitoraggio}
                            onChange={(e) => setFiltroOperatoreMonitoraggio(e.target.value)}
                          >
                            <option value="">Tutti gli operatori</option>
                            {users.filter(u => u.account_status === 'active').map((utente) => (
                              <option key={utente.id} value={utente.id}>
                                {utente.first_name} {utente.last_name}
                              </option>
                            ))}
                          </select>
                        </div>
                        
                        {/* Contenitore pulsanti azioni */}
                        <div className="flex gap-3 w-full sm:w-auto">
                          {/* Pulsante Aggiorna */}
                          <Button 
                            onClick={fetchProgressiOperatori}
                            variant="outline"
                            size="sm"
                            className="border-2 border-gray-300 text-gray-700 hover:bg-gray-50 font-medium h-10 w-10 p-0"
                            title="Aggiorna dati"
                          >
                            <span className="text-base">🔄</span>
                          </Button>
                          
                          {/* Pulsante Export Excel */}
                          <Button 
                            onClick={handleExportExcelStatistiche}
                            variant="outline"
                            size="sm"
                            className="border-2 border-gray-300 text-gray-700 hover:bg-gray-50 font-medium h-10 px-3 flex-1 sm:flex-initial"
                            title="Scarica Excel"
                          >
                            <span className="text-base mr-1">📥</span>
                            <span className="text-xs sm:text-sm">Excel</span>
                          </Button>
                        </div>
                      </div>
                    </div>
                    <Card>
                      <CardContent className="p-0">
                        {progressiOperatori.length === 0 ? (
                          <div className="text-center py-8 text-gray-500 p-4">
                            <svg className="w-12 h-12 mx-auto mb-3 opacity-30" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                            </svg>
                            <p>Nessun progresso trovato</p>
                            <p className="text-xs mt-2">Gli operatori devono iniziare i corsi assegnati</p>
                          </div>
                        ) : (
                          <div className="overflow-x-auto">
                            <table className="w-full">
                              <thead className="bg-gray-50 border-b">
                                <tr>
                                  <th className="text-left p-3 text-sm font-semibold text-gray-700">Operatore</th>
                                  <th className="text-left p-3 text-sm font-semibold text-gray-700">Corso</th>
                                  <th className="text-left p-3 text-sm font-semibold text-gray-700">Progresso</th>
                                  <th className="text-left p-3 text-sm font-semibold text-gray-700">Completato</th>
                                  <th className="text-left p-3 text-sm font-semibold text-gray-700">Punteggio</th>
                                  <th className="text-left p-3 text-sm font-semibold text-gray-700">Ultimo accesso</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y">
                                {progressiOperatori.map((progresso) => {
                                  const operatore = users.find(u => u.id === progresso.user_id);
                                  const corso = corsi.find(c => c.id === progresso.corso_id);
                                  const percentualeCompletamento = corso ? 
                                    Math.round((progresso.moduli_completati.length / moduli.filter(m => m.corso_id === corso.id).length) * 100) || 0 
                                    : 0;
                                  
                                  return (
                                    <tr key={progresso.id} className="hover:bg-gray-50">
                                      <td className="p-3 text-sm">
                                        {operatore ? `${operatore.first_name} ${operatore.last_name}` : 'Sconosciuto'}
                                      </td>
                                      <td className="p-3 text-sm">{corso?.titolo || 'Corso non trovato'}</td>
                                      <td className="p-3">
                                        <div className="flex items-center gap-2">
                                          <div className="flex-1 bg-gray-200 rounded-full h-2 max-w-[120px]">
                                            <div 
                                              className="bg-green-500 h-2 rounded-full transition-all"
                                              style={{ width: `${percentualeCompletamento}%` }}
                                            />
                                          </div>
                                          <span className="text-xs text-gray-600">{percentualeCompletamento}%</span>
                                        </div>
                                      </td>
                                      <td className="p-3 text-sm">
                                        {progresso.completato ? (
                                          <span className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded-full">
                                            ✓ Completato
                                          </span>
                                        ) : (
                                          <span className="px-2 py-1 bg-yellow-100 text-yellow-700 text-xs rounded-full">
                                            In corso
                                          </span>
                                        )}
                                      </td>
                                      <td className="p-3 text-sm">
                                        {progresso.punteggio_finale ? 
                                          <span className="font-semibold">{progresso.punteggio_finale}%</span> 
                                          : '-'}
                                      </td>
                                      <td className="p-3 text-sm text-gray-600">
                                        {new Date(progresso.ultimo_accesso).toLocaleDateString('it-IT')}
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  </div>

                  {/* Sezione Test da Correggere */}
                  <div>
                    <h3 className="text-lg font-semibold mb-4">Test da Correggere</h3>
                    <Card>
                      <CardContent className="p-0">
                        {risposteDaCorreggere.length === 0 ? (
                          <div className="text-center py-8 text-gray-500 p-4">
                            <svg className="w-12 h-12 mx-auto mb-3 opacity-30" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <p>Nessuna risposta da correggere</p>
                          </div>
                        ) : (
                          <div className="overflow-x-auto">
                            <table className="w-full">
                              <thead className="bg-gray-50 border-b">
                                <tr>
                                  <th className="text-left p-3 text-sm font-semibold text-gray-700">Operatore</th>
                                  <th className="text-left p-3 text-sm font-semibold text-gray-700">Corso</th>
                                  <th className="text-left p-3 text-sm font-semibold text-gray-700">Test</th>
                                  <th className="text-left p-3 text-sm font-semibold text-gray-700">Domanda</th>
                                  <th className="text-left p-3 text-sm font-semibold text-gray-700">Risposta</th>
                                  <th className="text-left p-3 text-sm font-semibold text-gray-700">Azioni</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y">
                                {risposteDaCorreggere.map((risposta) => (
                                  <tr key={risposta.id} className="hover:bg-gray-50">
                                    <td className="p-3 text-sm">{risposta.user_name}</td>
                                    <td className="p-3 text-sm">{risposta.corso_titolo}</td>
                                    <td className="p-3 text-sm">{risposta.test_titolo}</td>
                                    <td className="p-3 text-sm max-w-xs truncate">{risposta.domanda_testo}</td>
                                    <td className="p-3 text-sm max-w-xs">
                                      <div className="line-clamp-2">{risposta.risposta}</div>
                                    </td>
                                    <td className="p-3">
                                      <Button 
                                        size="sm"
                                        onClick={() => openCorrezioneDialog(risposta)}
                                        className="bg-blue-600 hover:bg-blue-700"
                                      >
                                        Correggi
                                      </Button>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  </div>

                  {/* Sezione Certificati */}
                  <div>
                    <h3 className="text-lg font-semibold mb-4">Attestati Generati</h3>
                    <Card>
                      <CardContent className="p-0">
                        {(() => {
                          // Applica filtri
                          const certificatiFiltrati = certificati.filter(cert => {
                            // Filtro per corso
                            if (filtroCorsoMonitoraggio && cert.corso_id !== filtroCorsoMonitoraggio) {
                              return false;
                            }
                            // Filtro per operatore
                            if (filtroOperatoreMonitoraggio && cert.user_id !== filtroOperatoreMonitoraggio) {
                              return false;
                            }
                            return true;
                          });
                          
                          return certificatiFiltrati.length === 0 ? (
                            <div className="text-center py-8 text-gray-500 p-4">
                              <svg className="w-12 h-12 mx-auto mb-3 opacity-30" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
                              </svg>
                              <p>Nessun attestato {filtroCorsoMonitoraggio || filtroOperatoreMonitoraggio ? 'trovato con i filtri selezionati' : 'generato'}</p>
                            </div>
                          ) : (
                            <div className="overflow-x-auto">
                              <table className="w-full">
                                <thead className="bg-gray-50 border-b">
                                  <tr>
                                    <th className="text-left p-3 text-sm font-semibold text-gray-700">Operatore</th>
                                    <th className="text-left p-3 text-sm font-semibold text-gray-700">Corso</th>
                                    <th className="text-left p-3 text-sm font-semibold text-gray-700">Data</th>
                                    <th className="text-left p-3 text-sm font-semibold text-gray-700">Punteggio</th>
                                    <th className="text-left p-3 text-sm font-semibold text-gray-700">Azioni</th>
                                  </tr>
                                </thead>
                                <tbody className="divide-y">
                                  {certificatiFiltrati.map((cert) => (
                                  <tr key={cert.id} className="hover:bg-gray-50">
                                    <td className="p-3 text-sm">{cert.nome_operatore}</td>
                                    <td className="p-3 text-sm">{cert.titolo_corso}</td>
                                    <td className="p-3 text-sm">{new Date(cert.data_completamento).toLocaleDateString('it-IT')}</td>
                                    <td className="p-3 text-sm">
                                      <span className="font-semibold text-green-600">{cert.punteggio_finale}%</span>
                                    </td>
                                    <td className="p-3">
                                      <div className="flex gap-2">
                                        <Button 
                                          size="sm"
                                          variant="outline"
                                          onClick={() => downloadPDFAttestato(cert.id, cert.nome_operatore)}
                                          className="text-green-600 border-green-300 hover:bg-green-50"
                                        >
                                          <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
                                          </svg>
                                          Attestato
                                        </Button>
                                        <Button 
                                          size="sm"
                                          variant="outline"
                                          onClick={() => downloadPDFTest(cert.id, cert.nome_operatore)}
                                          className="text-purple-600 border-purple-300 hover:bg-purple-50"
                                        >
                                          <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                                          </svg>
                                          Test
                                        </Button>
                                        <Button 
                                          size="sm"
                                          variant="outline"
                                          onClick={() => visualizzaRiepilogoTest(cert.id)}
                                          className="text-blue-600 border-blue-300 hover:bg-blue-50"
                                        >
                                          <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                          </svg>
                                          Vedi
                                        </Button>
                                      </div>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                          );
                        })()}
                      </CardContent>
                    </Card>
                  </div>
                  
                  {/* Sezione Risposte Questionari */}
                  <div>
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-semibold">Risposte Questionari di Soddisfacimento</h3>
                      <Button 
                        onClick={fetchRisposteQuestionari} 
                        variant="outline" 
                        size="sm"
                      >
                        <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                        </svg>
                        Ricarica
                      </Button>
                    </div>
                    <Card>
                      <CardContent className="p-0">
                        {(() => {
                          // Applica filtri
                          const risposteFiltrate = risposteQuestionari.filter(risposta => {
                            // Filtro per corso
                            if (filtroCorsoMonitoraggio && risposta.corso_id !== filtroCorsoMonitoraggio) {
                              return false;
                            }
                            // Filtro per operatore
                            if (filtroOperatoreMonitoraggio && risposta.user_id !== filtroOperatoreMonitoraggio) {
                              return false;
                            }
                            return true;
                          });
                          
                          return risposteFiltrate.length === 0 ? (
                            <div className="text-center py-12 text-gray-500">
                              <svg className="w-12 h-12 mx-auto mb-3 opacity-30" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                              </svg>
                              <p>Nessuna risposta {filtroCorsoMonitoraggio || filtroOperatoreMonitoraggio ? 'trovata con i filtri selezionati' : 'ai questionari'}</p>
                            </div>
                          ) : (
                            <div className="overflow-x-auto">
                              <table className="w-full">
                                <thead className="bg-gray-50">
                                  <tr>
                                    <th className="p-3 text-left text-sm font-semibold text-gray-700">Operatore</th>
                                    <th className="p-3 text-left text-sm font-semibold text-gray-700">Corso</th>
                                    <th className="p-3 text-left text-sm font-semibold text-gray-700">Data</th>
                                    <th className="p-3 text-left text-sm font-semibold text-gray-700">Valutazione Media</th>
                                    <th className="p-3 text-left text-sm font-semibold text-gray-700">Azioni</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {risposteFiltrate.map((risposta) => {
                                  // Calcola media dalle risposte (dizionario)
                                  let media = 'N/A';
                                  if (risposta.risposte && Object.keys(risposta.risposte).length > 0) {
                                    const valori = Object.values(risposta.risposte);
                                    const somma = valori.reduce((sum, val) => sum + val, 0);
                                    media = (somma / valori.length).toFixed(1);
                                  }
                                  
                                  const user = users.find(u => u.id === risposta.user_id);
                                  const corso = corsi.find(c => c.id === risposta.corso_id);
                                  
                                  return (
                                    <tr key={risposta.id} className="hover:bg-gray-50 border-b">
                                      <td className="p-3 text-sm">
                                        {user ? `${user.first_name} ${user.last_name}` : 'N/A'}
                                      </td>
                                      <td className="p-3 text-sm">{corso?.titolo || 'N/A'}</td>
                                      <td className="p-3 text-sm">
                                        {new Date(risposta.created_at).toLocaleDateString('it-IT')}
                                      </td>
                                      <td className="p-3">
                                        <div className="flex items-center gap-2">
                                          <span className="text-sm font-semibold">{media}</span>
                                          <div className="flex gap-0.5">
                                            {[...Array(5)].map((_, i) => (
                                              <svg 
                                                key={i} 
                                                className={`w-4 h-4 ${i < Math.round(parseFloat(media)) ? 'text-yellow-400' : 'text-gray-300'}`}
                                                fill="currentColor" 
                                                viewBox="0 0 20 20"
                                              >
                                                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                                              </svg>
                                            ))}
                                          </div>
                                        </div>
                                      </td>
                                      <td className="p-3">
                                        <Button 
                                          size="sm" 
                                          variant="outline"
                                          onClick={() => viewDettaglioRisposta(risposta)}
                                        >
                                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                          </svg>
                                        </Button>
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                          );
                        })()}
                      </CardContent>
                    </Card>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          )}

          {/* Tab Gestione Turni */}
          {user.role === 'super_amministratore' && (
            <TabsContent value="turni" className="space-y-4">
              <GestioneTurni key="gestione-turni" user={user} />
            </TabsContent>
          )}
          
          {/* Tab Planning Orario */}
          {(user.has_planning_access || user.role === 'super_amministratore') && (
            <TabsContent value="planning" className="space-y-4">
              <PlanningOrario key="planning-orario-admin" user={user} />
            </TabsContent>
          )}
          
          {/* Tab Procedure */}
          <TabsContent value="procedure" className="space-y-4">
            <Procedure user={user} />
          </TabsContent>

          {/* Tab Audit */}
          <TabsContent value="audit" className="space-y-4">
            <Audit user={user} />
          </TabsContent>

          {/* Tab Accreditamenti */}
          {(user.role === 'super_amministratore' || user.has_accreditamenti_access === true) && (
            <TabsContent value="accreditamenti" className="space-y-4">
              <Accreditamenti user={user} />
            </TabsContent>
          )}

          {/* Tab Privacy & GDPR */}
          {user.role === 'super_amministratore' && (
            <TabsContent value="privacy" className="space-y-4">
              <Informative user={user} />
            </TabsContent>
          )}


        </Tabs>
          </div>
        </main>
      </div>

      {/* Dialog Gestione Figura */}
      <Dialog open={showFiguraDialog} onOpenChange={setShowFiguraDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingFigura ? 'Modifica' : 'Nuova'} Figura Professionale</DialogTitle>
            <DialogDescription>
              Configura il nome, le tipologie di accesso e la nota per i pacchetti sollievo
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSaveFigura} className="space-y-6 mt-4">
            <div className="space-y-2">
              <Label>Nome Figura Professionale *</Label>
              <Input
                value={newFigura.nome}
                onChange={(e) => setNewFigura({...newFigura, nome: e.target.value})}
                placeholder="Es: Terapista Occupazionale"
                required
              />
            </div>

            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <Label>Tipologie di Accesso *</Label>
                <Button type="button" onClick={addTipologia} variant="outline" size="sm" className="gap-2">
                  <Plus className="w-4 h-4" />
                  Aggiungi Tipologia
                </Button>
              </div>
              
              {newFigura.tipologie_accessi.map((tipo, index) => (
                <div key={index} className="flex gap-3 items-end">
                  <div className="flex-1">
                    <Label className="text-xs">Nome Tipologia</Label>
                    <Input
                      value={tipo.nome}
                      onChange={(e) => updateTipologia(index, 'nome', e.target.value)}
                      placeholder="Es: bassa, standard, etc"
                      required
                    />
                  </div>
                  <div className="w-32">
                    <Label className="text-xs">Minuti</Label>
                    <Input
                      type="number"
                      min="0"
                      value={tipo.minuti}
                      onChange={(e) => updateTipologia(index, 'minuti', e.target.value)}
                      required
                    />
                  </div>
                  {newFigura.tipologie_accessi.length > 1 && (
                    <Button 
                      type="button" 
                      onClick={() => removeTipologia(index)} 
                      variant="outline" 
                      size="sm"
                      className="text-red-600"
                    >
                      Rimuovi
                    </Button>
                  )}
                </div>
              ))}
            </div>

            <div className="space-y-2">
              <Label>Nota Pacchetti Sollievo (opzionale)</Label>
              <Textarea
                value={newFigura.nota_pacchetti}
                onChange={(e) => setNewFigura({...newFigura, nota_pacchetti: e.target.value})}
                placeholder="Es: Per i pacchetti multipli, indicare il numero di ore nel campo..."
                rows={3}
              />
            </div>

            <div className="flex gap-3 pt-4">
              <Button
                type="submit"
                className="flex-1 bg-cyan-600 hover:bg-cyan-700 text-white"
              >
                {editingFigura ? 'Aggiorna' : 'Crea'} Figura
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setShowFiguraDialog(false);
                  setEditingFigura(null);
                }}
              >
                Annulla
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Settings Dialog */}
      <Dialog open={showSettings} onOpenChange={setShowSettings}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Impostazioni Sistema</DialogTitle>
            <DialogDescription>Configura tempo di spostamento e costi</DialogDescription>
          </DialogHeader>
          <div className="space-y-6 mt-4">
            <div className="space-y-2">
              <Label>Tempo Spostamento per Accesso (minuti)</Label>
              <Input
                type="number"
                min="0"
                value={settings.tempo_spostamento}
                onChange={(e) => setSettings({...settings, tempo_spostamento: parseInt(e.target.value) || 0})}
              />
              <p className="text-xs text-gray-500">
                Tempo aggiunto automaticamente per ogni accesso (default: 10 minuti)
              </p>
            </div>

            <div className="space-y-4">
              <h3 className="font-semibold">Gestione Costi</h3>
              <p className="text-sm text-gray-600">
                Imposta i costi per ogni figura professionale e tipologia di accesso
              </p>
              
              {settings && settings.figure_professionali && settings.figure_professionali.map(figuraObj => {
                const figuraNome = figuraObj.nome;
                const tipologieAccessi = figuraObj.tipologie_accessi || [];
                
                return (
                  <div key={figuraObj.id} className="border rounded-lg p-4 space-y-3">
                    <h4 className="font-medium">{figuraNome}</h4>
                    <div className="grid grid-cols-3 gap-3">
                      {tipologieAccessi.map(tipoObj => {
                        const tipoNome = tipoObj.nome;
                        const currentCosto = settings.costi?.[figuraNome]?.[tipoNome] || 0;
                        
                        return (
                          <div key={tipoNome} className="space-y-1">
                            <Label className="text-xs capitalize">{tipoNome} ({tipoObj.minuti}min)</Label>
                            <Input
                              type="number"
                              step="0.01"
                              min="0"
                              placeholder="0.00"
                              value={currentCosto}
                              onChange={(e) => {
                                const newCosti = {...(settings.costi || {})};
                                if (!newCosti[figuraNome]) newCosti[figuraNome] = {};
                                newCosti[figuraNome][tipoNome] = parseFloat(e.target.value) || 0;
                                setSettings({...settings, costi: newCosti});
                              }}
                            />
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="flex gap-3 pt-4">
              <Button
                onClick={handleUpdateSettings}
                className="flex-1 bg-cyan-600 hover:bg-cyan-700 text-white"
              >
                Salva Impostazioni
              </Button>
              <Button
                variant="outline"
                onClick={() => setShowSettings(false)}
              >
                Annulla
              </Button>
            </div>
            {user.role === 'super_amministratore' && (
              <div className="mt-6 pt-4 border-t border-gray-200">
                <Label className="text-xs font-semibold text-gray-700 uppercase tracking-wide">Diagnostica sistema</Label>
                <p className="text-[11px] text-gray-500 mt-1 mb-2">Strumenti tecnici per verificare la configurazione del sistema. Riservato a Super Amministratore.</p>
                <Button
                  type="button"
                  data-testid="btn-test-email"
                  variant="outline"
                  size="sm"
                  onClick={async () => {
                    const defaultEmail = user.email || '';
                    const email = window.prompt("Invia un'email di prova a:", defaultEmail);
                    if (!email) return;
                    try {
                      const res = await axios.post(`${API}/admin/test-email`, { to_email: email });
                      toast.success(res.data.message || 'Email inviata');
                    } catch (err) {
                      toast.error(err.response?.data?.detail || 'Invio fallito');
                    }
                  }}
                  className="h-9 gap-1.5 text-xs border-gray-200 text-gray-600 hover:bg-gray-50"
                >
                  <Mail className="w-3.5 h-3.5" /> Verifica configurazione SMTP (invia email di prova)
                </Button>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Organigramma & Qualifiche Panel */}
      <OrganigrammaPanel
        open={showOrganigramma}
        onClose={() => { setShowOrganigramma(false); fetchData(); }}
        currentUser={user}
      />

      {/* Importa Dati HR (Excel) - Super Admin */}
      <HRImportPanel
        open={showHRImport}
        onClose={() => { setShowHRImport(false); fetchData(); }}
      />

      {/* Approve Report Dialog */}
      <Dialog open={showApproveDialog} onOpenChange={setShowApproveDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Conferma Approvazione Report</DialogTitle>
            <DialogDescription>
              Sei sicuro di voler approvare questo report?
            </DialogDescription>
          </DialogHeader>
          {selectedReport && (
            <div className="py-4">
              <p className="text-sm text-gray-700">
                <span className="font-semibold">Operatore:</span> {selectedReport.full_name}
              </p>
              <p className="text-sm text-gray-700">
                <span className="font-semibold">Periodo:</span> {selectedReport.month}/{selectedReport.year}
              </p>
            </div>
          )}
          <div className="flex gap-3">
            <Button
              onClick={handleApproveReport}
              className="flex-1 bg-green-600 hover:bg-green-700 text-white"
            >
              Conferma Approvazione
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                setShowApproveDialog(false);
                setSelectedReport(null);
              }}
            >
              Annulla
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Reject Report Dialog */}
      <Dialog open={showRejectDialog} onOpenChange={setShowRejectDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Rifiuta Report</DialogTitle>
            <DialogDescription>
              Inserisci la motivazione del rifiuto
            </DialogDescription>
          </DialogHeader>
          {selectedReport && (
            <div className="space-y-4">
              <div>
                <p className="text-sm text-gray-700">
                  <span className="font-semibold">Operatore:</span> {selectedReport.full_name}
                </p>
                <p className="text-sm text-gray-700">
                  <span className="font-semibold">Periodo:</span> {selectedReport.month}/{selectedReport.year}
                </p>
              </div>
              <div className="space-y-2">
                <Label>Motivazione Rifiuto *</Label>
                <Textarea
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  placeholder="Inserisci la motivazione del rifiuto..."
                  rows={4}
                />
              </div>
            </div>
          )}
          <div className="flex gap-3">
            <Button
              onClick={handleRejectReport}
              className="flex-1 bg-yellow-600 hover:bg-yellow-700 text-white"
            >
              Conferma Rifiuto
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                setShowRejectDialog(false);
                setSelectedReport(null);
                setRejectionReason('');
              }}
            >
              Annulla
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Report Dialog */}
      <Dialog open={showDeleteReportDialog} onOpenChange={setShowDeleteReportDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Conferma Eliminazione Report</DialogTitle>
            <DialogDescription>
              Questa azione è irreversibile. Il report verrà eliminato permanentemente.
            </DialogDescription>
          </DialogHeader>
          {selectedReport && (
            <div className="py-4">
              <p className="text-sm text-gray-700">
                <span className="font-semibold">Operatore:</span> {selectedReport.full_name}
              </p>
              <p className="text-sm text-gray-700">
                <span className="font-semibold">Periodo:</span> {selectedReport.month}/{selectedReport.year}
              </p>
            </div>
          )}
          <div className="flex gap-3">
            <Button
              onClick={handleDeleteReport}
              className="flex-1 bg-red-600 hover:bg-red-700 text-white"
            >
              Elimina Report
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                setShowDeleteReportDialog(false);
                setSelectedReport(null);
              }}
            >
              Annulla
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog Configurazione Billing */}
      <Dialog open={showBillingConfigDialog} onOpenChange={setShowBillingConfigDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Configurazione Costi - {selectedInvoiceUser?.full_name}</DialogTitle>
            <DialogDescription>
              Configura i costi per tipologia di accesso e i parametri di calcolo fattura
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-6 mt-4">
            {/* Costi per Accesso */}
            <div>
              <h3 className="font-semibold mb-3">Costi per Tipologia Accesso</h3>
              {selectedInvoiceUser && settings.figure_professionali
                ?.find(f => f.nome === selectedInvoiceUser.figura_professionale)
                ?.tipologie_accessi.map((tipo, idx) => (
                  <div key={idx} className="flex items-center gap-3 mb-2">
                    <Label className="w-48">{tipo.nome}</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={billingConfig.costi_per_accesso[tipo.nome] || 0}
                      onChange={(e) => setBillingConfig({
                        ...billingConfig,
                        costi_per_accesso: {
                          ...billingConfig.costi_per_accesso,
                          [tipo.nome]: parseFloat(e.target.value) || 0
                        }
                      })}
                      className="flex-1"
                    />
                    <span className="text-gray-600">€</span>
                  </div>
                ))
              }
            </div>
            
            {/* Parametri Calcolo */}
            <div>
              <h3 className="font-semibold mb-3">Parametri Calcolo</h3>
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <Label className="w-48">Percentuale Cassa Prev.</Label>
                  <Input
                    type="number"
                    step="0.1"
                    value={billingConfig.percentuale_cassa_previdenziale}
                    onChange={(e) => setBillingConfig({
                      ...billingConfig,
                      percentuale_cassa_previdenziale: parseFloat(e.target.value) || 0
                    })}
                    className="flex-1"
                  />
                  <span className="text-gray-600">%</span>
                </div>
                <div className="flex items-center gap-3">
                  <Label className="w-48">Reperibilità Default</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={billingConfig.importo_reperibilita_default}
                    onChange={(e) => setBillingConfig({
                      ...billingConfig,
                      importo_reperibilita_default: parseFloat(e.target.value) || 0
                    })}
                    className="flex-1"
                  />
                  <span className="text-gray-600">€</span>
                </div>
                <div className="flex items-center gap-3">
                  <Label className="w-48">Costo Auto Default</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={billingConfig.costo_auto_default}
                    onChange={(e) => setBillingConfig({
                      ...billingConfig,
                      costo_auto_default: parseFloat(e.target.value) || 0
                    })}
                    className="flex-1"
                  />
                  <span className="text-gray-600">€</span>
                </div>
              </div>
            </div>
            
            <div className="flex gap-3">
              <Button onClick={saveBillingConfig} className="flex-1">
                Salva Configurazione
              </Button>
              <Button 
                variant="outline" 
                onClick={() => setShowBillingConfigDialog(false)}
              >
                Annulla
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog Creazione Fattura */}
      <Dialog open={showInvoiceDialog} onOpenChange={setShowInvoiceDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Nuova Fattura - {selectedInvoiceUser?.full_name}</DialogTitle>
            <DialogDescription>
              Inserisci manualmente i dati per la fattura mensile
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreateInvoice} className="space-y-6 mt-4">
            {/* Periodo */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Mese *</Label>
                <select
                  value={newInvoice.month}
                  onChange={(e) => setNewInvoice({...newInvoice, month: parseInt(e.target.value)})}
                  className="w-full h-10 px-3 rounded-md border border-gray-300 bg-white"
                  required
                >
                  {months.map((m, i) => <option key={i} value={i+1}>{m}</option>)}
                </select>
              </div>
              <div className="space-y-2">
                <Label>Anno *</Label>
                <select
                  value={newInvoice.year}
                  onChange={(e) => setNewInvoice({...newInvoice, year: parseInt(e.target.value)})}
                  className="w-full h-10 px-3 rounded-md border border-gray-300 bg-white"
                  required
                >
                  {[2024, 2025, 2026].map(y => <option key={y} value={y}>{y}</option>)}
                </select>
              </div>
            </div>

            {/* ASL e Note */}
            <div className="grid grid-cols-1 gap-4">
              <div className="space-y-2">
                <Label>ASL di Riferimento</Label>
                <Input
                  type="text"
                  placeholder="es. Asl Pescara"
                  value={newInvoice.asl_riferimento}
                  onChange={(e) => setNewInvoice({...newInvoice, asl_riferimento: e.target.value})}
                />
                <p className="text-xs text-gray-500">Permette di creare più fatture per lo stesso mese</p>
              </div>
              <div className="space-y-2">
                <Label>Note</Label>
                <Textarea
                  placeholder="Note aggiuntive..."
                  value={newInvoice.note}
                  onChange={(e) => setNewInvoice({...newInvoice, note: e.target.value})}
                  rows={3}
                />
              </div>
            </div>

            {/* Accessi */}
            <div>
              <h3 className="font-semibold mb-3">Accessi del Mese</h3>
              <div className="space-y-2">
                {newInvoice.accessi_data.map((accesso, idx) => (
                  <div key={idx} className="grid grid-cols-4 gap-2 items-end">
                    <div className="col-span-1">
                      <Label className="text-xs">{accesso.tipo_accesso}</Label>
                    </div>
                    <div>
                      <Label className="text-xs">N. Accessi</Label>
                      <Input
                        type="number"
                        min="0"
                        value={accesso.numero_accessi}
                        onChange={(e) => updateInvoiceAccesso(idx, 'numero_accessi', e.target.value)}
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Costo €</Label>
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        value={accesso.costo_unitario}
                        onChange={(e) => updateInvoiceAccesso(idx, 'costo_unitario', e.target.value)}
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Totale</Label>
                      <Input
                        type="text"
                        value={`€${accesso.totale.toFixed(2)}`}
                        disabled
                        className="bg-gray-100"
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Reperibilità e Costo Auto */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Importo Reperibilità</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={newInvoice.importo_reperibilita}
                  onChange={(e) => setNewInvoice({...newInvoice, importo_reperibilita: parseFloat(e.target.value) || 0})}
                />
              </div>
              <div className="space-y-2">
                <Label>Costo Auto (negativo)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={newInvoice.costo_auto}
                  onChange={(e) => setNewInvoice({...newInvoice, costo_auto: parseFloat(e.target.value) || 0})}
                />
              </div>
            </div>

            {/* Voci Libere */}
            <div>
              <div className="flex justify-between items-center mb-3">
                <h3 className="font-semibold">Voci Aggiuntive</h3>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    setNewInvoice({
                      ...newInvoice,
                      voci_libere: [...newInvoice.voci_libere, { descrizione: '', importo: 0, escludi_da_imponibile: false }]
                    });
                  }}
                  className="gap-1"
                >
                  <Plus className="w-4 h-4" />
                  Aggiungi Voce
                </Button>
              </div>
              {newInvoice.voci_libere.length > 0 && (
                <div className="space-y-2">
                  {newInvoice.voci_libere.map((voce, idx) => (
                    <div key={idx} className="space-y-2 p-3 border rounded-lg bg-gray-50">
                      <div className="grid grid-cols-12 gap-2 items-end">
                        <div className="col-span-6">
                          <Label className="text-xs">Descrizione</Label>
                          <Input
                            type="text"
                            placeholder="es. Rimborso km, Indennità età"
                            value={voce.descrizione}
                            onChange={(e) => {
                              const updated = [...newInvoice.voci_libere];
                              updated[idx].descrizione = e.target.value;
                              setNewInvoice({...newInvoice, voci_libere: updated});
                            }}
                          />
                        </div>
                        <div className="col-span-3">
                          <Label className="text-xs">Importo €</Label>
                          <Input
                            type="number"
                            step="0.01"
                            value={voce.importo}
                            onChange={(e) => {
                              const updated = [...newInvoice.voci_libere];
                              updated[idx].importo = parseFloat(e.target.value) || 0;
                              setNewInvoice({...newInvoice, voci_libere: updated});
                            }}
                          />
                        </div>
                        <div className="col-span-2 flex items-center">
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={voce.escludi_da_imponibile || false}
                              onChange={(e) => {
                                const updated = [...newInvoice.voci_libere];
                                updated[idx].escludi_da_imponibile = e.target.checked;
                                setNewInvoice({...newInvoice, voci_libere: updated});
                              }}
                              className="w-4 h-4 text-cyan-600 rounded"
                            />
                            <span className="text-xs">Escludi da imponibile</span>
                          </label>
                        </div>
                        <div className="col-span-1">
                          <Button
                            type="button"
                            size="sm"
                            variant="ghost"
                            onClick={() => {
                              setNewInvoice({
                                ...newInvoice,
                                voci_libere: newInvoice.voci_libere.filter((_, i) => i !== idx)
                              });
                            }}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {newInvoice.voci_libere.length === 0 && (
                <p className="text-sm text-gray-500 italic">Nessuna voce aggiuntiva. Clicca "Aggiungi Voce" per inserire rimborsi o indennità.</p>
              )}
            </div>

            {/* Riepilogo */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <h3 className="font-semibold mb-2">Riepilogo</h3>
              <div className="space-y-1 text-sm">
                <div className="flex justify-between">
                  <span>Totale Accessi:</span>
                  <span>€{newInvoice.accessi_data.reduce((sum, a) => sum + a.totale, 0).toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span>Reperibilità:</span>
                  <span>€{newInvoice.importo_reperibilita.toFixed(2)}</span>
                </div>
                {newInvoice.voci_libere.length > 0 && (
                  <>
                    {newInvoice.voci_libere.map((voce, idx) => (
                      <div key={idx} className="flex justify-between text-blue-600">
                        <span>{voce.descrizione || `Voce ${idx + 1}`}:</span>
                        <span>€{voce.importo.toFixed(2)}</span>
                      </div>
                    ))}
                  </>
                )}
                <div className="flex justify-between font-medium">
                  <span>Totale Lordo:</span>
                  <span>€{(
                    newInvoice.accessi_data.reduce((sum, a) => sum + a.totale, 0) + 
                    newInvoice.importo_reperibilita +
                    newInvoice.voci_libere.reduce((sum, v) => sum + v.importo, 0)
                  ).toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-red-600">
                  <span>Costo Auto:</span>
                  <span>€{newInvoice.costo_auto.toFixed(2)}</span>
                </div>
                <div className="border-t pt-1 mt-1">
                  <p className="text-xs text-gray-600">Il calcolo finale sarà completato dal sistema includendo la cassa previdenziale</p>
                </div>
              </div>
            </div>

            <div className="flex gap-3">
              <Button type="submit" className="flex-1 bg-green-600 hover:bg-green-700">
                Crea Fattura
              </Button>
              <Button 
                type="button"
                variant="outline" 
                onClick={() => setShowInvoiceDialog(false)}
              >
                Annulla
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Dialog Modifica Fattura */}
      <Dialog open={showEditInvoiceDialog} onOpenChange={setShowEditInvoiceDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-orange-700">Modifica Conteggio - {selectedInvoiceUser?.full_name}</DialogTitle>
            <DialogDescription>
              Correggi i dati della fattura e ricalcola gli importi
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleUpdateInvoice} className="space-y-6 mt-4">
            {/* Alert richiesta verifica */}
            <div className="bg-orange-50 border border-orange-200 rounded-lg p-3">
              <p className="text-sm text-orange-800 font-medium">
                ⚠️ Modifica i dati necessari. Al salvataggio, la fattura verrà ricalcolata e l'operatore riceverà una notifica email.
              </p>
            </div>

            {/* Periodo */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Mese *</Label>
                <select
                  value={newInvoice.month}
                  onChange={(e) => setNewInvoice({...newInvoice, month: parseInt(e.target.value)})}
                  className="w-full h-10 px-3 rounded-md border border-gray-300 bg-white"
                  required
                >
                  {months.map((m, i) => <option key={i} value={i+1}>{m}</option>)}
                </select>
              </div>
              <div className="space-y-2">
                <Label>Anno *</Label>
                <Input
                  type="number"
                  value={newInvoice.year}
                  onChange={(e) => setNewInvoice({...newInvoice, year: parseInt(e.target.value)})}
                  required
                />
              </div>
            </div>

            {/* ASL e Note */}
            <div className="grid grid-cols-1 gap-4">
              <div className="space-y-2">
                <Label>ASL di Riferimento</Label>
                <Input
                  type="text"
                  placeholder="es. Asl Pescara"
                  value={newInvoice.asl_riferimento}
                  onChange={(e) => setNewInvoice({...newInvoice, asl_riferimento: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <Label>Note</Label>
                <Textarea
                  placeholder="Note aggiuntive..."
                  value={newInvoice.note}
                  onChange={(e) => setNewInvoice({...newInvoice, note: e.target.value})}
                  rows={2}
                />
              </div>
            </div>

            {/* Accessi */}
            <div>
              <h3 className="font-semibold mb-3">Accessi</h3>
              <div className="space-y-3">
                {newInvoice.accessi_data.map((accesso, idx) => (
                  <div key={idx} className="grid grid-cols-12 gap-2 items-end p-3 bg-gray-50 rounded-lg">
                    <div className="col-span-6">
                      <Label className="text-xs">Tipo Accesso</Label>
                      <Input
                        value={accesso.tipo_accesso}
                        disabled
                        className="bg-gray-100"
                      />
                    </div>
                    <div className="col-span-3">
                      <Label className="text-xs">N. Accessi</Label>
                      <Input
                        type="number"
                        value={accesso.numero_accessi}
                        onChange={(e) => {
                          const updated = [...newInvoice.accessi_data];
                          updated[idx].numero_accessi = parseInt(e.target.value) || 0;
                          updated[idx].totale = updated[idx].numero_accessi * updated[idx].costo_unitario;
                          setNewInvoice({...newInvoice, accessi_data: updated});
                        }}
                      />
                    </div>
                    <div className="col-span-3">
                      <Label className="text-xs">Totale</Label>
                      <Input
                        value={`€${accesso.totale.toFixed(2)}`}
                        disabled
                        className="bg-gray-100"
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Reperibilità e Costo Auto */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Importo Reperibilità</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={newInvoice.importo_reperibilita}
                  onChange={(e) => setNewInvoice({...newInvoice, importo_reperibilita: parseFloat(e.target.value) || 0})}
                />
              </div>
              <div className="space-y-2">
                <Label>Costo Auto (negativo)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={newInvoice.costo_auto}
                  onChange={(e) => setNewInvoice({...newInvoice, costo_auto: parseFloat(e.target.value) || 0})}
                />
              </div>
            </div>

            {/* Voci Libere */}
            <div>
              <div className="flex justify-between items-center mb-3">
                <h3 className="font-semibold">Voci Aggiuntive</h3>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    setNewInvoice({
                      ...newInvoice,
                      voci_libere: [...newInvoice.voci_libere, { descrizione: '', importo: 0, escludi_da_imponibile: false }]
                    });
                  }}
                  className="gap-1"
                >
                  <Plus className="w-4 h-4" />
                  Aggiungi Voce
                </Button>
              </div>
              {newInvoice.voci_libere.length > 0 && (
                <div className="space-y-2">
                  {newInvoice.voci_libere.map((voce, idx) => (
                    <div key={idx} className="space-y-2 p-3 border rounded-lg bg-gray-50">
                      <div className="grid grid-cols-12 gap-2 items-end">
                        <div className="col-span-6">
                          <Label className="text-xs">Descrizione</Label>
                          <Input
                            type="text"
                            placeholder="es. Rimborso km, Indennità età"
                            value={voce.descrizione}
                            onChange={(e) => {
                              const updated = [...newInvoice.voci_libere];
                              updated[idx].descrizione = e.target.value;
                              setNewInvoice({...newInvoice, voci_libere: updated});
                            }}
                          />
                        </div>
                        <div className="col-span-3">
                          <Label className="text-xs">Importo €</Label>
                          <Input
                            type="number"
                            step="0.01"
                            value={voce.importo}
                            onChange={(e) => {
                              const updated = [...newInvoice.voci_libere];
                              updated[idx].importo = parseFloat(e.target.value) || 0;
                              setNewInvoice({...newInvoice, voci_libere: updated});
                            }}
                          />
                        </div>
                        <div className="col-span-2 flex items-center">
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={voce.escludi_da_imponibile || false}
                              onChange={(e) => {
                                const updated = [...newInvoice.voci_libere];
                                updated[idx].escludi_da_imponibile = e.target.checked;
                                setNewInvoice({...newInvoice, voci_libere: updated});
                              }}
                              className="w-4 h-4 text-cyan-600 rounded"
                            />
                            <span className="text-xs">Escludi da imponibile</span>
                          </label>
                        </div>
                        <div className="col-span-1">
                          <Button
                            type="button"
                            size="sm"
                            variant="ghost"
                            onClick={() => {
                              setNewInvoice({
                                ...newInvoice,
                                voci_libere: newInvoice.voci_libere.filter((_, i) => i !== idx)
                              });
                            }}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Riepilogo */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <h3 className="font-semibold mb-2">Riepilogo</h3>
              <div className="space-y-1 text-sm">
                <div className="flex justify-between">
                  <span>Totale Accessi:</span>
                  <span>€{newInvoice.accessi_data.reduce((sum, a) => sum + a.totale, 0).toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span>Reperibilità:</span>
                  <span>€{newInvoice.importo_reperibilita.toFixed(2)}</span>
                </div>
                {newInvoice.voci_libere.length > 0 && (
                  <>
                    {newInvoice.voci_libere.map((voce, idx) => (
                      <div key={idx} className="flex justify-between text-blue-600">
                        <span>{voce.descrizione || `Voce ${idx + 1}`}:</span>
                        <span>€{voce.importo.toFixed(2)}</span>
                      </div>
                    ))}
                  </>
                )}
                <div className="flex justify-between font-medium">
                  <span>Totale Lordo:</span>
                  <span>€{(
                    newInvoice.accessi_data.reduce((sum, a) => sum + a.totale, 0) + 
                    newInvoice.importo_reperibilita +
                    newInvoice.voci_libere.reduce((sum, v) => sum + v.importo, 0)
                  ).toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-red-600">
                  <span>Costo Auto:</span>
                  <span>€{newInvoice.costo_auto.toFixed(2)}</span>
                </div>
              </div>
            </div>

            <div className="flex gap-3">
              <Button type="submit" className="flex-1 bg-orange-600 hover:bg-orange-700">
                Aggiorna e Ricalcola Fattura
              </Button>
              <Button 
                type="button"
                variant="outline" 
                onClick={() => setShowEditInvoiceDialog(false)}
              >
                Annulla
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Dialog Dettaglio Fattura */}
      <Dialog open={showInvoiceDetailDialog} onOpenChange={setShowInvoiceDetailDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Dettaglio Fattura</DialogTitle>
            <DialogDescription>
              Visualizza il riepilogo completo della fattura
            </DialogDescription>
          </DialogHeader>
          {selectedInvoice && (
            <div className="space-y-6 mt-4">
              {/* Intestazione */}
              <div className="bg-gradient-to-r from-cyan-50 to-blue-50 p-4 rounded-lg border border-cyan-200">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <h3 className="font-bold text-lg mb-2">
                      {users.find(u => u.id === selectedInvoice.user_id)?.full_name || 'N/A'}
                    </h3>
                    <p className="text-sm text-gray-700">
                      {users.find(u => u.id === selectedInvoice.user_id)?.figura_professionale || 'N/A'}
                    </p>
                    <p className="text-sm text-gray-600">
                      {users.find(u => u.id === selectedInvoice.user_id)?.asl_riferimento || 'N/A'}
                    </p>
                  </div>
                  <div className="text-left md:text-right">
                    <p className="text-sm text-gray-600">Periodo</p>
                    <p className="font-bold text-lg">
                      {months[selectedInvoice.month - 1]} {selectedInvoice.year}
                    </p>
                    <div className="mt-2">
                      {selectedInvoice.status === 'pending' && (
                        <span className="px-3 py-1 bg-yellow-100 text-yellow-800 text-sm rounded-full">In Attesa</span>
                      )}
                      {selectedInvoice.status === 'viewed' && (
                        <span className="px-3 py-1 bg-blue-100 text-blue-800 text-sm rounded-full">Visto</span>
                      )}
                      {selectedInvoice.status === 'uploaded' && (
                        <span className="px-3 py-1 bg-green-100 text-green-800 text-sm rounded-full">Caricata</span>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* ASL e Note */}
              {(selectedInvoice.asl_riferimento || selectedInvoice.note) && (
                <div className="border rounded-lg p-4 bg-blue-50">
                  <h4 className="font-semibold mb-2">Informazioni Aggiuntive</h4>
                  <div className="text-sm space-y-1">
                    {selectedInvoice.asl_riferimento && (
                      <p><span className="font-medium">ASL:</span> {selectedInvoice.asl_riferimento}</p>
                    )}
                    {selectedInvoice.note && (
                      <p><span className="font-medium">Note:</span> {selectedInvoice.note}</p>
                    )}
                  </div>
                </div>
              )}

              {/* Richiesta Verifica Operatore */}
              {selectedInvoice.richiesta_verifica && selectedInvoice.nota_operatore && (
                <div className="border-2 border-orange-300 rounded-lg p-4 bg-orange-50">
                  <div className="flex items-start gap-3">
                    <svg className="w-6 h-6 text-orange-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                    <div className="flex-1">
                      <h4 className="font-semibold text-orange-800 mb-2">⚠️ Richiesta Verifica dall'Operatore</h4>
                      <p className="text-sm text-gray-700 whitespace-pre-wrap">{selectedInvoice.nota_operatore}</p>
                      <Button
                        className="mt-3 bg-orange-600 hover:bg-orange-700"
                        size="sm"
                        onClick={() => openEditInvoiceDialog(selectedInvoice)}
                      >
                        Modifica Conteggio
                      </Button>
                    </div>
                  </div>
                </div>
              )}

              {/* Destinatario */}
              <div className="border rounded-lg p-4 bg-gray-50">
                <h4 className="font-semibold mb-2">Destinatario</h4>
                <div className="text-sm space-y-1">
                  <p className="font-medium">Cooperativa Sociale Croce Gialla soc.coop.</p>
                  <p>Contrada Mozzoni 13</p>
                  <p>66030 Treglio CH</p>
                  <p>P.I. 02501790691</p>
                  <p>SDI: M5UXCR1</p>
                </div>
              </div>

              {/* Dettaglio Accessi */}
              <div>
                <h4 className="font-semibold mb-3">Dettaglio Accessi</h4>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Tipologia</TableHead>
                        <TableHead className="text-center">N. Accessi</TableHead>
                        <TableHead className="text-right">Costo Unitario</TableHead>
                        <TableHead className="text-right">Totale</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {selectedInvoice.accessi_data.map((accesso, idx) => (
                        <TableRow key={idx}>
                          <TableCell className="font-medium">{accesso.tipo_accesso}</TableCell>
                          <TableCell className="text-center">{accesso.numero_accessi}</TableCell>
                          <TableCell className="text-right">€{accesso.costo_unitario.toFixed(2)}</TableCell>
                          <TableCell className="text-right">€{accesso.totale.toFixed(2)}</TableCell>
                        </TableRow>
                      ))}
                      <TableRow className="bg-gray-50 font-semibold">
                        <TableCell colSpan={3} className="text-right">Totale Accessi:</TableCell>
                        <TableCell className="text-right">€{selectedInvoice.totale_accessi.toFixed(2)}</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </div>
              </div>

              {/* Calcolo Fattura */}
              <div className="border rounded-lg p-4 bg-gradient-to-br from-green-50 to-cyan-50">
                <h4 className="font-semibold mb-3">Calcolo Fattura</h4>
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span>Totale Accessi:</span>
                    <span className="font-medium">€{selectedInvoice.totale_accessi.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span>Reperibilità:</span>
                    <span className="font-medium">€{selectedInvoice.importo_reperibilita.toFixed(2)}</span>
                  </div>
                  {selectedInvoice.voci_libere && selectedInvoice.voci_libere.length > 0 && (
                    <>
                      {selectedInvoice.voci_libere.map((voce, idx) => (
                        <div key={idx} className="flex justify-between text-sm text-blue-600">
                          <span>{voce.descrizione}:</span>
                          <span className="font-medium">€{voce.importo.toFixed(2)}</span>
                        </div>
                      ))}
                    </>
                  )}
                  <div className="flex justify-between border-t pt-2 font-semibold">
                    <span>Totale Lordo:</span>
                    <span>€{selectedInvoice.totale_lordo.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-sm text-red-600">
                    <span>Costo Auto:</span>
                    <span className="font-medium">€{selectedInvoice.costo_auto.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between border-t pt-2">
                    <span>Imponibile:</span>
                    <span className="font-medium">€{selectedInvoice.imponibile.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span>Cassa Previdenziale ({selectedInvoice.percentuale_cassa}%):</span>
                    <span className="font-medium">€{selectedInvoice.importo_cassa_previdenziale.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between border-t-2 border-green-600 pt-2 text-lg font-bold text-green-600">
                    <span>TOTALE NETTO:</span>
                    <span>€{selectedInvoice.totale_netto.toFixed(2)}</span>
                  </div>
                </div>
              </div>

              {/* Estremi Fattura (se caricata) */}
              {selectedInvoice.numero_fattura && (
                <div className="border rounded-lg p-4 bg-blue-50">
                  <h4 className="font-semibold mb-2">Estremi Fattura</h4>
                  <div className="text-sm space-y-1">
                    <p><span className="font-medium">Numero:</span> {selectedInvoice.numero_fattura}</p>
                    <p><span className="font-medium">Data:</span> {selectedInvoice.data_fattura}</p>
                    {selectedInvoice.uploaded_at && (
                      <p className="text-xs text-gray-600 mt-2">
                        Caricata il: {new Date(selectedInvoice.uploaded_at).toLocaleString('it-IT')}
                      </p>
                    )}
                  </div>
                </div>
              )}

              <div className="flex justify-end">
                <Button 
                  variant="outline" 
                  onClick={() => setShowInvoiceDetailDialog(false)}
                >
                  Chiudi
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Dialog Upload Estremi Fattura - Super Admin */}
      <Dialog open={showAdminUploadInvoiceDialog} onOpenChange={setShowAdminUploadInvoiceDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {selectedInvoice?.numero_fattura ? 'Modifica Estremi Fattura' : 'Inserisci Estremi Fattura'}
            </DialogTitle>
            <DialogDescription>
              Inserisci i dati della fattura ricevuta dall'operatore
            </DialogDescription>
          </DialogHeader>
          {selectedInvoice && (
            <form onSubmit={handleAdminUploadInvoice} className="space-y-4 mt-4">
              <div className="bg-cyan-50 border border-cyan-200 rounded-lg p-3">
                <p className="text-sm font-medium text-cyan-900">
                  {users.find(u => u.id === selectedInvoice.user_id)?.full_name || 'N/A'}
                </p>
                <p className="text-xs text-cyan-700">
                  {months[selectedInvoice.month - 1]} {selectedInvoice.year} - €{selectedInvoice.totale_netto.toFixed(2)}
                </p>
              </div>

              <div className="space-y-2">
                <Label>Data Fattura *</Label>
                <Input
                  type="date"
                  value={adminInvoiceUploadData.data_fattura}
                  onChange={(e) => setAdminInvoiceUploadData({...adminInvoiceUploadData, data_fattura: e.target.value})}
                  required
                />
              </div>
              
              <div className="space-y-2">
                <Label>Numero Fattura *</Label>
                <Input
                  type="text"
                  value={adminInvoiceUploadData.numero_fattura}
                  onChange={(e) => setAdminInvoiceUploadData({...adminInvoiceUploadData, numero_fattura: e.target.value})}
                  placeholder="Es: 2024/001"
                  required
                />
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <p className="text-sm text-blue-800">
                  <span className="font-semibold">Nota:</span> Inserisci gli estremi della fattura dopo averla ricevuta dall'operatore.
                </p>
              </div>

              <div className="flex gap-3">
                <Button type="submit" className="flex-1 bg-green-600 hover:bg-green-700">
                  Salva Estremi
                </Button>
                <Button 
                  type="button"
                  variant="outline" 
                  onClick={() => setShowAdminUploadInvoiceDialog(false)}
                >
                  Annulla
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Dialog Crea/Modifica Convenzione */}
      <Dialog open={showConvenzioneDialog} onOpenChange={setShowConvenzioneDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col p-0">
          <div className="px-6 pt-6">
            <DialogHeader>
              <DialogTitle>{isEditingConvenzione ? 'Modifica' : 'Nuova'} Convenzione</DialogTitle>
              <DialogDescription>
                {isEditingConvenzione ? 'Modifica i dettagli della convenzione' : 'Crea una nuova convenzione per gli operatori'}
              </DialogDescription>
            </DialogHeader>
          </div>
          <div className="overflow-y-auto px-6 pb-6">
            <form onSubmit={handleSaveConvenzione} className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label htmlFor="titolo">Titolo *</Label>
              <Input
                id="titolo"
                value={convenzioneForm.titolo}
                onChange={(e) => setConvenzioneForm({...convenzioneForm, titolo: e.target.value})}
                placeholder="es. Sconto Apple Store"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="descrizione">Descrizione *</Label>
              <Textarea
                id="descrizione"
                value={convenzioneForm.descrizione}
                onChange={(e) => setConvenzioneForm({...convenzioneForm, descrizione: e.target.value})}
                placeholder="Descrivi i dettagli della convenzione..."
                rows={4}
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="categoria">Categoria *</Label>
                <select
                  id="categoria"
                  value={convenzioneForm.categoria}
                  onChange={(e) => setConvenzioneForm({...convenzioneForm, categoria: e.target.value})}
                  className="w-full h-10 px-3 rounded-md border border-gray-300 bg-white"
                  required
                >
                  <option value="moda">Moda</option>
                  <option value="viaggi">Viaggi</option>
                  <option value="tecnologia">Tecnologia</option>
                  <option value="tempo_libero">Tempo Libero</option>
                  <option value="benessere">Benessere</option>
                  <option value="casa">Casa</option>
                  <option value="alimentari">Alimentari</option>
                  <option value="sport">Sport</option>
                  <option value="altro">Altro</option>
                </select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="offerta">Offerta / Sconto *</Label>
                <Input
                  id="offerta"
                  value={convenzioneForm.offerta}
                  onChange={(e) => setConvenzioneForm({...convenzioneForm, offerta: e.target.value})}
                  placeholder="es. fino al 70%, 2x1, Sconto 15€"
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="link">Link (opzionale)</Label>
              <Input
                id="link"
                type="url"
                value={convenzioneForm.link}
                onChange={(e) => setConvenzioneForm({...convenzioneForm, link: e.target.value})}
                placeholder="https://..."
              />
            </div>

            <div className="space-y-2">
              <Label>Immagine Banner</Label>
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <Input
                    type="file"
                    accept="image/*"
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (file) {
                        // Validazione dimensione
                        if (file.size > 5 * 1024 * 1024) {
                          toast.error('L\'immagine non può superare i 5MB');
                          e.target.value = '';
                          return;
                        }
                        setConvenzioneForm({...convenzioneForm, immagine_file: file, immagine_url: ''});
                        setUploadError('');
                      }
                    }}
                    className="flex-1"
                  />
                </div>
                <p className="text-xs text-gray-500">Oppure inserisci un URL:</p>
                <Input
                  id="immagine_url"
                  type="url"
                  value={convenzioneForm.immagine_url}
                  onChange={(e) => setConvenzioneForm({...convenzioneForm, immagine_url: e.target.value, immagine_file: null})}
                  placeholder="https://..."
                />
                
                {/* Preview immagine */}
                {convenzioneForm.immagine_file && (
                  <div className="space-y-2">
                    <p className="text-xs text-green-600">
                      ✓ File selezionato: {convenzioneForm.immagine_file.name} ({(convenzioneForm.immagine_file.size / 1024).toFixed(0)} KB)
                    </p>
                    <div className="w-full h-32 rounded-lg overflow-hidden border-2 border-gray-200">
                      <img 
                        src={URL.createObjectURL(convenzioneForm.immagine_file)} 
                        alt="Preview" 
                        className="w-full h-full object-cover"
                      />
                    </div>
                  </div>
                )}
                
                {convenzioneForm.immagine_url && !convenzioneForm.immagine_file && (
                  <div className="w-full h-32 rounded-lg overflow-hidden border-2 border-gray-200">
                    <img 
                      src={getAuthenticatedFileUrl(convenzioneForm.immagine_url)} 
                      alt="Preview" 
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        toast.error('URL immagine non valido');
                      }}
                    />
                  </div>
                )}
                
                {/* Progress bar upload */}
                {uploadProgress > 0 && uploadProgress < 100 && (
                  <div className="space-y-1">
                    <div className="flex justify-between text-xs text-gray-600">
                      <span>Caricamento...</span>
                      <span>{uploadProgress}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div 
                        className="bg-gradient-to-r from-cyan-500 to-blue-500 h-2 rounded-full transition-all duration-300"
                        style={{ width: `${uploadProgress}%` }}
                      />
                    </div>
                  </div>
                )}
                
                {uploadError && (
                  <p className="text-xs text-red-600">❌ {uploadError}</p>
                )}
                
                <p className="text-xs text-gray-500">
                  💡 Suggerimento: Usa immagini in formato 16:9 (es. 1600x900px) max 5MB
                </p>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="istruzioni">
                Istruzioni per l'accesso (opzionale)
                <span className="text-xs text-gray-500 ml-2">
                  {convenzioneForm.istruzioni?.length || 0}/1000 caratteri
                </span>
              </Label>
              <Textarea
                id="istruzioni"
                value={convenzioneForm.istruzioni}
                onChange={(e) => {
                  if (e.target.value.length <= 1000) {
                    setConvenzioneForm({...convenzioneForm, istruzioni: e.target.value});
                  }
                }}
                placeholder="Inserisci istruzioni dettagliate su come accedere alla convenzione. Supporta markdown:
**grassetto**, *corsivo*, [link](url), liste con - o 1."
                rows={6}
                className={convenzioneForm.istruzioni?.length > 950 ? 'border-orange-300' : ''}
              />
              <p className="text-xs text-gray-500">
                💡 Puoi usare la formattazione Markdown: **grassetto**, *corsivo*, liste, link, ecc.
              </p>
            </div>

            <div className="flex gap-3 pt-4">
              <Button type="submit" className="flex-1 bg-green-600 hover:bg-green-700">
                {isEditingConvenzione ? 'Aggiorna' : 'Crea'} Convenzione
              </Button>
              <Button 
                type="button"
                variant="outline" 
                onClick={() => setShowConvenzioneDialog(false)}
              >
                Annulla
              </Button>
            </div>
          </form>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog Elimina Convenzione */}
      <Dialog open={showDeleteConvenzioneDialog} onOpenChange={setShowDeleteConvenzioneDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Conferma Eliminazione</DialogTitle>
            <DialogDescription>
              Sei sicuro di voler eliminare la convenzione "{selectedConvenzione?.titolo}"? Questa azione non può essere annullata.
            </DialogDescription>
          </DialogHeader>
          <div className="flex gap-3 mt-6">
            <Button 
              onClick={handleDeleteConvenzione}
              className="flex-1 bg-red-600 hover:bg-red-700"
            >
              Elimina
            </Button>
            <Button 
              variant="outline"
              onClick={() => setShowDeleteConvenzioneDialog(false)}
            >
              Annulla
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog Gestione Corso */}
      <Dialog open={showCorsoDialog} onOpenChange={setShowCorsoDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{isEditingCorso ? 'Modifica' : 'Nuovo'} Corso di Formazione</DialogTitle>
            <DialogDescription>
              {isEditingCorso ? 'Modifica' : 'Crea'} un corso per la formazione degli operatori
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSaveCorso} className="space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Titolo Corso *</label>
              <Input
                value={corsoForm.titolo}
                onChange={(e) => setCorsoForm({...corsoForm, titolo: e.target.value})}
                placeholder="Es: Sicurezza sul Lavoro"
                required
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Descrizione *</label>
              <textarea
                className="w-full p-2 border rounded-md min-h-[100px]"
                value={corsoForm.descrizione}
                onChange={(e) => setCorsoForm({...corsoForm, descrizione: e.target.value})}
                placeholder="Descrizione del corso..."
                required
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Immagine Copertina (opzionale)</label>
              <input
                type="file"
                accept="image/*"
                onChange={async (e) => {
                  const file = e.target.files?.[0];
                  if (file) {
                    const formData = new FormData();
                    formData.append('file', file);
                    try {
                      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/formazione/upload-logo`, {
                        method: 'POST',
                        body: formData,
                        headers: {
                          'Authorization': `Bearer ${localStorage.getItem('token')}`
                        }
                      });
                      if (response.ok) {
                        const data = await response.json();
                        setCorsoForm({...corsoForm, immagine_copertina_url: data.image_url});
                        toast.success("Immagine copertina caricata con successo");
                      } else {
                        toast.error("Errore nel caricamento dell'immagine");
                      }
                    } catch (error) {
                      toast.error("Errore nel caricamento dell'immagine");
                    }
                  }
                }}
                className="w-full p-2 border rounded-md"
              />
              {corsoForm.immagine_copertina_url && (
                <div className="w-full h-32 rounded-lg overflow-hidden border-2 border-gray-200 mt-2">
                  <img 
                    src={getAuthenticatedFileUrl(corsoForm.immagine_copertina_url)} 
                    alt="Copertina Preview" 
                    className="w-full h-full object-cover"
                  />
                </div>
              )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Data Scadenza (opzionale)</label>
                <Input
                  type="date"
                  value={corsoForm.data_scadenza}
                  onChange={(e) => setCorsoForm({...corsoForm, data_scadenza: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Punteggio Minimo Attestato (%)</label>
                <Input
                  type="number"
                  min="0"
                  max="100"
                  value={corsoForm.punteggio_minimo_attestato}
                  onChange={(e) => setCorsoForm({...corsoForm, punteggio_minimo_attestato: parseInt(e.target.value)})}
                  required
                />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="obbligatorio"
                checked={corsoForm.obbligatorio}
                onChange={(e) => setCorsoForm({...corsoForm, obbligatorio: e.target.checked})}
                className="w-4 h-4"
              />
              <label htmlFor="obbligatorio" className="text-sm font-medium">
                Corso Obbligatorio
              </label>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Ruoli Assegnati</label>
              <div className="grid grid-cols-2 gap-2">
                {['operatore', 'coordinatore', 'amministratore', 'super_amministratore'].map((ruolo) => (
                  <div key={ruolo} className="flex items-center gap-2 p-2 border rounded">
                    <input
                      type="checkbox"
                      id={`ruolo-${ruolo}`}
                      checked={corsoForm.ruoli_assegnati.includes(ruolo)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setCorsoForm({...corsoForm, ruoli_assegnati: [...corsoForm.ruoli_assegnati, ruolo]});
                        } else {
                          setCorsoForm({...corsoForm, ruoli_assegnati: corsoForm.ruoli_assegnati.filter(r => r !== ruolo)});
                        }
                      }}
                      className="w-4 h-4"
                    />
                    <label htmlFor={`ruolo-${ruolo}`} className="text-sm capitalize">
                      {ruolo.replace('_', ' ')}
                    </label>
                  </div>
                ))}
              </div>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Figure Professionali Assegnate</label>
              <div className="grid grid-cols-2 gap-2">
                {settings && settings.figure_professionali && settings.figure_professionali.map((figuraObj) => (
                  <div key={figuraObj.nome} className="flex items-center gap-2 p-2 border rounded">
                    <input
                      type="checkbox"
                      id={`figura-${figuraObj.nome}`}
                      checked={corsoForm.figure_professionali_assegnate.includes(figuraObj.nome)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setCorsoForm({...corsoForm, figure_professionali_assegnate: [...corsoForm.figure_professionali_assegnate, figuraObj.nome]});
                        } else {
                          setCorsoForm({...corsoForm, figure_professionali_assegnate: corsoForm.figure_professionali_assegnate.filter(f => f !== figuraObj.nome)});
                        }
                      }}
                      className="w-4 h-4"
                    />
                    <label htmlFor={`figura-${figuraObj.nome}`} className="text-sm">
                      {figuraObj.nome}
                    </label>
                  </div>
                ))}
              </div>
              <p className="text-xs text-gray-500 mt-1">Seleziona almeno un ruolo o una figura professionale</p>
            </div>
            
            {/* Sezione Personalizzazione Attestato */}
            <div className="space-y-4 border-t pt-4">
              <h3 className="text-sm font-semibold text-gray-700">📜 Personalizzazione Attestato</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium">Nome Firmatario</label>
                  <Input
                    value={corsoForm.nome_firmatario}
                    onChange={(e) => setCorsoForm({...corsoForm, nome_firmatario: e.target.value})}
                    placeholder="Es: Mario"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium">Cognome Firmatario</label>
                  <Input
                    value={corsoForm.cognome_firmatario}
                    onChange={(e) => setCorsoForm({...corsoForm, cognome_firmatario: e.target.value})}
                    placeholder="Es: Rossi"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <label className="text-sm font-medium">Ruolo Firmatario</label>
                <Input
                  value={corsoForm.ruolo_firmatario}
                  onChange={(e) => setCorsoForm({...corsoForm, ruolo_firmatario: e.target.value})}
                  placeholder="Es: Direttore Sanitario"
                />
              </div>
              
              <div className="space-y-2">
                <label className="text-sm font-medium">Logo Aggiuntivo (oltre Croce Gialla)</label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={async (e) => {
                    const file = e.target.files[0];
                    if (file) {
                      const token = localStorage.getItem('token');
                      const formData = new FormData();
                      formData.append('file', file);
                      try {
                        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/formazione/upload-logo`, {
                          method: 'POST',
                          headers: {
                            'Authorization': `Bearer ${token}`
                          },
                          body: formData
                        });
                        if (response.ok) {
                          const data = await response.json();
                          setCorsoForm({...corsoForm, logo_aggiuntivo_url: data.image_url});
                          toast.success("Logo aggiuntivo caricato con successo");
                        } else {
                          const errorData = await response.json().catch(() => ({}));
                          const errorMsg = errorData.detail || "Errore nel caricamento del logo";
                          toast.error(errorMsg);
                        }
                      } catch (error) {
                        toast.error("Errore nel caricamento del logo");
                      }
                    }
                  }}
                  className="w-full p-2 border rounded-md"
                />
                {corsoForm.logo_aggiuntivo_url && (
                  <p className="text-xs text-green-600">✓ Logo caricato</p>
                )}
              </div>
              
              <div className="space-y-2">
                <label className="text-sm font-medium">Immagine Firma</label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={async (e) => {
                    const file = e.target.files[0];
                    if (file) {
                      const token = localStorage.getItem('token');
                      const formData = new FormData();
                      formData.append('file', file);
                      try {
                        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/formazione/upload-firma`, {
                          method: 'POST',
                          headers: {
                            'Authorization': `Bearer ${token}`
                          },
                          body: formData
                        });
                        if (response.ok) {
                          const data = await response.json();
                          setCorsoForm({...corsoForm, firma_immagine_url: data.image_url});
                          toast.success("Immagine firma caricata con successo");
                        } else {
                          const errorData = await response.json().catch(() => ({}));
                          const errorMsg = errorData.detail || "Errore nel caricamento della firma";
                          toast.error(errorMsg);
                        }
                      } catch (error) {
                        toast.error("Errore nel caricamento della firma");
                      }
                    }
                  }}
                  className="w-full p-2 border rounded-md"
                />
                {corsoForm.firma_immagine_url && (
                  <p className="text-xs text-green-600">✓ Firma caricata</p>
                )}
              </div>
            </div>
            
            <div className="flex gap-3 mt-6">
              <Button type="submit" className="flex-1 bg-green-600 hover:bg-green-700">
                {isEditingCorso ? 'Aggiorna' : 'Crea'} Corso
              </Button>
              <Button 
                type="button"
                variant="outline"
                onClick={() => setShowCorsoDialog(false)}
              >
                Annulla
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Dialog Elimina Corso */}
      <Dialog open={showDeleteCorsoDialog} onOpenChange={setShowDeleteCorsoDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Conferma Eliminazione Corso</DialogTitle>
            <DialogDescription>
              Sei sicuro di voler eliminare il corso "{selectedCorso?.titolo}"? Questa azione non può essere annullata.
            </DialogDescription>
          </DialogHeader>
          <div className="flex gap-3 mt-6">
            <Button 
              onClick={handleDeleteCorso}
              className="flex-1 bg-red-600 hover:bg-red-700"
            >
              Elimina Corso
            </Button>
            <Button 
              variant="outline"
              onClick={() => setShowDeleteCorsoDialog(false)}
            >
              Annulla
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog Personalizza Questionario Soddisfacimento */}
      <Dialog open={showQuestionarioConfigDialog} onOpenChange={setShowQuestionarioConfigDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Personalizza Questionario di Soddisfacimento</DialogTitle>
            <DialogDescription>
              Configura le domande del questionario per il corso "{questionarioCorso?.titolo}"
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSaveQuestionario} className="space-y-4">
            <div className="space-y-3">
              <label className="text-sm font-medium">Domande del Questionario</label>
              {questionarioForm.domande.map((domanda, index) => (
                <div key={index} className="flex gap-2">
                  <Input
                    value={domanda}
                    onChange={(e) => {
                      const newDomande = [...questionarioForm.domande];
                      newDomande[index] = e.target.value;
                      setQuestionarioForm({...questionarioForm, domande: newDomande});
                    }}
                    placeholder={`Domanda ${index + 1}`}
                    className="flex-1"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      const newDomande = questionarioForm.domande.filter((_, i) => i !== index);
                      setQuestionarioForm({...questionarioForm, domande: newDomande});
                    }}
                    className="text-red-600"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </Button>
                </div>
              ))}
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => {
                  setQuestionarioForm({
                    ...questionarioForm,
                    domande: [...questionarioForm.domande, '']
                  });
                }}
                className="w-full"
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Aggiungi Domanda
              </Button>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Scala Minima</label>
                <Input
                  type="number"
                  min="1"
                  value={questionarioForm.scala_min}
                  onChange={(e) => setQuestionarioForm({...questionarioForm, scala_min: parseInt(e.target.value)})}
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Scala Massima</label>
                <Input
                  type="number"
                  min="1"
                  value={questionarioForm.scala_max}
                  onChange={(e) => setQuestionarioForm({...questionarioForm, scala_max: parseInt(e.target.value)})}
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Etichetta Minima</label>
                <Input
                  value={questionarioForm.etichetta_min}
                  onChange={(e) => setQuestionarioForm({...questionarioForm, etichetta_min: e.target.value})}
                  placeholder="Es: Per niente"
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Etichetta Massima</label>
                <Input
                  value={questionarioForm.etichetta_max}
                  onChange={(e) => setQuestionarioForm({...questionarioForm, etichetta_max: e.target.value})}
                  placeholder="Es: Moltissimo"
                />
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="abilita_commenti"
                checked={questionarioForm.abilita_commenti}
                onChange={(e) => setQuestionarioForm({...questionarioForm, abilita_commenti: e.target.checked})}
                className="w-4 h-4"
              />
              <label htmlFor="abilita_commenti" className="text-sm font-medium">
                Abilita Campo Commenti Liberi
              </label>
            </div>
            
            <div className="flex gap-3 mt-6">
              <Button type="submit" className="flex-1 bg-purple-600 hover:bg-purple-700">
                Salva Questionario
              </Button>
              <Button 
                type="button"
                variant="outline"
                onClick={() => setShowQuestionarioConfigDialog(false)}
              >
                Annulla
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Dialog Gestione Modulo */}
      <Dialog open={showModuloDialog} onOpenChange={setShowModuloDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{isEditingModulo ? 'Modifica' : 'Nuovo'} Modulo</DialogTitle>
            <DialogDescription>
              {isEditingModulo ? 'Modifica' : 'Aggiungi'} un modulo al corso "{selectedCorso?.titolo}"
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSaveModulo} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="md:col-span-2 space-y-2">
                <label className="text-sm font-medium">Titolo Modulo *</label>
                <Input
                  value={moduloForm.titolo}
                  onChange={(e) => setModuloForm({...moduloForm, titolo: e.target.value})}
                  placeholder="Es: Introduzione alla Sicurezza"
                  required
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Ordine *</label>
                <Input
                  type="number"
                  min="1"
                  value={moduloForm.ordine}
                  onChange={(e) => setModuloForm({...moduloForm, ordine: parseInt(e.target.value)})}
                  required
                />
              </div>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Descrizione (opzionale)</label>
              <textarea
                className="w-full p-2 border rounded-md min-h-[80px]"
                value={moduloForm.descrizione}
                onChange={(e) => setModuloForm({...moduloForm, descrizione: e.target.value})}
                placeholder="Breve descrizione del modulo..."
              />
            </div>
            <div className="border-t pt-4">
              <h4 className="font-semibold mb-3">Contenuto Video</h4>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-3">
                <div className="space-y-2">
                  <label className="text-sm font-medium">Tipo Video</label>
                  <select
                    className="w-full p-2 border rounded-md"
                    value={moduloForm.video_type}
                    onChange={(e) => setModuloForm({...moduloForm, video_type: e.target.value, video_url: ''})}
                  >
                    <option value="">Nessun video</option>
                    <option value="upload">📤 Carica File Video</option>
                    <option value="gdrive">Google Drive</option>
                    <option value="youtube">YouTube</option>
                  </select>
                </div>
                {moduloForm.video_type && moduloForm.video_type !== 'upload' && (
                  <div className="md:col-span-3 space-y-2">
                    <label className="text-sm font-medium">URL Video</label>
                    <Input
                      type="url"
                      value={moduloForm.video_url}
                      onChange={(e) => setModuloForm({...moduloForm, video_url: e.target.value})}
                      placeholder={moduloForm.video_type === 'gdrive' ? 'https://drive.google.com/file/d/...' : 'https://youtube.com/watch?v=...'}
                    />
                  </div>
                )}
              </div>
              
              {/* Upload Video */}
              {moduloForm.video_type === 'upload' && (
                <div className="space-y-2 mb-3">
                  <label className="text-sm font-medium">Carica Video (MP4, WEBM, AVI, MOV - Max 500MB)</label>
                  <input
                    type="file"
                    accept="video/mp4,video/webm,video/avi,video/quicktime,video/x-msvideo"
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        // Verifica dimensione
                        const sizeMB = file.size / (1024 * 1024);
                        if (sizeMB > 500) {
                          toast.error("File troppo grande! Massimo 500MB");
                          e.target.value = '';
                          return;
                        }
                        
                        const formData = new FormData();
                        formData.append('file', file);
                        
                        toast.info(`Caricamento video in corso... (${sizeMB.toFixed(1)}MB)`);
                        
                        try {
                          const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/formazione/upload-video`, {
                            method: 'POST',
                            body: formData,
                            headers: {
                              'Authorization': `Bearer ${localStorage.getItem('token')}`
                            }
                          });
                          
                          if (response.ok) {
                            const data = await response.json();
                            setModuloForm({...moduloForm, video_url: data.video_url});
                            toast.success(`Video caricato con successo! (${data.size_mb}MB)`);
                          } else {
                            const error = await response.json();
                            toast.error(error.detail || "Errore nel caricamento del video");
                          }
                        } catch (error) {
                          toast.error("Errore nel caricamento del video");
                        }
                      }
                    }}
                    className="w-full p-2 border rounded-md"
                  />
                  {moduloForm.video_url && (
                    <div className="mt-2">
                      <p className="text-sm text-green-600">✓ Video caricato sul tuo server</p>
                      <video controls className="w-full max-w-md mt-2 rounded border">
                        <source src={getAuthenticatedFileUrl(moduloForm.video_url)} />
                        Il tuo browser non supporta il tag video.
                      </video>
                    </div>
                  )}
                </div>
              )}
              
              {moduloForm.video_type === 'youtube' && (
                <div className="space-y-2">
                  <label className="text-sm font-medium">Password YouTube (opzionale)</label>
                  <Input
                    value={moduloForm.video_password}
                    onChange={(e) => setModuloForm({...moduloForm, video_password: e.target.value})}
                    placeholder="Password se video privato"
                  />
                </div>
              )}
            </div>
            <div className="border-t pt-4">
              <h4 className="font-semibold mb-3">Contenuto Testuale</h4>
              <div className="space-y-2">
                <label className="text-sm font-medium">Testo (Markdown) (opzionale)</label>
                <textarea
                  className="w-full p-2 border rounded-md min-h-[150px] font-mono text-sm"
                  value={moduloForm.testo_markdown}
                  onChange={(e) => setModuloForm({...moduloForm, testo_markdown: e.target.value})}
                  placeholder="# Titolo&#10;&#10;Contenuto in **Markdown**..."
                />
                <p className="text-xs text-gray-500">Supporta formattazione Markdown (# titoli, **grassetto**, ecc.)</p>
              </div>
            </div>
            <div className="border-t pt-4">
              <h4 className="font-semibold mb-3">Documento PDF</h4>
              <div className="space-y-2">
                <label className="text-sm font-medium">Carica PDF (opzionale)</label>
                <input
                  type="file"
                  accept=".pdf,application/pdf"
                  onChange={async (e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                      const formData = new FormData();
                      formData.append('file', file);
                      try {
                        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/formazione/upload-pdf`, {
                          method: 'POST',
                          body: formData,
                          headers: {
                            'Authorization': `Bearer ${localStorage.getItem('token')}`
                          }
                        });
                        if (response.ok) {
                          const data = await response.json();
                          setModuloForm({...moduloForm, pdf_url: data.pdf_url});
                          toast.success("PDF caricato con successo");
                        } else {
                          toast.error("Errore nel caricamento del PDF");
                        }
                      } catch (error) {
                        toast.error("Errore nel caricamento del PDF");
                      }
                    }
                  }}
                  className="w-full p-2 border rounded-md"
                />
                {moduloForm.pdf_url && (
                  <div className="flex items-center gap-2">
                    <p className="text-xs text-green-600">✓ PDF caricato</p>
                    <button
                      type="button"
                      onClick={() => window.open(getAuthenticatedFileUrl(moduloForm.pdf_url), '_blank')}
                      className="text-xs text-blue-600 hover:underline"
                    >
                      Visualizza PDF
                    </button>
                  </div>
                )}
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <Button 
                type="submit" 
                className="flex-1 bg-green-600 hover:bg-green-700"
                disabled={isSavingModulo}
              >
                {isSavingModulo ? 'Salvataggio...' : (isEditingModulo ? 'Aggiorna' : 'Crea')} Modulo
              </Button>
              <Button 
                type="button"
                variant="outline"
                onClick={() => setShowModuloDialog(false)}
                disabled={isSavingModulo}
              >
                Annulla
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Dialog Elimina Modulo */}
      <Dialog open={showDeleteModuloDialog} onOpenChange={setShowDeleteModuloDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Conferma Eliminazione Modulo</DialogTitle>
            <DialogDescription>
              Sei sicuro di voler eliminare il modulo "{selectedModulo?.titolo}"? Questa azione non può essere annullata.
            </DialogDescription>
          </DialogHeader>
          <div className="flex gap-3 mt-6">
            <Button 
              onClick={handleDeleteModulo}
              className="flex-1 bg-red-600 hover:bg-red-700"
            >
              Elimina Modulo
            </Button>
            <Button 
              variant="outline"
              onClick={() => setShowDeleteModuloDialog(false)}
            >
              Annulla
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog Crea Test Finale */}
      <Dialog open={showTestFinaleDialog} onOpenChange={setShowTestFinaleDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Crea Test Finale</DialogTitle>
            <DialogDescription>
              Crea il test finale per il corso "{selectedCorso?.titolo}". Il test finale sarà obbligatorio per completare il corso e ottenere l'attestato.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSaveTestFinale} className="space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Titolo Test *</label>
              <Input
                value={testFinaleForm.titolo}
                onChange={(e) => setTestFinaleForm({...testFinaleForm, titolo: e.target.value})}
                placeholder="Es: Test Finale - Sicurezza sul Lavoro"
                required
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Descrizione</label>
              <textarea
                className="w-full border border-gray-300 rounded-md p-2 min-h-[80px]"
                value={testFinaleForm.descrizione}
                onChange={(e) => setTestFinaleForm({...testFinaleForm, descrizione: e.target.value})}
                placeholder="Descrizione del test finale..."
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Punteggio Minimo (%) *</label>
                <Input
                  type="number"
                  min="1"
                  max="100"
                  value={testFinaleForm.punteggio_minimo}
                  onChange={(e) => setTestFinaleForm({...testFinaleForm, punteggio_minimo: parseInt(e.target.value)})}
                  required
                />
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium">Tentativi Massimi *</label>
                <Input
                  type="number"
                  min="1"
                  max="10"
                  value={testFinaleForm.tentativi_max}
                  onChange={(e) => setTestFinaleForm({...testFinaleForm, tentativi_max: parseInt(e.target.value)})}
                  required
                />
              </div>
            </div>

            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="ordine_casuale_finale"
                checked={testFinaleForm.ordine_casuale}
                onChange={(e) => setTestFinaleForm({...testFinaleForm, ordine_casuale: e.target.checked})}
                className="rounded"
              />
              <label htmlFor="ordine_casuale_finale" className="text-sm">
                Mostra domande in ordine casuale
              </label>
            </div>

            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <p className="text-sm text-yellow-800">
                <strong>⚠️ Nota importante:</strong> Dopo aver creato il test finale, dovrai aggiungere le domande. Il test finale non può essere ripetuto se superato.
              </p>
            </div>

            <div className="flex gap-3">
              <Button type="submit" className="flex-1 bg-yellow-600 hover:bg-yellow-700">
                Crea Test Finale
              </Button>
              <Button 
                type="button"
                variant="outline"
                onClick={() => setShowTestFinaleDialog(false)}
              >
                Annulla
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Dialog Gestione Test */}
      <Dialog open={showTestDialog} onOpenChange={setShowTestDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{isEditingTest ? 'Modifica' : 'Nuovo'} Test</DialogTitle>
            <DialogDescription>
              {isEditingTest ? 'Modifica' : 'Crea'} un test per il modulo "{selectedModulo?.titolo}"
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSaveTest} className="space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Titolo Test *</label>
              <Input
                value={testForm.titolo}
                onChange={(e) => setTestForm({...testForm, titolo: e.target.value})}
                placeholder="Es: Test di verifica modulo 1"
                required
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Descrizione</label>
              <textarea
                className="w-full p-2 border rounded-md min-h-[60px]"
                value={testForm.descrizione}
                onChange={(e) => setTestForm({...testForm, descrizione: e.target.value})}
                placeholder="Breve descrizione del test..."
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Tipo Test</label>
                <select
                  className="w-full p-2 border rounded-md"
                  value={testForm.tipo}
                  onChange={(e) => setTestForm({...testForm, tipo: e.target.value})}
                  required
                >
                  <option value="intermedio">Intermedio</option>
                  <option value="finale">Finale</option>
                </select>
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Punteggio Minimo (%)</label>
                <Input
                  type="number"
                  min="0"
                  max="100"
                  value={testForm.punteggio_minimo}
                  onChange={(e) => setTestForm({...testForm, punteggio_minimo: parseInt(e.target.value)})}
                  required
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Tentativi Massimi</label>
                <Input
                  type="number"
                  min="1"
                  max="10"
                  value={testForm.tentativi_max}
                  onChange={(e) => setTestForm({...testForm, tentativi_max: parseInt(e.target.value)})}
                  required
                />
              </div>
              <div className="flex items-center gap-2 pt-6">
                <input
                  type="checkbox"
                  id="ordine_casuale"
                  checked={testForm.ordine_casuale}
                  onChange={(e) => setTestForm({...testForm, ordine_casuale: e.target.checked})}
                  className="w-4 h-4"
                />
                <label htmlFor="ordine_casuale" className="text-sm">
                  Domande in ordine casuale
                </label>
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <Button type="submit" className="flex-1 bg-green-600 hover:bg-green-700">
                {isEditingTest ? 'Aggiorna' : 'Crea'} Test
              </Button>
              <Button 
                type="button"
                variant="outline"
                onClick={() => setShowTestDialog(false)}
              >
                Annulla
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Dialog Elimina Test */}
      <Dialog open={showDeleteTestDialog} onOpenChange={setShowDeleteTestDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Conferma Eliminazione Test</DialogTitle>
            <DialogDescription>
              Sei sicuro di voler eliminare questo test? Tutte le domande associate verranno eliminate. Questa azione non può essere annullata.
            </DialogDescription>
          </DialogHeader>
          <div className="flex gap-3 mt-6">
            <Button 
              onClick={handleDeleteTest}
              className="flex-1 bg-red-600 hover:bg-red-700"
            >
              Elimina Test
            </Button>
            <Button 
              variant="outline"
              onClick={() => setShowDeleteTestDialog(false)}
            >
              Annulla
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog Gestione Domanda */}
      <Dialog open={showDomandaDialog} onOpenChange={setShowDomandaDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{isEditingDomanda ? 'Modifica' : 'Nuova'} Domanda</DialogTitle>
            <DialogDescription>
              {isEditingDomanda ? 'Modifica' : 'Aggiungi'} una domanda al test
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSaveDomanda} className="space-y-4">
            <div className="grid grid-cols-3 gap-4">
              <div className="col-span-2 space-y-2">
                <label className="text-sm font-medium">Tipo Domanda *</label>
                <select
                  className="w-full p-2 border rounded-md"
                  value={domandaForm.tipo}
                  onChange={(e) => setDomandaForm({...domandaForm, tipo: e.target.value})}
                  required
                >
                  <option value="multipla">Risposta Multipla</option>
                  <option value="vero_falso">Vero/Falso</option>
                  <option value="matching">Abbinamento (Matching)</option>
                  <option value="testuale">Risposta Aperta (Testuale)</option>
                </select>
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Punti *</label>
                <Input
                  type="number"
                  min="1"
                  value={domandaForm.punti}
                  onChange={(e) => setDomandaForm({...domandaForm, punti: parseInt(e.target.value)})}
                  required
                />
              </div>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Testo Domanda *</label>
              <textarea
                className="w-full p-2 border rounded-md min-h-[80px]"
                value={domandaForm.testo}
                onChange={(e) => setDomandaForm({...domandaForm, testo: e.target.value})}
                placeholder="Inserisci il testo della domanda..."
                required
              />
            </div>
            
            {(domandaForm.tipo === 'multipla' || domandaForm.tipo === 'vero_falso') && (
              <div className="space-y-2">
                <label className="text-sm font-medium">
                  {domandaForm.tipo === 'vero_falso' ? 'Risposta Corretta' : 'Opzioni e Risposta Corretta'}
                </label>
                {domandaForm.tipo === 'vero_falso' ? (
                  <select
                    className="w-full p-2 border rounded-md"
                    value={domandaForm.risposta_corretta}
                    onChange={(e) => setDomandaForm({...domandaForm, risposta_corretta: e.target.value})}
                    required
                  >
                    <option value="">Seleziona...</option>
                    <option value="vero">Vero</option>
                    <option value="falso">Falso</option>
                  </select>
                ) : (
                  <div className="space-y-2">
                    {domandaForm.opzioni.map((opzione, idx) => (
                      <div key={idx} className="flex gap-2">
                        <input
                          type="radio"
                          name="risposta_corretta"
                          checked={domandaForm.risposta_corretta === idx.toString()}
                          onChange={() => setDomandaForm({...domandaForm, risposta_corretta: idx.toString()})}
                          className="mt-3"
                        />
                        <Input
                          value={opzione}
                          onChange={(e) => {
                            const newOpzioni = [...domandaForm.opzioni];
                            newOpzioni[idx] = e.target.value;
                            setDomandaForm({...domandaForm, opzioni: newOpzioni});
                          }}
                          placeholder={`Opzione ${idx + 1}`}
                          required
                        />
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
            
            {domandaForm.tipo === 'testuale' && (
              <div className="flex items-center gap-2 p-3 bg-yellow-50 border border-yellow-200 rounded">
                <input
                  type="checkbox"
                  id="correzione_manuale"
                  checked={domandaForm.richiede_correzione_manuale}
                  onChange={(e) => setDomandaForm({...domandaForm, richiede_correzione_manuale: e.target.checked})}
                  className="w-4 h-4"
                />
                <label htmlFor="correzione_manuale" className="text-sm text-yellow-800">
                  Richiede correzione manuale da Super Admin
                </label>
              </div>
            )}
            
            <div className="flex gap-3 mt-6">
              <Button type="submit" className="flex-1 bg-green-600 hover:bg-green-700">
                {isEditingDomanda ? 'Aggiorna' : 'Crea'} Domanda
              </Button>
              <Button 
                type="button"
                variant="outline"
                onClick={() => setShowDomandaDialog(false)}
              >
                Annulla
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Dialog Elimina Domanda */}
      <Dialog open={showDeleteDomandaDialog} onOpenChange={setShowDeleteDomandaDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Conferma Eliminazione Domanda</DialogTitle>
            <DialogDescription>
              Sei sicuro di voler eliminare questa domanda? Questa azione non può essere annullata.
            </DialogDescription>
          </DialogHeader>
          <div className="flex gap-3 mt-6">
            <Button 
              onClick={handleDeleteDomanda}
              className="flex-1 bg-red-600 hover:bg-red-700"
            >
              Elimina Domanda
            </Button>
            <Button 
              variant="outline"
              onClick={() => setShowDeleteDomandaDialog(false)}
            >
              Annulla
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      {/* Dialog Correzione Risposta */}
      <Dialog open={showCorrezioneDialog} onOpenChange={setShowCorrezioneDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Correggi Risposta</DialogTitle>
            <DialogDescription>
              Correggi manualmente la risposta dell'operatore
            </DialogDescription>
          </DialogHeader>
          {rispostaCorrente && (
            <div className="space-y-4">
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="text-sm text-gray-600 mb-2">Operatore:</p>
                <p className="font-semibold">{rispostaCorrente.user_name}</p>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="text-sm text-gray-600 mb-2">Domanda:</p>
                <p className="font-medium">{rispostaCorrente.domanda_testo}</p>
                <p className="text-xs text-gray-500 mt-1">Punti: {rispostaCorrente.domanda_punti}</p>
              </div>
              <div className="bg-blue-50 p-4 rounded-lg">
                <p className="text-sm text-gray-600 mb-2">Risposta dell'operatore:</p>
                <p className="text-gray-900">{rispostaCorrente.risposta}</p>
              </div>
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    id="corretta"
                    checked={correzioneForm.corretta}
                    onChange={(e) => setCorrezioneForm({
                      ...correzioneForm,
                      corretta: e.target.checked,
                      punti_ottenuti: e.target.checked ? rispostaCorrente.domanda_punti : 0
                    })}
                    className="w-4 h-4"
                  />
                  <label htmlFor="corretta" className="font-medium">
                    Risposta corretta
                  </label>
                </div>
                <div>
                  <label className="text-sm font-medium mb-2 block">Punti assegnati</label>
                  <Input
                    type="number"
                    min="0"
                    max={rispostaCorrente.domanda_punti}
                    value={correzioneForm.punti_ottenuti}
                    onChange={(e) => setCorrezioneForm({
                      ...correzioneForm,
                      punti_ottenuti: parseInt(e.target.value) || 0
                    })}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium mb-2 block">Feedback (opzionale)</label>
                  <textarea
                    className="w-full p-2 border rounded-md min-h-[80px]"
                    value={correzioneForm.feedback}
                    onChange={(e) => setCorrezioneForm({
                      ...correzioneForm,
                      feedback: e.target.value
                    })}
                    placeholder="Aggiungi un commento per l'operatore..."
                  />
                </div>
              </div>
              <div className="flex gap-3">
                <Button 
                  onClick={handleSalvaCorrezione}
                  className="flex-1 bg-green-600 hover:bg-green-700"
                >
                  Salva Correzione
                </Button>
                <Button 
                  variant="outline"
                  onClick={() => setShowCorrezioneDialog(false)}
                >
                  Annulla
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Dialog Dettaglio Risposta Questionario */}
      <Dialog open={showDettaglioRispostaDialog} onOpenChange={setShowDettaglioRispostaDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Dettaglio Questionario di Soddisfacimento</DialogTitle>
            <DialogDescription>
              Risposte complete al questionario di gradimento del corso
            </DialogDescription>
          </DialogHeader>
          {rispostaSelezionata && (
            <div className="space-y-4">
              {/* Info Operatore e Corso */}
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-blue-50 p-4 rounded-lg">
                  <p className="text-xs text-gray-600 mb-1">Operatore</p>
                  <p className="font-semibold">
                    {users.find(u => u.id === rispostaSelezionata.user_id) 
                      ? `${users.find(u => u.id === rispostaSelezionata.user_id).first_name} ${users.find(u => u.id === rispostaSelezionata.user_id).last_name}`
                      : 'N/A'}
                  </p>
                </div>
                <div className="bg-green-50 p-4 rounded-lg">
                  <p className="text-xs text-gray-600 mb-1">Corso</p>
                  <p className="font-semibold">
                    {corsi.find(c => c.id === rispostaSelezionata.corso_id)?.titolo || 'N/A'}
                  </p>
                </div>
              </div>

              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="text-xs text-gray-600 mb-1">Data Compilazione</p>
                <p className="font-medium">
                  {new Date(rispostaSelezionata.created_at).toLocaleString('it-IT')}
                </p>
              </div>

              {/* Valutazione Media */}
              {rispostaSelezionata.risposte && Object.keys(rispostaSelezionata.risposte).length > 0 && (
                <div className="bg-gradient-to-r from-yellow-50 to-orange-50 p-4 rounded-lg">
                  <p className="text-sm text-gray-600 mb-2">Valutazione Media</p>
                  <div className="flex items-center gap-3">
                    <span className="text-3xl font-bold text-orange-600">
                      {(() => {
                        const valori = Object.values(rispostaSelezionata.risposte);
                        return (valori.reduce((sum, val) => sum + val, 0) / valori.length).toFixed(1);
                      })()}
                    </span>
                    <div className="flex gap-1">
                      {[...Array(5)].map((_, i) => {
                        const valori = Object.values(rispostaSelezionata.risposte);
                        const media = valori.reduce((sum, val) => sum + val, 0) / valori.length;
                        return (
                          <svg 
                            key={i} 
                            className={`w-6 h-6 ${i < Math.round(media) ? 'text-yellow-400' : 'text-gray-300'}`}
                            fill="currentColor" 
                            viewBox="0 0 20 20"
                          >
                            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                          </svg>
                        );
                      })}
                    </div>
                    <span className="text-sm text-gray-500">
                      su {Object.keys(rispostaSelezionata.risposte).length} domande
                    </span>
                  </div>
                </div>
              )}

              {/* Risposte alle Domande */}
              <div className="space-y-3">
                <h4 className="font-semibold text-gray-900">Risposte alle Domande</h4>
                {rispostaSelezionata.questionario?.domande?.map((domanda, idx) => {
                  const rispostaValore = rispostaSelezionata.risposte?.[idx.toString()];
                  return (
                    <Card key={idx} className="border-l-4 border-l-blue-500">
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1">
                            <p className="font-medium text-gray-900 mb-2">
                              {idx + 1}. {domanda}
                            </p>
                            {rispostaValore !== undefined && (
                              <div className="flex items-center gap-2 mt-2">
                                <div className="flex gap-0.5">
                                  {[...Array(rispostaSelezionata.questionario.scala_max || 5)].map((_, i) => (
                                    <svg 
                                      key={i} 
                                      className={`w-5 h-5 ${i < rispostaValore ? 'text-yellow-400' : 'text-gray-300'}`}
                                      fill="currentColor" 
                                      viewBox="0 0 20 20"
                                    >
                                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                                    </svg>
                                  ))}
                                </div>
                                <span className="text-sm font-semibold text-gray-700">
                                  {rispostaValore} / {rispostaSelezionata.questionario.scala_max || 5}
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>

              {/* Commenti */}
              {rispostaSelezionata.commenti && (
                <Card className="border-l-4 border-l-green-500">
                  <CardContent className="p-4">
                    <h4 className="font-semibold text-gray-900 mb-2">Commenti Aggiuntivi</h4>
                    <p className="text-gray-700 whitespace-pre-wrap">{rispostaSelezionata.commenti}</p>
                  </CardContent>
                </Card>
              )}

              {/* Pulsante Chiudi */}
              <div className="flex justify-end pt-4">
                <Button 
                  variant="outline"
                  onClick={() => setShowDettaglioRispostaDialog(false)}
                >
                  Chiudi
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>


      {/* Dialog Riepilogo Test Finale */}
      <Dialog open={showRiepilogoTestDialog} onOpenChange={setShowRiepilogoTestDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Riepilogo Test Finale</DialogTitle>
            <DialogDescription>
              Test correlato all'attestato di completamento corso
            </DialogDescription>
          </DialogHeader>
          {riepilogoTest && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4 p-4 bg-gray-50 rounded-lg">
                <div>
                  <p className="text-sm text-gray-600">Operatore:</p>
                  <p className="font-semibold">{riepilogoTest.certificato.nome_operatore}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Corso:</p>
                  <p className="font-semibold">{riepilogoTest.corso.titolo}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Data:</p>
                  <p className="font-semibold">{new Date(riepilogoTest.certificato.data_completamento).toLocaleDateString('it-IT')}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Punteggio Finale:</p>
                  <p className="font-semibold text-green-600 text-xl">{riepilogoTest.punteggio_finale}%</p>
                </div>
              </div>

              <div className="border-t pt-4">
                <h3 className="font-semibold mb-3">Domande e Risposte</h3>
                <div className="space-y-4">
                  {riepilogoTest.domande_con_risposte.map((item, idx) => (
                    <Card key={item.domanda.id} className={`${item.risposta_utente?.corretta === true ? 'border-green-200 bg-green-50' : item.risposta_utente?.corretta === false ? 'border-red-200 bg-red-50' : 'border-yellow-200 bg-yellow-50'}`}>
                      <CardContent className="p-4">
                        <div className="flex gap-3">
                          <div className="flex-shrink-0">
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold ${
                              item.risposta_utente?.corretta === true ? 'bg-green-200 text-green-700' : 
                              item.risposta_utente?.corretta === false ? 'bg-red-200 text-red-700' : 
                              'bg-yellow-200 text-yellow-700'
                            }`}>
                              {idx + 1}
                            </div>
                          </div>
                          <div className="flex-1">
                            <p className="font-medium text-gray-900 mb-2">{item.domanda.testo}</p>
                            <div className="text-sm">
                              <span className="text-gray-600">Tipo: </span>
                              <span className="font-medium">
                                {item.domanda.tipo === 'multipla' && 'Risposta Multipla'}
                                {item.domanda.tipo === 'vero_falso' && 'Vero/Falso'}
                                {item.domanda.tipo === 'testuale' && 'Risposta Aperta'}
                              </span>
                              <span className="ml-4 text-gray-600">Punti: </span>
                              <span className="font-medium">{item.risposta_utente?.punti_ottenuti || 0}/{item.domanda.punti}</span>
                            </div>
                            {item.domanda.tipo === 'multipla' && item.domanda.opzioni && (
                              <div className="mt-2 space-y-1">
                                {item.domanda.opzioni.map((opzione, optIdx) => (
                                  <div 
                                    key={optIdx}
                                    className={`p-2 rounded text-sm ${
                                      optIdx.toString() === item.domanda.risposta_corretta ? 'bg-green-100 font-semibold' : 
                                      optIdx.toString() === item.risposta_utente?.risposta ? 'bg-red-100' : 
                                      ''
                                    }`}
                                  >
                                    {String.fromCharCode(65 + optIdx)}. {opzione}
                                    {optIdx.toString() === item.domanda.risposta_corretta && ' ✓ (Corretta)'}
                                    {optIdx.toString() === item.risposta_utente?.risposta && optIdx.toString() !== item.domanda.risposta_corretta && ' ✗ (Tua risposta)'}
                                  </div>
                                ))}
                              </div>
                            )}
                            {item.domanda.tipo === 'vero_falso' && (
                              <div className="mt-2">
                                <p className="text-sm">
                                  <span className="text-gray-600">Risposta corretta: </span>
                                  <span className="font-semibold capitalize">{item.domanda.risposta_corretta}</span>
                                </p>
                                <p className="text-sm">
                                  <span className="text-gray-600">Tua risposta: </span>
                                  <span className={`font-semibold capitalize ${item.risposta_utente?.corretta ? 'text-green-600' : 'text-red-600'}`}>
                                    {item.risposta_utente?.risposta}
                                  </span>
                                </p>
                              </div>
                            )}
                            {item.domanda.tipo === 'testuale' && (
                              <div className="mt-2">
                                <p className="text-sm text-gray-600 mb-1">Tua risposta:</p>
                                <div className="p-2 bg-white border rounded">
                                  {item.risposta_utente?.risposta}
                                </div>
                                {item.risposta_utente?.feedback && (
                                  <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded">
                                    <p className="text-sm text-gray-600 mb-1">Feedback:</p>
                                    <p className="text-sm">{item.risposta_utente.feedback}</p>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>

              <div className="flex gap-3 mt-6 border-t pt-4">
                <Button 
                  onClick={() => toast.info('Generazione PDF in sviluppo')}
                  className="flex-1 bg-blue-600 hover:bg-blue-700"
                >
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  Scarica PDF Test
                </Button>
                <Button 
                  variant="outline"
                  onClick={() => setShowRiepilogoTestDialog(false)}
                >
                  Chiudi
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Dialog Export Massivo */}
      {showExportMassivoDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md">
            <CardHeader>
              <CardTitle>Export Massivo per ASL</CardTitle>
              <CardDescription>
                Esporta i dati di tutti gli operatori di un'ASL specifica
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>ASL di Riferimento</Label>
                <select
                  className="w-full border rounded-lg px-3 py-2"
                  value={filtriExportMassivo.asl_riferimento}
                  onChange={(e) => setFiltriExportMassivo({...filtriExportMassivo, asl_riferimento: e.target.value})}
                >
                  <option value="">Seleziona ASL...</option>
                  <option value="Asl Pescara">Asl Pescara</option>
                  <option value="Asl L'Aquila">Asl L'Aquila</option>
                  <option value="Asl Teramo">Asl Teramo</option>
                  <option value="Asl Chieti">Asl Chieti</option>
                  <option value="Asl Lanciano">Asl Lanciano</option>
                  <option value="Asl Avezzano">Asl Avezzano</option>
                </select>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Anno</Label>
                  <select
                    className="w-full border rounded-lg px-3 py-2"
                    value={filtriExportMassivo.anno}
                    onChange={(e) => setFiltriExportMassivo({...filtriExportMassivo, anno: parseInt(e.target.value)})}
                  >
                    {[2024, 2025, 2026].map(y => (
                      <option key={y} value={y}>{y}</option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <Label>Mese Inizio</Label>
                  <select
                    className="w-full border rounded-lg px-3 py-2"
                    value={filtriExportMassivo.mese_inizio}
                    onChange={(e) => setFiltriExportMassivo({...filtriExportMassivo, mese_inizio: parseInt(e.target.value)})}
                  >
                    {['Gen', 'Feb', 'Mar', 'Apr', 'Mag', 'Giu', 'Lug', 'Ago', 'Set', 'Ott', 'Nov', 'Dic'].map((m, i) => (
                      <option key={i+1} value={i+1}>{m}</option>
                    ))}
                  </select>
                </div>
              </div>
              
              <div>
                <Label>Mese Fine</Label>
                <select
                  className="w-full border rounded-lg px-3 py-2"
                  value={filtriExportMassivo.mese_fine}
                  onChange={(e) => setFiltriExportMassivo({...filtriExportMassivo, mese_fine: parseInt(e.target.value)})}
                >
                  {['Gen', 'Feb', 'Mar', 'Apr', 'Mag', 'Giu', 'Lug', 'Ago', 'Set', 'Ott', 'Nov', 'Dic'].map((m, i) => (
                    <option key={i+1} value={i+1}>{m}</option>
                  ))}
                </select>
              </div>
              
              <div className="flex gap-2 pt-4">
                <Button 
                  onClick={exportMassivoPDF}
                  disabled={!filtriExportMassivo.asl_riferimento}
                  variant="outline"
                  className="flex-1 border-2 border-red-500 text-red-600 bg-transparent hover:bg-red-50"
                >
                  <FileText className="w-4 h-4 mr-2" />
                  PDF
                </Button>
                <Button 
                  onClick={exportMassivoExcel}
                  disabled={!filtriExportMassivo.asl_riferimento}
                  variant="outline"
                  className="flex-1 border-2 border-green-500 text-green-600 bg-transparent hover:bg-green-50"
                >
                  <FileText className="w-4 h-4 mr-2" />
                  Excel
                </Button>
                <Button 
                  onClick={() => setShowExportMassivoDialog(false)}
                  variant="outline"
                  className="flex-1"
                >
                  Annulla
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

function App() {
  return (
    <div className="App">
      <Toaster position="top-center" richColors />
      <AuthContext>
        {({ user, login, logout }) => (
          <BrowserRouter>
            <Routes>
              {/* Home Page - Landing */}
              <Route
                path="/"
                element={
                  user ? (
                    <Navigate to="/dashboard" replace />
                  ) : (
                    <HomePage />
                  )
                }
              />
              
              {/* Login Page */}
              <Route
                path="/login"
                element={
                  user ? (
                    <Navigate to="/dashboard" replace />
                  ) : (
                    <LoginPage onLogin={login} />
                  )
                }
              />
              
              {/* Public Registration */}
              <Route
                path="/register"
                element={
                  user ? (
                    <Navigate to="/dashboard" replace />
                  ) : (
                    <PublicRegister onBackToLogin={() => window.location.href = '/login'} />
                  )
                }
              />
              
              {/* Reset Password */}
              <Route
                path="/reset-password"
                element={
                  user ? (
                    <Navigate to="/dashboard" replace />
                  ) : (
                    <ResetPasswordPage />
                  )
                }
              />
              
              {/* Dashboard */}
              <Route
                path="/dashboard"
                element={
                  user ? (
                    user.role === 'amministratore' || user.role === 'super_amministratore' ? (
                      <AdminDashboard user={user} onLogout={logout} getAuthenticatedFileUrl={getAuthenticatedFileUrl} />
                    ) : (
                      <OperatorDashboard user={user} onLogout={logout} getAuthenticatedFileUrl={getAuthenticatedFileUrl} />
                    )
                  ) : (
                    <Navigate to="/login" replace />
                  )
                }
              />
            </Routes>

          </BrowserRouter>
        )}
      </AuthContext>
    </div>
  );
}

export default App;
